import React, { useEffect, useRef } from "react"
import { CARDS_FLIPPED_EVENT, getCityPlayerNames } from "../util/util"

const WORLD = 720
const WATER_H = 52
const PIXEL = 3
const CITY_ZOOM = 0.9
const BUILD_EXTRA = 40
const WIN = "#f5d84a"
const FW_COLORS = ["#ff6b8a", "#f5d84a", "#7ec8e0", "#c8a0ff", "#88e0a0", "#ff9a4a", "#f0f4ff"]

interface WindowLit {
    x: number
    y: number
    s: number
}

type BuildingKind = "block" | "step" | "peak" | "tower" | "factory" | "slab" | "tall" | "needle" | "office" | "apartments" | "terrace" | "hotel" | "warehouse" | "villa" | "twin" | "stack" | "tank" | "pagoda" | "stall" | "arena" | "artscenter" | "cathedral" | "ferris" | "coaster" | "lighthouse" | "harborcrane" | "neonshop" | "hospital" | "school" | "powerplant" | "station" | "controltower" | "stilthouse" | "castle" | "cinema" | "garage" | "construction" | "greenhouse" | "aquarium" | "datacenter" | "floatstall" | "marquee"

type WindowStyle = "grid" | "tight" | "stripes" | "sparse" | "bands" | "checkers" | "columns"

type District = "downtown" | "office" | "industrial" | "lowrise" | "pagoda" | "market" | "arena" | "arts" | "cathedral" | "wharf" | "amusement" | "neon" | "lighthouse" | "civic" | "campus" | "utilities" | "station" | "harborvillage" | "castle" | "cinema" | "floatmarket" | "aquarium" | "greenhouse"

type RoofProp = "none" | "watertower" | "dish" | "crane" | "helipad" | "mast"

interface BuildingSign {
    vert: boolean
    color: string
    motif: number
    x: number
    y: number
    w: number
    h: number
}

interface Building {
    x: number
    w: number
    h: number
    color: string
    shade: string
    windows: WindowLit[]
    kind: BuildingKind
    spire: number
    spireGold: boolean
    antenna: boolean
    crown: number
    sign: BuildingSign | null
    winStyle: WindowStyle
    greeble: number
    roofProp: RoofProp
    neon: boolean
    sprite: HTMLCanvasElement
}

interface CityLayer {
    speed: number
    strip: HTMLCanvasElement
}

interface Cloud {
    x: number
    y: number
    parts: { dx: number; dy: number; w: number; h: number }[]
}

interface Star {
    x: number
    y: number
    s: number
    tw: number
    twAmp: number
}

interface Boat {
    x: number
    y: number
    w: number
    speed: number
    kind: "skiff" | "ferry" | "tug" | "yacht" | "cargo" | "steamer" | "lantern" | "fireboat" | "police" | "cruise" | "sailboat" | "houseboat" | "dockship"
}

interface StackSmoke {
    layer: number
    x: number
    yOff: number
    phase: number
    rate: number
}

interface Plane {
    x: number
    y: number
    speed: number
    blink: number
    banner: boolean
    contrail: boolean
}

interface Craft {
    x: number
    y: number
    speed: number
    blink: number
    kind: "blimp" | "heli" | "balloon"
}

interface Spotlight {
    x: number
    yOff: number
    phase: number
    rate: number
    sweep: number
    lean: number
    len: number
    clock: number
}

const LAYER_DEFS = [
    { seed: 11, minH: 40, maxH: 62, colors: ["#6a78a4", "#5e6c98", "#647090"], density: 0.08, landmarks: false, gapChance: 0.05, maxGap: 4, speed: 0.1, detail: 0, plazas: false },
    { seed: 19, minH: 38, maxH: 66, colors: ["#5a6894", "#506088", "#56648c"], density: 0.11, landmarks: false, gapChance: 0.06, maxGap: 4, speed: 0.2, detail: 0, plazas: false },
    { seed: 29, minH: 22, maxH: 48, colors: ["#4a587c", "#425074", "#465478"], density: 0.12, landmarks: false, gapChance: 0.16, maxGap: 10, speed: 0.31, detail: 1, plazas: false },
    { seed: 37, minH: 26, maxH: 62, colors: ["#3a466c", "#344060", "#384464"], density: 0.15, landmarks: true, gapChance: 0.12, maxGap: 8, speed: 0.42, detail: 1, plazas: false },
    { seed: 47, minH: 24, maxH: 50, colors: ["#2e3a5c", "#283454", "#323e58"], density: 0.2, landmarks: false, gapChance: 0.1, maxGap: 5, speed: 0.54, detail: 1, plazas: false },
    { seed: 59, minH: 20, maxH: 42, colors: ["#242c4c", "#1e2644", "#283050"], density: 0.18, landmarks: false, gapChance: 0.12, maxGap: 6, speed: 0.67, detail: 2, plazas: false },
    { seed: 71, minH: 14, maxH: 28, colors: ["#1a203c", "#161a34", "#1e2440"], density: 0.16, landmarks: false, gapChance: 0.22, maxGap: 14, speed: 0.82, detail: 2, plazas: true },
    { seed: 83, minH: 8, maxH: 18, colors: ["#101428", "#141830", "#0c1020", "#181c34"], density: 0.12, landmarks: false, gapChance: 0.34, maxGap: 22, speed: 1, detail: 2, plazas: true },
]

function mulberry32(seed: number) {
    return function () {
        let t = seed += 0x6D2B79F5
        t = Math.imul((t ^ (t >>> 15)), t | 1)
        t ^= t + Math.imul((t ^ (t >>> 7)), t | 61)
        return (((t ^ (t >>> 14)) >>> 0) / 4294967296)
    }
}

function pick<T>(rng: () => number, items: T[]): T {
    return items[Math.floor(rng() * items.length)]
}

function shuffle<T>(rng: () => number, items: T[]): T[] {
    const a = items.slice()
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1))
        const tmp = a[i]
        a[i] = a[j]
        a[j] = tmp
    }
    return a
}

function mod(n: number, m: number) {
    return ((n % m) + m) % m
}

function coasterTrackAt(u: number, w: number, h: number) {
    const t = Math.max(0, Math.min(1, u))
    const amp = Math.min(18, Math.floor(h * 0.95) + 8)
    const rise = Math.round(Math.sin(t * Math.PI * 2) * amp + Math.sin(t * Math.PI * 4) * amp * 0.28)
    const x = Math.round(t * (w - 1))
    const y = h - 3 - Math.max(4, rise + 6)
    return { x, y }
}

const MARQUEE_COLS = 11
const MARQUEE_ROWS = 5
const MARQUEE_PITCH = 2
const MARQUEE_GLYPH: { [ch: string]: string[] } = {
    " ": ["000", "000", "000", "000", "000"],
    "·": ["000", "000", "010", "000", "000"],
    "0": ["111", "101", "101", "101", "111"],
    "1": ["010", "110", "010", "010", "111"],
    "2": ["111", "001", "111", "100", "111"],
    "3": ["111", "001", "111", "001", "111"],
    "4": ["101", "101", "111", "001", "001"],
    "5": ["111", "100", "111", "001", "111"],
    "6": ["111", "100", "111", "101", "111"],
    "7": ["111", "001", "001", "001", "001"],
    "8": ["111", "101", "111", "101", "111"],
    "9": ["111", "101", "111", "001", "111"],
    A: ["010", "101", "111", "101", "101"],
    B: ["110", "101", "110", "101", "110"],
    C: ["111", "100", "100", "100", "111"],
    D: ["110", "101", "101", "101", "110"],
    E: ["111", "100", "110", "100", "111"],
    F: ["111", "100", "110", "100", "100"],
    G: ["111", "100", "101", "101", "111"],
    H: ["101", "101", "111", "101", "101"],
    I: ["111", "010", "010", "010", "111"],
    J: ["001", "001", "001", "101", "111"],
    K: ["101", "101", "110", "101", "101"],
    L: ["100", "100", "100", "100", "111"],
    M: ["101", "111", "101", "101", "101"],
    N: ["110", "101", "101", "101", "101"],
    O: ["111", "101", "101", "101", "111"],
    P: ["111", "101", "111", "100", "100"],
    Q: ["111", "101", "101", "111", "001"],
    R: ["111", "101", "110", "101", "101"],
    S: ["111", "100", "111", "001", "111"],
    T: ["111", "010", "010", "010", "010"],
    U: ["101", "101", "101", "101", "111"],
    V: ["101", "101", "101", "101", "010"],
    W: ["101", "101", "111", "111", "101"],
    X: ["101", "101", "010", "101", "101"],
    Y: ["101", "101", "010", "010", "010"],
    Z: ["111", "001", "010", "100", "111"],
}

function marqueeTextColumns(text: string): boolean[][] {
    const cols: boolean[][] = []
    const cleaned = text.toUpperCase().replace(/[^A-Z0-9 ·]/g, " ").replace(/\s+/g, " ").trim()
    const src = cleaned.length > 0 ? cleaned : "HELLO · CITY"
    for (const ch of src) {
        const g = MARQUEE_GLYPH[ch] || MARQUEE_GLYPH[" "]
        for (let c = 0; c < 3; c++) {
            cols.push(g.map(row => row[c] === "1"))
        }
        cols.push([false, false, false, false, false])
    }
    for (let i = 0; i < 4; i++) {
        cols.push([false, false, false, false, false])
    }
    return cols.length > 0 ? cols : [[false, false, false, false, false]]
}

function shadeHex(hex: string, amt: number) {
    const n = parseInt(hex.slice(1), 16)
    const r = Math.max(0, Math.min(255, ((n >> 16) & 255) + amt))
    const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt))
    const b = Math.max(0, Math.min(255, (n & 255) + amt))
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

function makeWindows(rng: () => number, w: number, h: number, litChance: number, kind: BuildingKind, style: WindowStyle): WindowLit[] {
    const windows: WindowLit[] = []
    if (kind === "tank" || kind === "stack" || kind === "stall" || kind === "arena" || kind === "artscenter" || kind === "pagoda" || kind === "cathedral" || kind === "ferris" || kind === "coaster" || kind === "lighthouse" || kind === "harborcrane" || kind === "neonshop" || kind === "hospital" || kind === "school" || kind === "powerplant" || kind === "station" || kind === "controltower" || kind === "stilthouse" || kind === "castle" || kind === "cinema" || kind === "garage" || kind === "construction" || kind === "greenhouse" || kind === "aquarium" || kind === "datacenter" || kind === "floatstall" || kind === "marquee") {
        return windows
    }
    const cellW = kind === "factory" || style === "stripes" ? 2 : 1
    const gapX = style === "tight" || style === "columns" ? 2 : style === "stripes" ? 3 : kind === "factory" ? 4 : 3
    const gapY = style === "bands" || style === "checkers" ? 3 : style === "tight" ? 3 : kind === "slab" ? 3 : 4
    const padX = Math.min(2, Math.max(1, Math.floor(w / 5)))
    const padTop = kind === "peak" || kind === "villa" ? 5 : 3
    const padBot = 3
    const chance = style === "sparse" ? litChance * 0.45 : style === "tight" ? Math.min(0.95, litChance * 1.35) : litChance
    for (let wy = padTop; wy <= h - padBot - 1; wy += gapY) {
        if (style === "bands") {
            for (let wx = padX; wx <= w - padX - 1; wx += 2) {
                if (rng() > chance + 0.2) {
                    continue
                }
                windows.push({ x: wx, y: wy, s: 1 })
            }
            continue
        }
        if (style === "columns") {
            for (let wx = padX; wx <= w - padX - 1; wx += 2) {
                if (rng() > chance) {
                    continue
                }
                windows.push({ x: wx, y: wy, s: 1 })
            }
            continue
        }
        for (let wx = padX; wx <= w - padX - cellW; wx += gapX) {
            if (style === "checkers" && ((wx + wy) % 4 === 0)) {
                continue
            }
            if (rng() > chance) {
                continue
            }
            windows.push({ x: wx, y: wy, s: cellW })
        }
    }
    return windows
}

function paintSignMotif(ctx: CanvasRenderingContext2D, s: BuildingSign, ink: string) {
    ctx.fillStyle = s.color
    ctx.fillRect(s.x, s.y, s.w, s.h)
    ctx.fillStyle = shadeHex(s.color, -22)
    ctx.fillRect(s.x + s.w - 1, s.y, 1, s.h)
    ctx.fillStyle = ink
    if (s.motif === 0 && s.vert) {
        for (let i = 1; i < s.h - 1; i += 3) {
            ctx.fillRect(s.x + 1, s.y + i, Math.max(1, s.w - 2), 2)
        }
    } else if (s.motif === 2) {
        for (let y = s.y + 1; y < s.y + s.h - 1; y += 2) {
            ctx.fillRect(s.x + 1, y, Math.max(1, s.w - 2), 1)
        }
    } else if (s.motif === 3) {
        for (let y = s.y + 1; y < s.y + s.h - 1; y += 3) {
            for (let x = s.x + 1; x < s.x + s.w - 1; x += 2) {
                ctx.fillRect(x, y, 1, 2)
            }
        }
    } else {
        ctx.fillRect(s.x + 1, s.y + 1, Math.max(1, s.w - 2), Math.max(1, s.h - 2))
    }
}

function paintBuilding(b: Building) {
    const extra = BUILD_EXTRA
    const canvas = document.createElement("canvas")
    canvas.width = b.w + 2
    canvas.height = b.h + extra
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    const top = extra
    ctx.fillStyle = b.color
    ctx.fillRect(0, top, b.w, b.h)
    ctx.fillStyle = b.shade
    ctx.fillRect(b.w - 2, top, 2, b.h)
    ctx.fillStyle = shadeHex(b.color, 18)
    ctx.fillRect(0, top, 1, b.h)

    if (b.kind === "step") {
        const inset = Math.max(2, Math.floor(b.w / 5))
        const sh = Math.max(4, Math.floor(b.h * 0.18))
        ctx.fillStyle = b.color
        ctx.fillRect(inset, top - sh, b.w - inset * 2, sh)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - inset - 2, top - sh, 2, sh)
        ctx.fillStyle = shadeHex(b.color, -22)
        ctx.fillRect(inset, top - 1, b.w - inset * 2, 1)
    } else if (b.kind === "peak") {
        const steps = Math.min(4, Math.floor(b.w / 2))
        for (let i = 0; i < steps; i++) {
            ctx.fillStyle = b.color
            ctx.fillRect(i, top - 1 - i, b.w - i * 2, 1)
        }
    } else if (b.kind === "factory") {
        for (let sx = 0; sx + 3 <= b.w; sx += 3) {
            ctx.fillStyle = b.color
            ctx.fillRect(sx, top - 3, 3, 3)
            ctx.fillRect(sx + 1, top - 4, 1, 1)
            ctx.fillStyle = b.shade
            ctx.fillRect(sx + 2, top - 3, 1, 3)
        }
        const cx = 1 + Math.floor(b.w * 0.18)
        const cx2 = Math.max(cx + 5, b.w - 5)
        ctx.fillStyle = b.shade
        ctx.fillRect(cx, top - 12, 2, 12)
        ctx.fillRect(cx2, top - 9, 2, 9)
        ctx.fillStyle = "#2a2a40"
        ctx.fillRect(cx, top - 14, 2, 2)
        ctx.fillRect(cx2, top - 11, 2, 2)
        ctx.fillStyle = shadeHex(b.color, -36)
        ctx.fillRect(2, top + b.h - 5, Math.max(3, Math.floor(b.w * 0.28)), 5)
        ctx.fillRect(0, top + b.h - 3, b.w, 3)
    } else if (b.kind === "stack") {
        ctx.fillStyle = shadeHex(b.color, -18)
        ctx.fillRect(0, top, b.w, 2)
        ctx.fillRect(0, top + Math.floor(b.h * 0.35), b.w, 1)
        ctx.fillRect(0, top + Math.floor(b.h * 0.7), b.w, 1)
        ctx.fillStyle = "#1a1a28"
        ctx.fillRect(0, top - 3, b.w, 3)
        ctx.fillRect(1, top - 5, Math.max(1, b.w - 2), 2)
    } else if (b.kind === "tank") {
        const inset = 1
        ctx.fillStyle = b.color
        ctx.fillRect(inset, top - 2, b.w - inset * 2, 2)
        ctx.fillRect(inset + 1, top - 3, b.w - inset * 2 - 2, 1)
        ctx.fillStyle = shadeHex(b.color, -22)
        ctx.fillRect(0, top + Math.floor(b.h * 0.45), b.w, 2)
        ctx.fillStyle = shadeHex(b.color, -36)
        ctx.fillRect(2, top + b.h - 2, 2, 2)
        ctx.fillRect(b.w - 4, top + b.h - 2, 2, 2)
    } else if (b.kind === "pagoda") {
        const cx = Math.floor(b.w / 2)
        const roof = "#2a3240"
        const roofHi = "#3c4656"
        const wood = "#6a3848"
        const woodDk = "#4a2434"
        const gold = "#c9a43a"
        ctx.fillStyle = gold
        ctx.fillRect(cx, top - 14, 1, 12)
        ctx.fillRect(cx, top - 15, 1, 1)
        ctx.fillRect(cx - 1, top - 12, 3, 1)
        ctx.fillRect(cx - 1, top - 9, 3, 1)
        ctx.fillRect(cx - 1, top - 6, 3, 1)
        let y = top - 3
        const tiers = 3
        for (let i = 0; i < tiers; i++) {
            const tw = Math.max(6, b.w - i * 3)
            const storyH = Math.max(3, Math.floor((b.h - 3) / tiers) - 1)
            const rx = Math.floor((b.w - tw) / 2)
            ctx.fillStyle = roof
            ctx.fillRect(rx - 1, y, tw + 2, 2)
            ctx.fillRect(rx, y - 1, tw, 1)
            ctx.fillRect(rx - 1, y - 2, 1, 2)
            ctx.fillRect(rx + tw, y - 2, 1, 2)
            ctx.fillStyle = roofHi
            ctx.fillRect(rx + 1, y, tw - 2, 1)
            y += 2
            ctx.fillStyle = wood
            ctx.fillRect(rx + 2, y, Math.max(2, tw - 4), storyH)
            ctx.fillStyle = woodDk
            ctx.fillRect(rx + tw - 3, y, 1, storyH)
            ctx.fillStyle = "#d8d0c0"
            ctx.fillRect(rx + 3, y + 1, 1, Math.max(1, storyH - 2))
            ctx.fillRect(rx + tw - 5, y + 1, 1, Math.max(1, storyH - 2))
            y += storyH
        }
        ctx.fillStyle = "#2a2838"
        ctx.fillRect(1, top + b.h - 2, b.w - 2, 2)
    } else if (b.kind === "stall") {
        ctx.fillStyle = shadeHex(b.color, 28)
        ctx.fillRect(0, top, b.w, 3)
        ctx.fillStyle = shadeHex(b.color, -24)
        for (let i = 0; i < b.w; i += 2) {
            ctx.fillRect(i, top, 1, 3)
        }
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(1, top + 4, b.w - 2, Math.max(2, b.h - 5))
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(2, top + 5, 1, 1)
        ctx.fillRect(b.w - 3, top + 5, 1, 1)
        ctx.fillStyle = b.shade
        ctx.fillRect(0, top + b.h - 2, 1, 2)
        ctx.fillRect(b.w - 1, top + b.h - 2, 1, 2)
    } else if (b.kind === "arena") {
        const rim = Math.max(3, Math.floor(b.h * 0.38))
        for (let i = 0; i < rim; i++) {
            const inset = Math.max(0, Math.floor((1 - i / rim) * Math.min(4, Math.floor(b.w / 5))))
            ctx.fillStyle = i < 2 ? shadeHex(b.color, -18) : b.color
            ctx.fillRect(inset, top + i, b.w - inset * 2, 1)
        }
        ctx.fillStyle = shadeHex(b.color, -28)
        ctx.fillRect(1, top + rim, b.w - 2, b.h - rim)
        ctx.fillStyle = "#0a1020"
        for (let ax = 2; ax < b.w - 3; ax += 4) {
            ctx.fillRect(ax, top + b.h - 5, 2, 5)
            ctx.fillRect(ax, top + b.h - 6, 2, 1)
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(Math.floor(b.w / 2) - 1, top - 3, 3, 2)
        ctx.fillRect(Math.floor(b.w / 2), top - 5, 1, 2)
    } else if (b.kind === "artscenter") {
        const roof = "#6a7488"
        const roofDark = "#4a5468"
        const glass = shadeHex(b.color, 8)
        const leftW = Math.floor(b.w * 0.58)
        const paintFan = (ox: number, fw: number, fh: number) => {
            const cx = ox + Math.floor(fw / 2)
            for (let row = 0; row < fh; row++) {
                const u = row / Math.max(1, fh - 1)
                const half = Math.max(2, Math.floor((fw / 2) * (0.22 + u * 0.78)))
                const y = top - fh + row
                ctx.fillStyle = row < 2 ? roofDark : roof
                ctx.fillRect(cx - half, y, half * 2 + 1, 1)
                if (row % 2 === 0) {
                    ctx.fillStyle = roofDark
                    for (let n = cx - half + 2; n < cx + half - 1; n += 4) {
                        ctx.fillRect(n, y, 2, 1)
                    }
                }
            }
        }
        paintFan(0, leftW, 15)
        paintFan(leftW - 3, b.w - leftW + 4, 11)
        ctx.fillStyle = glass
        ctx.fillRect(2, top + 1, b.w - 4, b.h - 2)
        ctx.fillStyle = shadeHex(b.color, -20)
        ctx.fillRect(1, top + b.h - 2, b.w - 2, 2)
        ctx.fillStyle = WIN
        for (let wy = top + 3; wy < top + b.h - 3; wy += 3) {
            for (let wx = 4; wx < b.w - 5; wx += 2) {
                if ((wx + wy) % 3 !== 0) {
                    ctx.fillRect(wx, wy, 1, 1)
                }
            }
        }
        ctx.fillStyle = "#8a94a8"
        for (let i = 0; i < 7; i++) {
            const x0 = 3 + i * Math.max(2, Math.floor((b.w - 8) / 7))
            for (let k = 0; k < b.h - 1; k += 2) {
                ctx.fillRect(x0 + Math.floor(k * 0.25), top + 1 + k, 1, 1)
            }
        }
    } else if (b.kind === "ferris") {
        const cx = Math.floor(b.w / 2)
        const r = Math.min(16, Math.floor(b.w / 2) - 2)
        const ay = top - 2
        ctx.fillStyle = shadeHex(b.color, -10)
        ctx.fillRect(2, top, b.w - 4, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 4, top, 2, b.h)
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(cx - 1, ay, 3, b.h + 2)
        ctx.fillRect(1, top + b.h - 2, b.w - 2, 2)
        for (let y = -r; y <= r; y++) {
            for (let x = -r; x <= r; x++) {
                const d = x * x + y * y
                if (d >= (r - 1) * (r - 1) && d <= r * r + r) {
                    ctx.fillStyle = "#4a5468"
                    ctx.fillRect(cx + x, ay + y, 1, 1)
                }
            }
        }
        ctx.fillStyle = "#8a94a8"
        ctx.fillRect(cx - 1, ay - 1, 3, 3)
    } else if (b.kind === "coaster") {
        ctx.fillStyle = shadeHex(b.color, -16)
        ctx.fillRect(0, top + b.h - 3, b.w, 3)
        ctx.fillStyle = "#3a465c"
        for (let i = 0; i < b.w; i++) {
            const p = coasterTrackAt(i / Math.max(1, b.w - 1), b.w, b.h)
            ctx.fillRect(p.x, top + p.y, 1, 2)
            if (i % 3 === 0) {
                ctx.fillRect(p.x, top + p.y, 1, Math.max(1, b.h - p.y))
            }
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(2, top + b.h - 5, 2, 2)
    } else if (b.kind === "lighthouse") {
        const cx = Math.floor(b.w / 2)
        for (let y = 0; y < b.h; y++) {
            const u = y / Math.max(1, b.h - 1)
            const half = Math.max(1, Math.floor((b.w / 2) * (0.45 + u * 0.4)))
            ctx.fillStyle = Math.floor(y / 3) % 2 === 0 ? "#8a3a38" : "#d8d0c4"
            ctx.fillRect(cx - half, top + y, half * 2 + 1, 1)
            ctx.fillStyle = "#2a2430"
            ctx.fillRect(cx + half, top + y, 1, 1)
        }
        ctx.fillStyle = "#d8d0c4"
        ctx.fillRect(cx - 2, top - 6, 5, 6)
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(cx - 1, top - 5, 3, 3)
        ctx.fillStyle = "#2a2430"
        ctx.fillRect(cx - 3, top - 7, 7, 1)
        ctx.fillRect(cx, top - 9, 1, 2)
    } else if (b.kind === "harborcrane") {
        ctx.fillStyle = shadeHex(b.color, 12)
        ctx.fillRect(2, top, 4, b.h)
        ctx.fillStyle = "#e0a060"
        ctx.fillRect(2, top - 2, 4, 2)
        ctx.fillRect(3, top - 14, 1, 12)
        ctx.fillRect(3, top - 14, Math.max(8, b.w - 4), 1)
        ctx.fillRect(b.w - 2, top - 13, 1, 4)
        ctx.fillStyle = "#ff6a4a"
        ctx.fillRect(3, top - 15, 1, 1)
        ctx.fillStyle = "#5a6878"
        ctx.fillRect(0, top + b.h - 3, b.w, 3)
        ctx.fillStyle = "#d89858"
        ctx.fillRect(6, top + b.h - 6, 3, 3)
        ctx.fillRect(10, top + b.h - 5, 3, 2)
    } else if (b.kind === "neonshop") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(1, top + 4, b.w - 3, Math.max(3, b.h - 6))
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(2, top + 6, 1, 1)
        ctx.fillRect(b.w - 4, top + 6, 1, 1)
        ctx.fillStyle = "#c45a78"
        ctx.fillRect(1, top - 4, b.w - 2, 4)
        ctx.fillStyle = "#e8a0b8"
        ctx.fillRect(2, top - 3, Math.max(1, b.w - 4), 1)
    } else if (b.kind === "hospital") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = "#8a3038"
        ctx.fillRect(0, top - 3, b.w, 3)
        const cx = Math.floor(b.w / 2)
        ctx.fillStyle = "#c85050"
        ctx.fillRect(cx - 1, top + 2, 3, 1)
        ctx.fillRect(cx, top + 1, 1, 3)
        ctx.fillStyle = WIN
        for (let wy = top + 6; wy < top + b.h - 3; wy += 3) {
            for (let wx = 2; wx < b.w - 3; wx += 3) {
                ctx.fillRect(wx, wy, 1, 1)
            }
        }
    } else if (b.kind === "school") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        const ped = Math.min(10, b.w - 2)
        const px0 = Math.floor((b.w - ped) / 2)
        for (let i = 0; i < 4; i++) {
            ctx.fillStyle = shadeHex(b.color, -8)
            ctx.fillRect(px0 + i, top - 4 + i, ped - i * 2, 1)
        }
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(Math.floor(b.w / 2) - 1, top - 2, 3, 3)
        ctx.fillStyle = WIN
        ctx.fillRect(Math.floor(b.w / 2), top - 1, 1, 1)
        for (let wx = 2; wx < b.w - 2; wx += 2) {
            ctx.fillRect(wx, top + 3, 1, 1)
            ctx.fillRect(wx, top + 6, 1, 1)
        }
    } else if (b.kind === "powerplant") {
        ctx.fillStyle = shadeHex(b.color, -10)
        ctx.fillRect(0, top + Math.floor(b.h * 0.45), b.w, Math.ceil(b.h * 0.55))
        const towers = 2
        const tw = Math.max(4, Math.floor(b.w / 5))
        for (let i = 0; i < towers; i++) {
            const tx = 1 + i * (tw + 3)
            const th = Math.floor(b.h * 0.85)
            for (let y = 0; y < th; y++) {
                const u = y / Math.max(1, th - 1)
                const pinch = 1 - Math.sin(u * Math.PI) * 0.35
                const half = Math.max(1, Math.floor((tw / 2) * pinch))
                ctx.fillStyle = u < 0.12 ? "#6a7488" : b.color
                ctx.fillRect(tx + Math.floor(tw / 2) - half, top + (b.h - th) + y, half * 2 + 1, 1)
            }
        }
        ctx.fillStyle = "#2a2430"
        ctx.fillRect(b.w - 5, top - 4, 2, b.h + 4)
        ctx.fillRect(b.w - 2, top - 2, 2, b.h + 2)
        ctx.fillStyle = WIN
        ctx.fillRect(2, top + b.h - 4, 1, 1)
        ctx.fillRect(5, top + b.h - 4, 1, 1)
    } else if (b.kind === "station") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top + 4, b.w, b.h - 4)
        ctx.fillStyle = shadeHex(b.color, 16)
        for (let i = 0; i < 5; i++) {
            const u = i / 4
            const half = Math.floor((b.w / 2) * (0.55 + u * 0.45))
            ctx.fillRect(Math.floor(b.w / 2) - half, top + i, half * 2, 1)
        }
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top + 4, 2, b.h - 4)
        const archW = Math.max(3, Math.floor((b.w - 6) / 4))
        for (let i = 0; i < 4; i++) {
            const ax = 2 + i * (archW + 1)
            ctx.fillStyle = "#0a1020"
            ctx.fillRect(ax, top + 7, archW, b.h - 9)
            ctx.fillRect(ax + 1, top + 6, Math.max(1, archW - 2), 1)
            ctx.fillStyle = WIN
            ctx.fillRect(ax + Math.floor(archW / 2), top + 8, 1, 1)
            ctx.fillRect(ax + Math.floor(archW / 2), top + 10, 1, 1)
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(Math.floor(b.w / 2), top + 2, 1, 1)
    } else if (b.kind === "controltower") {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const stem = Math.max(2, Math.floor(b.w * 0.28))
        const sx = Math.floor((b.w - stem) / 2)
        ctx.fillStyle = b.color
        ctx.fillRect(sx, top + 5, stem, b.h - 5)
        ctx.fillStyle = b.shade
        ctx.fillRect(sx + stem - 1, top + 5, 1, b.h - 5)
        ctx.fillStyle = "#4a5a70"
        ctx.fillRect(1, top, b.w - 2, 6)
        ctx.fillStyle = "#6a7a90"
        ctx.fillRect(2, top + 1, b.w - 4, 4)
        ctx.fillStyle = WIN
        ctx.fillRect(3, top + 2, 1, 1)
        ctx.fillRect(5, top + 2, 1, 1)
        ctx.fillRect(b.w - 5, top + 2, 1, 1)
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(Math.floor(b.w / 2), top - 2, 1, 2)
    } else if (b.kind === "stilthouse") {
        const cabH = Math.max(4, Math.floor(b.h * 0.45))
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.color
        ctx.fillRect(1, top, b.w - 2, cabH)
        ctx.fillStyle = shadeHex(b.color, -20)
        ctx.fillRect(1, top, b.w - 2, 1)
        ctx.fillStyle = WIN
        ctx.fillRect(2, top + 2, 1, 1)
        ctx.fillRect(b.w - 4, top + 2, 1, 1)
        ctx.fillStyle = "#1a2030"
        for (let p = 2; p < b.w - 1; p += 3) {
            ctx.fillRect(p, top + cabH, 1, b.h - cabH)
        }
    } else if (b.kind === "castle") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = shadeHex(b.color, 8)
        for (let i = 0; i < b.w; i += 3) {
            ctx.fillRect(i, top - 3, 2, 3)
        }
        ctx.fillStyle = "#0a1020"
        const gw = Math.max(3, Math.floor(b.w * 0.22))
        ctx.fillRect(Math.floor((b.w - gw) / 2), top + b.h - 6, gw, 6)
        ctx.fillRect(Math.floor(b.w / 2) - 1, top + b.h - 7, 3, 1)
        ctx.fillStyle = WIN
        ctx.fillRect(3, top + 4, 1, 1)
        ctx.fillRect(b.w - 5, top + 4, 1, 1)
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(Math.floor(b.w / 2) - 1, top + 2, 3, 3)
        ctx.fillStyle = WIN
        ctx.fillRect(Math.floor(b.w / 2), top + 3, 1, 1)
    } else if (b.kind === "cinema") {
        ctx.fillStyle = b.color
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(2, top + 5, b.w - 4, b.h - 7)
        ctx.fillStyle = "#c45a78"
        ctx.fillRect(-1, top - 5, b.w + 2, 5)
        ctx.fillStyle = "#e07090"
        ctx.fillRect(0, top - 4, b.w, 2)
        ctx.fillStyle = WIN
        ctx.fillRect(3, top + 2, 1, 1)
        ctx.fillRect(b.w - 4, top + 2, 1, 1)
    } else if (b.kind === "garage") {
        ctx.fillStyle = shadeHex(b.color, -18)
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 1, top, 1, b.h)
        for (let wy = top + 2; wy < top + b.h - 1; wy += 3) {
            ctx.fillStyle = "#0a1020"
            ctx.fillRect(1, wy, b.w - 3, 2)
            ctx.fillStyle = "#1a2438"
            ctx.fillRect(1, wy + 1, b.w - 3, 1)
        }
    } else if (b.kind === "construction") {
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(0, top + Math.floor(b.h * 0.55), b.w, Math.ceil(b.h * 0.45))
        ctx.fillStyle = "#5a6878"
        ctx.fillRect(1, top, 1, b.h)
        ctx.fillRect(b.w - 2, top + 4, 1, b.h - 4)
        ctx.fillRect(Math.floor(b.w / 2), top + 2, 1, b.h - 2)
        for (let wy = top + 3; wy < top + b.h - 2; wy += 3) {
            ctx.fillRect(1, wy, Math.floor(b.w * 0.62), 1)
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(Math.floor(b.w / 2), top - 6, 1, 6)
    } else if (b.kind === "greenhouse") {
        ctx.fillStyle = shadeHex(b.color, -12)
        ctx.fillRect(0, top + 3, b.w, b.h - 3)
        for (let i = 0; i < 4; i++) {
            const u = i / 3
            const half = Math.floor((b.w / 2) * (0.4 + u * 0.6))
            ctx.fillStyle = "#2a5040"
            ctx.fillRect(Math.floor(b.w / 2) - half, top + i, half * 2, 1)
        }
        ctx.fillStyle = "#3ae07a"
        for (let wy = top + 4; wy < top + b.h - 2; wy += 2) {
            for (let wx = 2; wx < b.w - 2; wx += 2) {
                ctx.fillRect(wx, wy, 1, 1)
            }
        }
        ctx.fillStyle = "#1a3028"
        ctx.fillRect(Math.floor(b.w / 2), top + 3, 1, b.h - 3)
    } else if (b.kind === "aquarium") {
        ctx.fillStyle = shadeHex(b.color, -8)
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = "#1a3048"
        ctx.fillRect(1, top + 3, b.w - 4, b.h - 5)
        ctx.fillStyle = "#3ae0c8"
        for (let wy = top + 4; wy < top + b.h - 3; wy += 2) {
            for (let wx = 2; wx < b.w - 4; wx += 3) {
                ctx.fillRect(wx, wy, 2, 1)
            }
        }
        ctx.fillStyle = "#2a88a8"
        ctx.fillRect(2, top + 1, b.w - 6, 1)
    } else if (b.kind === "datacenter") {
        ctx.fillStyle = shadeHex(b.color, -22)
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.shade
        ctx.fillRect(b.w - 2, top, 2, b.h)
        ctx.fillStyle = WIN
        ctx.fillRect(2, top + Math.floor(b.h * 0.45), 1, 1)
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(2, top - 2, 3, 2)
        ctx.fillRect(7, top - 3, 3, 3)
        ctx.fillRect(b.w - 6, top - 2, 3, 2)
        ctx.fillStyle = "#2a3448"
        ctx.fillRect(3, top - 3, 1, 1)
        ctx.fillRect(8, top - 4, 1, 1)
        ctx.fillRect(b.w - 5, top - 3, 1, 1)
    } else if (b.kind === "floatstall") {
        const cabH = Math.max(4, Math.floor(b.h * 0.42))
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = b.color
        ctx.fillRect(1, top, b.w - 2, cabH)
        ctx.fillStyle = shadeHex(b.color, 18)
        ctx.fillRect(0, top - 2, b.w, 2)
        ctx.fillRect(1, top - 3, b.w - 2, 1)
        ctx.fillStyle = WIN
        ctx.fillRect(2, top + 1, 1, 1)
        ctx.fillRect(b.w - 4, top + 1, 1, 1)
        ctx.fillStyle = "#1a2030"
        for (let p = 2; p < b.w - 1; p += 3) {
            ctx.fillRect(p, top + cabH, 1, b.h - cabH)
        }
    } else if (b.kind === "marquee") {
        ctx.fillStyle = shadeHex(b.color, 12)
        ctx.fillRect(0, top, b.w, 2)
        ctx.fillStyle = "#0a1020"
        const bandX = 2
        const bandY = 3
        ctx.fillRect(bandX - 1, top + bandY - 1, MARQUEE_COLS * MARQUEE_PITCH, MARQUEE_ROWS * MARQUEE_PITCH)
        ctx.fillStyle = "#1a2438"
        for (let r = 0; r < MARQUEE_ROWS; r++) {
            for (let c = 0; c < MARQUEE_COLS; c++) {
                ctx.fillRect(bandX + c * MARQUEE_PITCH, top + bandY + r * MARQUEE_PITCH, 1, 1)
            }
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(1, top - 2, b.w - 2, 2)
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(2, top + b.h - 3, 1, 3)
        ctx.fillRect(b.w - 4, top + b.h - 3, 1, 3)
        ctx.fillRect(Math.floor(b.w / 2), top + b.h - 3, 1, 3)
    } else if (b.kind === "cathedral") {
        const stone = "#2a303c"
        const dark = "#141820"
        const tw = Math.max(4, Math.floor(b.w * 0.26))
        const th = 11
        ctx.fillStyle = stone
        ctx.fillRect(1, top - th, tw, th + 2)
        ctx.fillRect(b.w - tw - 1, top - th, tw, th + 2)
        ctx.fillRect(2, top - th - 3, tw - 2, 3)
        ctx.fillRect(b.w - tw, top - th - 3, tw - 2, 3)
        ctx.fillStyle = dark
        ctx.fillRect(2, top - th + 2, tw - 2, 4)
        ctx.fillRect(b.w - tw, top - th + 2, tw - 2, 4)
        ctx.fillStyle = stone
        ctx.fillRect(0, top, b.w, b.h)
        ctx.fillStyle = shadeHex(stone, -24)
        ctx.fillRect(b.w - 2, top, 2, b.h)
        const rcx = Math.floor(b.w / 2)
        const rcy = top + 3
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(rcx - 2, rcy, 5, 5)
        ctx.fillStyle = "#2e3848"
        ctx.fillRect(rcx - 1, rcy + 1, 3, 3)
        ctx.fillStyle = WIN
        ctx.fillRect(rcx, rcy + 2, 1, 1)
        ctx.fillStyle = "#121820"
        const pw = Math.max(3, Math.floor((b.w - 10) / 3))
        for (let i = 0; i < 3; i++) {
            const px0 = 3 + i * (pw + 2)
            ctx.fillRect(px0, top + b.h - 6, pw, 6)
            ctx.fillRect(px0 + 1, top + b.h - 7, Math.max(1, pw - 2), 1)
        }
        ctx.fillStyle = "#222830"
        ctx.fillRect(Math.floor(b.w / 2) - 1, top - 2, 3, 2)
    } else if (b.kind === "slab" || b.kind === "office") {
        ctx.fillStyle = shadeHex(b.color, -16)
        ctx.fillRect(0, top, b.w, 2)
        ctx.fillRect(1, b.h + top - 3, 2, 2)
        if (b.kind === "office") {
            ctx.fillStyle = shadeHex(b.color, 10)
            ctx.fillRect(2, top + 2, b.w - 4, 2)
        }
    } else if (b.kind === "apartments") {
        const inset = 1 + Math.floor(b.w * 0.15)
        ctx.fillStyle = b.color
        ctx.fillRect(inset, top - 3, b.w - inset * 2, 3)
        ctx.fillStyle = shadeHex(b.color, -24)
        ctx.fillRect(0, top, b.w, 1)
    } else if (b.kind === "terrace") {
        const mid = Math.max(3, Math.floor(b.w * 0.7))
        const topH = Math.max(4, Math.floor(b.h * 0.28))
        ctx.fillStyle = b.color
        ctx.fillRect(Math.floor((b.w - mid) / 2), top - 1, mid, 1)
        ctx.fillRect(Math.floor((b.w - mid) / 2), top - topH, mid, topH)
        ctx.fillStyle = b.shade
        ctx.fillRect(Math.floor((b.w - mid) / 2) + mid - 1, top - topH, 1, topH)
    } else if (b.kind === "hotel") {
        ctx.fillStyle = shadeHex(b.color, -18)
        ctx.fillRect(0, top, b.w, 2)
        ctx.fillRect(-0, top - 2, b.w + 1, 2)
        ctx.fillStyle = shadeHex(b.color, 12)
        ctx.fillRect(1, top + Math.floor(b.h * 0.35), b.w - 2, 1)
    } else if (b.kind === "warehouse") {
        ctx.fillStyle = shadeHex(b.color, -24)
        ctx.fillRect(0, top + b.h - 4, b.w, 4)
        ctx.fillRect(2, top + b.h - 6, 3, 2)
        ctx.fillRect(b.w - 5, top + b.h - 6, 3, 2)
    } else if (b.kind === "villa") {
        const steps = Math.min(3, Math.floor(b.w / 2))
        for (let i = 0; i < steps; i++) {
            ctx.fillStyle = b.color
            ctx.fillRect(i, top - 1 - i, b.w - i * 2, 1)
        }
        ctx.fillStyle = shadeHex(b.color, -20)
        ctx.fillRect(0, top + b.h - 2, b.w, 2)
    } else if (b.kind === "twin") {
        const gap = 1
        const col = Math.max(2, Math.floor((b.w - gap) / 2))
        ctx.fillStyle = "#0a1020"
        ctx.fillRect(col, top + 4, gap, b.h - 4)
        ctx.fillStyle = shadeHex(b.color, 8)
        ctx.fillRect(0, top, col, 2)
        ctx.fillRect(col + gap, top, b.w - col - gap, 2)
    } else {
        ctx.fillStyle = shadeHex(b.color, -20)
        ctx.fillRect(0, top, b.w, 1)
    }

    if (b.crown > 0) {
        ctx.fillStyle = "#e8c84a"
        ctx.fillRect(1, top - b.crown, b.w - 2, b.crown)
        ctx.fillRect(2, top - b.crown - 2, b.w - 4, 2)
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(1, top - 1, b.w - 2, 1)
    }

    if (b.spire) {
        const mx = Math.floor(b.w / 2)
        ctx.fillStyle = b.spireGold ? "#e8c84a" : "#2a2e58"
        ctx.fillRect(mx, top - b.crown - b.spire, 1, b.spire)
        if (b.spireGold) {
            ctx.fillRect(mx - 1, top - b.crown - 3, 3, 2)
            ctx.fillRect(mx, top - b.crown - b.spire - 1, 1, 1)
        }
    } else if (b.antenna) {
        const mx = 1 + Math.floor((b.w - 2) * 0.4)
        ctx.fillStyle = "#1a1e40"
        ctx.fillRect(mx, top - 8, 1, 8)
        ctx.fillRect(mx - 1, top - 4, 3, 1)
    }

    if (b.greeble === 1 && b.w > 5) {
        ctx.fillStyle = shadeHex(b.color, -30)
        ctx.fillRect(2, top - 3, 3, 3)
        ctx.fillRect(3, top - 4, 1, 1)
    } else if (b.greeble === 2 && b.w > 4) {
        ctx.fillStyle = "#2a3048"
        ctx.fillRect(b.w - 4, top - 2, 3, 2)
        ctx.fillRect(b.w - 3, top - 3, 1, 1)
    } else if (b.greeble === 3) {
        ctx.fillStyle = "#1a1e38"
        ctx.fillRect(b.w - 2, top - 6, 1, 6)
    }

    if (b.roofProp === "watertower" && b.w > 5) {
        const tx = Math.max(1, Math.floor(b.w * 0.35))
        ctx.fillStyle = "#3a4058"
        ctx.fillRect(tx, top - 7, 4, 4)
        ctx.fillRect(tx + 1, top - 8, 2, 1)
        ctx.fillStyle = "#2a3044"
        ctx.fillRect(tx, top - 3, 1, 3)
        ctx.fillRect(tx + 3, top - 3, 1, 3)
    } else if (b.roofProp === "dish" && b.w > 4) {
        const dx = Math.max(1, b.w - 5)
        ctx.fillStyle = "#4a5468"
        ctx.fillRect(dx, top - 3, 3, 1)
        ctx.fillRect(dx + 1, top - 4, 1, 1)
        ctx.fillRect(dx + 1, top - 2, 1, 2)
    } else if (b.roofProp === "crane" && b.w > 6) {
        const cx = Math.floor(b.w * 0.3)
        ctx.fillStyle = "#e0a060"
        ctx.fillRect(cx, top - 12, 1, 12)
    } else if (b.roofProp === "mast" && b.w > 3) {
        const mx = Math.max(1, Math.floor(b.w / 2))
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(mx, top - 16, 1, 16)
        ctx.fillStyle = "#2a3448"
        ctx.fillRect(mx - 2, top - 6, 5, 1)
        ctx.fillRect(mx - 3, top - 11, 7, 1)
        ctx.fillStyle = "#6a3030"
        ctx.fillRect(mx, top - 17, 1, 1)
    } else if (b.roofProp === "helipad" && b.w > 7) {
        const hx = Math.max(1, Math.floor((b.w - 7) / 2))
        ctx.fillStyle = "#2a3040"
        ctx.fillRect(hx, top - 3, 7, 3)
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(hx + 1, top - 3, 1, 3)
        ctx.fillRect(hx + 5, top - 3, 1, 3)
        ctx.fillRect(hx + 1, top - 2, 5, 1)
    }

    ctx.fillStyle = WIN
    for (const win of b.windows) {
        ctx.fillRect(win.x, top + win.y, win.s, 1)
    }

    if (b.sign) {
        paintSignMotif(ctx, {
            ...b.sign,
            y: b.sign.vert ? top + b.sign.y : top - b.sign.h,
        }, shadeHex(b.sign.color, 40))
    }
    return canvas
}

function makeDistricts(rng: () => number, extras: District[] = []): { start: number; end: number; type: District }[] {
    const spans: { start: number; end: number; type: District }[] = []
    let x = 0
    let last: District | null = null

    if (extras.length > 0) {
        const generic: District[] = ["lowrise", "downtown", "office", "wharf"]
        const must = extras.filter(d => d === "arts" || d === "cathedral")
        const optional = extras.filter(d => d !== "arts" && d !== "cathedral")
        const picked = shuffle(rng, optional).slice(0, 7)
        const filler = shuffle(rng, picked.concat(generic, generic))
        const reserved: { start: number; end: number; type: District }[] = []
        for (const type of shuffle(rng, must)) {
            const len = type === "arts" ? 52 + Math.floor(rng() * 16) : 48 + Math.floor(rng() * 16)
            let placed = false
            for (let tries = 0; tries < 24 && !placed; tries++) {
                const start = 10 + Math.floor(rng() * Math.max(1, WORLD - len - 20))
                const end = start + len
                if (reserved.some(s => start < s.end + 12 && end > s.start - 12)) {
                    continue
                }
                reserved.push({ start, end, type })
                placed = true
            }
            if (!placed) {
                const start = reserved.length === 0 ? 12 : Math.min(WORLD - len - 4, reserved[reserved.length - 1].end + 16)
                reserved.push({ start, end: Math.min(WORLD, start + len), type })
            }
        }
        reserved.sort((a, b) => a.start - b.start)
        let cursor = 0
        let fi = 0
        const takeFiller = (): District => {
            let type = filler[fi % filler.length]
            fi += 1
            if (type === last) {
                type = filler[fi % filler.length]
                fi += 1
            }
            return type
        }
        while (cursor < WORLD) {
            const next = reserved.find(s => s.start >= cursor)
            if (next && cursor >= next.start) {
                spans.push(next)
                last = next.type
                cursor = next.end
                continue
            }
            const limit = next ? next.start : WORLD
            const room = limit - cursor
            if (room <= 4) {
                cursor = limit
                continue
            }
            const type = takeFiller()
            const special = type === "arts" || type === "cathedral" || type === "pagoda" || type === "industrial" || type === "arena" || type === "market" || type === "amusement" || type === "neon" || type === "lighthouse" || type === "civic" || type === "campus" || type === "utilities" || type === "station" || type === "harborvillage" || type === "castle" || type === "cinema"
            let len = type === "amusement" || type === "station" || type === "castle" ? 96 + Math.floor(rng() * 28)
                : type === "civic" || type === "campus" || type === "utilities" || type === "harborvillage" ? 70 + Math.floor(rng() * 24)
                : special ? 52 + Math.floor(rng() * 28)
                : 36 + Math.floor(rng() * 40)
            len = Math.min(len, room)
            if (special && len < 40) {
                const g = pick(rng, generic)
                spans.push({ start: cursor, end: cursor + room, type: g })
                last = g
                cursor = limit
                continue
            }
            spans.push({ start: cursor, end: cursor + len, type })
            last = type
            cursor += len
        }
        return spans
    }

    const civic: District[] = ["downtown", "office", "lowrise"]
    let factories = 0
    while (x < WORLD) {
        if (factories < 1 && rng() > 0.88 && x > 40 && x < WORLD - 80) {
            const len = 52 + Math.floor(rng() * 40)
            spans.push({ start: x, end: Math.min(WORLD, x + len), type: "industrial" })
            last = "industrial"
            factories += 1
            x += len
            continue
        }
        let type = pick(rng, civic)
        if (type === last) {
            type = pick(rng, civic)
        }
        const len = 72 + Math.floor(rng() * 96)
        spans.push({ start: x, end: Math.min(WORLD, x + len), type })
        last = type
        x += len
    }
    return spans
}

function makeOccasionalSpans(seed: number, minCount: number, maxCount: number, minW: number, maxW: number) {
    const rng = mulberry32(seed)
    const n = minCount + Math.floor(rng() * (maxCount - minCount + 1))
    const spans: { start: number; end: number }[] = []
    let guard = 0
    while (spans.length < n && guard < 40) {
        guard += 1
        const w = minW + Math.floor(rng() * (maxW - minW))
        const start = 8 + Math.floor(rng() * Math.max(1, WORLD - w - 16))
        if (spans.some(s => Math.abs(s.start - start) < w + 40)) {
            continue
        }
        spans.push({ start, end: start + w })
    }
    return spans
}

function inSpans(spans: { start: number; end: number }[] | undefined, x: number) {
    if (!spans) {
        return false
    }
    return spans.some(s => x >= s.start && x < s.end)
}

function spanEndAt(spans: { start: number; end: number }[] | undefined, x: number) {
    if (!spans) {
        return x
    }
    const hit = spans.find(s => x >= s.start && x < s.end)
    return hit ? hit.end : x
}

function districtSpanAt(spans: { start: number; end: number; type: District }[], x: number) {
    return spans.find(s => x >= s.start && x < s.end) || null
}

function districtAt(spans: { start: number; end: number; type: District }[], x: number): District {
    for (const s of spans) {
        if (x >= s.start && x < s.end) {
            return s.type
        }
    }
    return "lowrise"
}

function kindsForDistrict(d: District, detail: number): BuildingKind[] {
    if (d === "downtown") {
        return detail >= 2
            ? ["office", "tall", "block", "hotel", "apartments", "tower", "terrace", "twin", "needle", "garage", "datacenter"]
            : ["block", "block", "step", "slab", "block", "peak", "terrace", "hotel", "office", "garage"]
    }
    if (d === "office") {
        return ["office", "slab", "tall", "block", "step", "hotel", "terrace", "twin", "garage", "datacenter"]
    }
        if (d === "industrial") {
        return ["factory", "warehouse", "stack", "tank", "factory", "warehouse", "construction"]
    }
    if (d === "pagoda") {
        return ["pagoda"]
    }
    if (d === "market") {
        return ["stall", "stall", "block", "villa", "stall", "terrace"]
    }
    if (d === "arena") {
        return ["arena", "block", "terrace", "stall", "block"]
    }
    if (d === "arts") {
        return ["artscenter", "block", "villa"]
    }
    if (d === "cathedral") {
        return ["cathedral"]
    }
    if (d === "wharf") {
        return ["marquee", "warehouse", "block", "villa", "slab", "warehouse", "aquarium"]
    }
    if (d === "amusement") {
        return ["ferris", "coaster", "stall", "block"]
    }
    if (d === "neon") {
        return ["neonshop", "neonshop", "hotel", "stall", "block"]
    }
    if (d === "lighthouse") {
        return ["lighthouse"]
    }
    if (d === "civic") {
        return ["hospital", "school", "block", "terrace"]
    }
    if (d === "campus") {
        return ["school", "school", "block", "villa", "greenhouse"]
    }
    if (d === "utilities") {
        return ["powerplant", "stack", "tank"]
    }
    if (d === "station") {
        return ["station"]
    }
    if (d === "harborvillage") {
        return ["stilthouse", "stilthouse", "villa", "warehouse", "marquee"]
    }
    if (d === "castle") {
        return ["castle"]
    }
    if (d === "cinema") {
        return ["cinema", "neonshop", "block"]
    }
    if (d === "floatmarket") {
        return ["floatstall", "floatstall", "stall", "stilthouse"]
    }
    if (d === "aquarium") {
        return ["aquarium", "warehouse", "block"]
    }
    if (d === "greenhouse") {
        return ["greenhouse", "school", "villa"]
    }
    return detail >= 2
        ? ["apartments", "block", "villa", "step", "peak", "office", "terrace"]
        : ["block", "step", "peak", "apartments", "villa"]
}

function makeLayer(
    seed: number,
    minH: number,
    maxH: number,
    colors: string[],
    density: number,
    landmarks: boolean,
    gapChance: number,
    maxGap: number,
    detail: number,
    plazas = false,
    plazaSpans?: { start: number; end: number }[],
    factorySpans?: { start: number; end: number }[],
    viewGaps?: { start: number; end: number }[],
    extraDistricts: District[] = [],
): Building[] {
    const rng = mulberry32(seed)
    const districts = makeDistricts(rng, extraDistricts)
    const buildings: Building[] = []
    const signColors = ["#7a5a68", "#6a7088", "#8a6848", "#4a6a78", "#5a4870", "#6a5878"]
    let x = 2
    let towerPlaced = !landmarks
    let tallCooldown = 0

    while (x < WORLD - 4) {
        let district = districtAt(districts, x)
        if (factorySpans && inSpans(factorySpans, x)) {
            district = "industrial"
        }
        const span = districtSpanAt(districts, x)
        if (district === "cathedral" && buildings.some(b => b.kind === "cathedral" && span && b.x >= span.start && b.x < span.end)) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "lighthouse" && buildings.some(b => b.kind === "lighthouse" && span && b.x >= span.start && b.x < span.end)
            && buildings.some(b => b.kind === "marquee" && span && b.x >= span.start && b.x < span.end)) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "amusement" && buildings.filter(b => b.kind === "ferris" && span && b.x >= span.start && b.x < span.end).length >= 1
            && buildings.filter(b => b.kind === "coaster" && span && b.x >= span.start && b.x < span.end).length >= 1) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "pagoda") {
            const pagodas = buildings.filter(b => b.kind === "pagoda" && span && b.x >= span.start && b.x < span.end).length
            if (pagodas >= 2) {
                x = span ? span.end : x + 8
                continue
            }
        }
        if (district === "cinema" && buildings.some(b => b.kind === "cinema" && span && b.x >= span.start && b.x < span.end)
            && buildings.filter(b => span && b.x >= span.start && b.x < span.end).length >= 3) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "aquarium" && buildings.some(b => b.kind === "aquarium" && span && b.x >= span.start && b.x < span.end)
            && buildings.filter(b => span && b.x >= span.start && b.x < span.end).length >= 3) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "greenhouse" && buildings.some(b => b.kind === "greenhouse" && span && b.x >= span.start && b.x < span.end)
            && buildings.filter(b => span && b.x >= span.start && b.x < span.end).length >= 3) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "castle" && buildings.some(b => b.kind === "castle" && span && b.x >= span.start && b.x < span.end)) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "station" && buildings.some(b => b.kind === "station" && span && b.x >= span.start && b.x < span.end)) {
            x = span ? span.end : x + 8
            continue
        }
        if (district === "utilities" && buildings.some(b => b.kind === "powerplant" && span && b.x >= span.start && b.x < span.end)
            && buildings.filter(b => span && b.x >= span.start && b.x < span.end).length >= 4) {
            x = span ? span.end : x + 8
            continue
        }
        const keepOpen = district !== "industrial" && district !== "arts" && district !== "pagoda" && district !== "cathedral" && district !== "amusement" && district !== "neon" && district !== "lighthouse" && district !== "civic" && district !== "campus" && district !== "utilities" && district !== "station" && district !== "harborvillage" && district !== "castle" && district !== "cinema" && district !== "floatmarket" && district !== "aquarium" && district !== "greenhouse"
        if (plazaSpans && inSpans(plazaSpans, x) && keepOpen) {
            x = spanEndAt(plazaSpans, x)
            continue
        }
        if (viewGaps && inSpans(viewGaps, x) && keepOpen) {
            x = spanEndAt(viewGaps, x)
            continue
        }
        if (plazas && keepOpen && district !== "arena" && rng() < 0.16) {
            x += 28 + Math.floor(rng() * 52)
            continue
        }
        const localGap = district === "downtown" ? gapChance * 0.35
            : district === "office" ? gapChance * 0.7
            : district === "industrial" ? gapChance * 0.2
            : district === "arts" || district === "pagoda" || district === "cathedral" || district === "amusement" || district === "neon" || district === "lighthouse" || district === "civic" || district === "campus" || district === "utilities" || district === "station" || district === "harborvillage" || district === "castle" || district === "cinema" || district === "floatmarket" || district === "aquarium" || district === "greenhouse" ? 0
            : gapChance * 1.25
        if (rng() < localGap) {
            x += 2 + Math.floor(rng() * maxGap)
            continue
        }

        let w = 4 + Math.floor(rng() * 7)
        let kind: BuildingKind = pick(rng, kindsForDistrict(district, detail))
        if (district === "arts" && !buildings.some(b => b.kind === "artscenter")) {
            kind = "artscenter"
        }
        if (district === "pagoda") {
            kind = "pagoda"
            const pagodas = buildings.filter(b => b.kind === "pagoda" && span && b.x >= span.start && b.x < span.end).length
            if (pagodas === 1) {
                x += 12 + Math.floor(rng() * 16)
            }
        }
        if (district === "cathedral") {
            kind = "cathedral"
        }
        if (district === "lighthouse") {
            const hasLight = buildings.some(b => b.kind === "lighthouse" && span && b.x >= span.start && b.x < span.end)
            kind = hasLight ? "marquee" : "lighthouse"
        }
        if (district === "amusement") {
            const hasFerris = buildings.some(b => b.kind === "ferris" && span && b.x >= span.start && b.x < span.end)
            const hasCoaster = buildings.some(b => b.kind === "coaster" && span && b.x >= span.start && b.x < span.end)
            if (!hasFerris) {
                kind = "ferris"
            } else if (!hasCoaster) {
                kind = "coaster"
            }
        }
        if (district === "neon" && rng() > 0.25) {
            kind = "neonshop"
        }
        if (district === "civic" && !buildings.some(b => b.kind === "hospital" && span && b.x >= span.start && b.x < span.end)) {
            kind = "hospital"
        }
        if (district === "campus") {
            kind = "school"
        }
        if (district === "utilities" && !buildings.some(b => b.kind === "powerplant" && span && b.x >= span.start && b.x < span.end)) {
            kind = "powerplant"
        }
        if (district === "station") {
            kind = "station"
        }
        if (district === "harborvillage" && rng() > 0.25) {
            kind = "stilthouse"
        }
        if ((district === "harborvillage" || district === "wharf") && !buildings.some(b => b.kind === "marquee" && span && b.x >= span.start && b.x < span.end) && rng() > 0.35) {
            kind = "marquee"
        }
        if (district === "castle") {
            kind = "castle"
        }
        if (district === "cinema" && !buildings.some(b => b.kind === "cinema" && span && b.x >= span.start && b.x < span.end)) {
            kind = "cinema"
        }
        if (district === "floatmarket" && rng() > 0.2) {
            kind = "floatstall"
        }
        if (district === "aquarium" && !buildings.some(b => b.kind === "aquarium" && span && b.x >= span.start && b.x < span.end)) {
            kind = "aquarium"
        }
        if (district === "greenhouse" && !buildings.some(b => b.kind === "greenhouse" && span && b.x >= span.start && b.x < span.end)) {
            kind = "greenhouse"
        }
        if (extraDistricts.length > 0 && !buildings.some(b => b.kind === "controltower") && x > 300 && x < 420 && rng() > 0.4) {
            kind = "controltower"
        }
        if (detail >= 2 && district === "industrial" && kind !== "stack"
            && !buildings.some(b => b.kind === "harborcrane" && span && b.x >= span.start && b.x < span.end)
            && rng() > 0.84) {
            kind = "harborcrane"
        }
        if (tallCooldown > 0 && (kind === "tall" || kind === "tower" || kind === "needle")) {
            kind = pick(rng, ["block", "step", "slab", "office", "apartments", "peak", "villa", "terrace"])
        }
        const heightRoll = Math.pow(rng(), detail === 0 ? 1.6 : 1.2)
        let h = minH + Math.floor(heightRoll * Math.max(1, maxH - minH))
        if (district === "lowrise") {
            h = minH + Math.floor(rng() * Math.max(1, (maxH - minH) * 0.65))
        } else if (district === "industrial") {
            h = minH + Math.floor(rng() * Math.max(4, (maxH - minH) * 0.5))
        }
        let spire = 0
        let spireGold = false
        let antenna = rng() > 0.78
        let crown = 0
        let color = pick(rng, colors)
        let winStyle: WindowStyle = district === "downtown" ? "tight"
            : district === "office" ? "grid"
            : district === "industrial" ? "sparse"
            : rng() > 0.5 ? "bands" : "grid"
        if (detail >= 2 && rng() > 0.55) {
            winStyle = pick(rng, ["tight", "stripes", "bands", "grid", "checkers", "columns"])
        } else if (detail >= 1 && rng() > 0.75) {
            winStyle = pick(rng, ["grid", "tight", "bands", "checkers"])
        }

        if (kind === "tower") {
            w = 4 + Math.floor(rng() * 4)
            h = Math.min(maxH + 4, h + 6)
            antenna = rng() > 0.45
        } else if (kind === "tall") {
            w = 5 + Math.floor(rng() * 4)
            h = maxH - 6 + Math.floor(rng() * 8)
            antenna = rng() > 0.5
        } else if (kind === "needle") {
            w = 3 + Math.floor(rng() * 3)
            h = maxH + 4 + Math.floor(rng() * 10)
            spire = 8 + Math.floor(rng() * 10)
            antenna = false
        } else if (kind === "factory") {
            w = 9 + Math.floor(rng() * 8)
            h = minH + Math.floor(rng() * Math.max(4, (maxH - minH) * 0.5))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "warehouse") {
            w = 10 + Math.floor(rng() * 8)
            h = minH + Math.floor(rng() * Math.max(3, (maxH - minH) * 0.38))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "stack") {
            w = 3 + Math.floor(rng() * 2)
            h = Math.min(maxH + 8, minH + 10 + Math.floor(rng() * Math.max(8, maxH - minH)))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "tank") {
            w = 7 + Math.floor(rng() * 5)
            h = Math.max(6, minH - 2 + Math.floor(rng() * 6))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "slab" || kind === "office" || kind === "hotel") {
            w = 6 + Math.floor(rng() * 8)
            winStyle = kind === "office" ? "tight" : kind === "hotel" ? "bands" : "grid"
        } else if (kind === "apartments" || kind === "terrace") {
            w = 5 + Math.floor(rng() * 6)
            winStyle = rng() > 0.5 ? "bands" : "tight"
        } else if (kind === "villa") {
            w = 5 + Math.floor(rng() * 4)
            h = minH + Math.floor(rng() * Math.max(3, (maxH - minH) * 0.4))
            antenna = false
        } else if (kind === "twin") {
            w = 7 + Math.floor(rng() * 5)
            winStyle = "columns"
        } else if (kind === "pagoda") {
            w = 16 + Math.floor(rng() * 6)
            h = Math.max(16, minH + 10)
            antenna = false
            winStyle = "sparse"
            color = "#6a3848"
        } else if (kind === "stall") {
            w = 5 + Math.floor(rng() * 5)
            h = Math.max(6, minH - 1 + Math.floor(rng() * 5))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "arena") {
            w = 16 + Math.floor(rng() * 10)
            h = Math.min(maxH + 2, minH + 6 + Math.floor(rng() * 6))
            antenna = false
            winStyle = "sparse"
        } else if (kind === "artscenter") {
            w = 38 + Math.floor(rng() * 12)
            h = Math.max(12, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2e3a50", "#343e54", "#2a3648"])
        } else if (kind === "cathedral") {
            w = 26 + Math.floor(rng() * 8)
            h = Math.max(16, minH + 8)
            antenna = false
            winStyle = "sparse"
            color = "#2a303c"
        } else if (kind === "ferris") {
            w = 26 + Math.floor(rng() * 8)
            h = Math.max(14, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a3248", "#243044"])
        } else if (kind === "coaster") {
            w = 48 + Math.floor(rng() * 16)
            h = Math.max(16, minH + 8)
            antenna = false
            winStyle = "sparse"
        } else if (kind === "lighthouse") {
            w = 7 + Math.floor(rng() * 3)
            h = Math.max(16, minH + 10)
            antenna = false
            winStyle = "sparse"
        } else if (kind === "harborcrane") {
            w = 14 + Math.floor(rng() * 6)
            h = Math.max(10, minH + 4)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#5a6878", "#6a7488"])
        } else if (kind === "neonshop") {
            w = 6 + Math.floor(rng() * 6)
            h = Math.max(8, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#3a2438", "#2a2030", "#402838"])
        } else if (kind === "hospital") {
            w = 18 + Math.floor(rng() * 8)
            h = Math.max(12, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#3a4458", "#404860"])
        } else if (kind === "school") {
            w = 22 + Math.floor(rng() * 10)
            h = Math.max(8, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#3a4050", "#343c4c"])
        } else if (kind === "powerplant") {
            w = 22 + Math.floor(rng() * 8)
            h = Math.max(14, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#3a4248", "#323840"])
        } else if (kind === "station") {
            w = 36 + Math.floor(rng() * 10)
            h = Math.max(12, minH + 4)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2e3848", "#343e50"])
        } else if (kind === "controltower") {
            w = 8 + Math.floor(rng() * 3)
            h = Math.max(16, minH + 10)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a3448", "#243040"])
        } else if (kind === "stilthouse") {
            w = 8 + Math.floor(rng() * 5)
            h = Math.max(10, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#4a3840", "#3a3040", "#403848"])
        } else if (kind === "castle") {
            w = 34 + Math.floor(rng() * 10)
            h = Math.max(14, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a3038", "#242830"])
        } else if (kind === "cinema") {
            w = 14 + Math.floor(rng() * 6)
            h = Math.max(10, minH + 3)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#3a2438", "#2a2030"])
        } else if (kind === "garage") {
            w = 16 + Math.floor(rng() * 10)
            h = Math.max(8, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a3040", "#242c3c"])
        } else if (kind === "construction") {
            w = 12 + Math.floor(rng() * 8)
            h = Math.max(12, minH + 6)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#4a5468", "#3a4458"])
        } else if (kind === "greenhouse") {
            w = 14 + Math.floor(rng() * 8)
            h = Math.max(8, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a4038", "#243830"])
        } else if (kind === "aquarium") {
            w = 22 + Math.floor(rng() * 10)
            h = Math.max(10, minH + 3)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#1a3048", "#243850"])
        } else if (kind === "datacenter") {
            w = 16 + Math.floor(rng() * 8)
            h = Math.max(10, minH + 2)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#1a2030", "#161c28"])
        } else if (kind === "floatstall") {
            w = 6 + Math.floor(rng() * 5)
            h = Math.max(8, minH + 1)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#6a4050", "#5a5040", "#4a4868", "#684848"])
        } else if (kind === "marquee") {
            w = 4 + MARQUEE_COLS * MARQUEE_PITCH
            h = Math.max(14, minH + 5)
            antenna = false
            winStyle = "sparse"
            color = pick(rng, ["#2a3448", "#243040", "#323848"])
        }

        if (landmarks && !towerPlaced && x > 160 && x < 280) {
            w = 9
            h = maxH + 10
            spire = 18
            spireGold = true
            crown = 4
            color = "#8a96b4"
            antenna = false
            kind = "tower"
            towerPlaced = true
        } else if (rng() > 0.94 && kind === "tower") {
            spire = 6 + Math.floor(rng() * 8)
            antenna = false
        }

        if (kind === "tall" || kind === "needle" || kind === "tower") {
            tallCooldown = detail === 0 ? 6 + Math.floor(rng() * 4) : 4 + Math.floor(rng() * 3)
        } else if (tallCooldown > 0) {
            tallCooldown -= 1
        }

        if (x + w > WORLD - 2) {
            w = WORLD - 2 - x
            if (w < 3) {
                break
            }
        }

        let sign: BuildingSign | null = null
        const signChance = detail <= 0 ? 0 : detail === 1 ? 0.16 : 0.38
        const wantSign = district === "downtown" ? signChance + 0.18
            : district === "office" ? signChance + 0.08
            : district === "industrial" || district === "pagoda" || district === "arena" || district === "arts" || district === "cathedral" || district === "amusement" || district === "lighthouse" || district === "civic" || district === "campus" || district === "utilities" || district === "station" || district === "harborvillage" || district === "castle" || district === "cinema" || district === "floatmarket" || district === "aquarium" || district === "greenhouse" ? 0
            : district === "neon" ? 0.7
            : district === "market" ? signChance * 0.5
            : signChance * 0.7
        if (rng() < wantSign && w >= 5 && h >= 12) {
            const vert = rng() > 0.4
            const colorS = pick(rng, signColors)
            if (vert) {
                const sw = 2 + Math.floor(rng() * 2)
                const sh = 8 + Math.floor(rng() * Math.min(16, h * 0.45))
                sign = {
                    vert: true,
                    color: colorS,
                    motif: pick(rng, [0, 2, 3, 4]),
                    x: rng() > 0.5 ? 1 : Math.max(1, w - sw - 2),
                    y: 3 + Math.floor(rng() * 6),
                    w: sw,
                    h: sh,
                }
            } else {
                const bh = 4 + Math.floor(rng() * 3)
                sign = {
                    vert: false,
                    color: colorS,
                    motif: pick(rng, [0, 2, 3, 4]),
                    x: 1,
                    y: 0,
                    w: Math.max(4, w - 2),
                    h: bh,
                }
            }
        }

        if (district === "industrial") {
            color = shadeHex(pick(rng, colors), rng() > 0.5 ? -8 : 6)
            antenna = false
            sign = null
        }
        if (district === "market" && kind === "stall") {
            color = pick(rng, ["#6a4050", "#5a5040", "#4a4868", "#684848", "#3a5458"])
        }
        if (kind === "pagoda") {
            color = pick(rng, [color, shadeHex(color, -12), "#5a4058"])
            antenna = false
        }

        const specialRoof = district === "industrial" || district === "pagoda" || district === "market" || district === "arena" || district === "arts" || district === "cathedral" || district === "amusement" || district === "neon" || district === "lighthouse" || district === "civic" || district === "campus" || district === "utilities" || district === "station" || district === "harborvillage" || district === "castle" || district === "cinema" || district === "floatmarket" || district === "aquarium" || district === "greenhouse"
        let roofProp: RoofProp = "none"
        if (!specialRoof && w >= 6 && h >= 12 && rng() > 0.55) {
            roofProp = pick(rng, ["watertower", "watertower", "dish", "dish", "none"])
            if (detail >= 2 && rng() > 0.96) {
                roofProp = "crane"
            }
            if (detail >= 1 && w >= 4 && h >= 16 && rng() > 0.88) {
                roofProp = "mast"
            }
            if (detail >= 2 && w >= 8 && h >= 14 && rng() > 0.97) {
                roofProp = "helipad"
            }
        }
        if (kind === "harborcrane" || kind === "lighthouse" || kind === "ferris" || kind === "coaster" || kind === "stack" || kind === "tank" || kind === "hospital" || kind === "school" || kind === "powerplant" || kind === "station" || kind === "controltower" || kind === "stilthouse" || kind === "castle" || kind === "cinema" || kind === "garage" || kind === "construction" || kind === "greenhouse" || kind === "aquarium" || kind === "datacenter" || kind === "floatstall" || kind === "marquee") {
            roofProp = "none"
        }
        if (extraDistricts.length > 0 && !buildings.some(b => b.roofProp === "helipad")
            && kind !== "harborcrane" && kind !== "lighthouse" && kind !== "ferris" && kind !== "coaster"
            && kind !== "pagoda" && kind !== "cathedral" && kind !== "artscenter"
            && kind !== "hospital" && kind !== "school" && kind !== "powerplant" && kind !== "station"
            && kind !== "controltower" && kind !== "stilthouse" && kind !== "castle" && kind !== "cinema"
            && kind !== "garage" && kind !== "construction" && kind !== "greenhouse" && kind !== "aquarium"
            && kind !== "datacenter" && kind !== "floatstall" && kind !== "marquee"
            && w >= 8 && h >= 12 && x > 90 && x < 400) {
            roofProp = "helipad"
        }
        if (extraDistricts.length > 0 && !buildings.some(b => b.roofProp === "mast")
            && kind !== "harborcrane" && kind !== "lighthouse" && kind !== "ferris" && kind !== "coaster"
            && w >= 4 && h >= 14 && x > 420 && x < 620) {
            roofProp = "mast"
        }
        const neon = district === "neon" || kind === "neonshop"

        const b: Building = {
            x,
            w,
            h,
            color,
            shade: shadeHex(color, -28),
            windows: makeWindows(rng, w, h, density, kind, winStyle),
            kind,
            spire,
            spireGold,
            antenna,
            crown,
            sign: neon && !sign && w >= 5 ? {
                vert: rng() > 0.35,
                color: pick(rng, ["#c45a78", "#5a88c8", "#c87848", "#7a5aa8"]),
                motif: pick(rng, [0, 2, 3, 4]),
                x: 1,
                y: 2,
                w: 2 + Math.floor(rng() * 2),
                h: 6 + Math.floor(rng() * 6),
            } : sign,
            winStyle,
            greeble: specialRoof ? 0 : Math.floor(rng() * 4),
            roofProp,
            neon,
            sprite: document.createElement("canvas"),
        }
        b.sprite = paintBuilding(b)
        buildings.push(b)
        x += w
    }
    if (extraDistricts.length > 0 && !buildings.some(b => b.kind === "marquee") && rng() > 0.42) {
        const mw = 4 + MARQUEE_COLS * MARQUEE_PITCH
        const mh = Math.max(14, minH + 5)
        for (let tries = 0; tries < 18; tries++) {
            const mx = 16 + Math.floor(rng() * Math.max(1, WORLD - mw - 32))
            if (buildings.some(b => mx < b.x + b.w + 3 && mx + mw > b.x - 3)) {
                continue
            }
            const color = pick(rng, ["#2a3448", "#243040", "#323848"])
            const mb: Building = {
                x: mx,
                w: mw,
                h: mh,
                color,
                shade: shadeHex(color, -28),
                windows: [],
                kind: "marquee",
                spire: 0,
                spireGold: false,
                antenna: false,
                crown: 0,
                sign: null,
                winStyle: "sparse",
                greeble: 0,
                roofProp: "none",
                neon: false,
                sprite: document.createElement("canvas"),
            }
            mb.sprite = paintBuilding(mb)
            buildings.push(mb)
            break
        }
    }
    return buildings
}

function makeClouds(seed: number): Cloud[] {
    const rng = mulberry32(seed)
    const clouds: Cloud[] = []
    for (let i = 0; i < 8; i++) {
        const parts: Cloud["parts"] = []
        const n = 3 + Math.floor(rng() * 3)
        let acc = 0
        for (let p = 0; p < n; p++) {
            const w = 10 + Math.floor(rng() * 16)
            parts.push({
                dx: acc,
                dy: Math.floor(rng() * 3) - 1,
                w,
                h: 3 + Math.floor(rng() * 3),
            })
            acc += Math.floor(w * 0.55)
        }
        clouds.push({
            x: Math.floor(rng() * WORLD),
            y: 56 + Math.floor(rng() * 28),
            parts,
        })
    }
    return clouds
}

function makeStars(seed: number, cssW: number, cssH: number): Star[] {
    const rng = mulberry32(seed)
    const count = Math.round(Math.max(14, Math.min(36, cssW * cssH / 14000)))
    const stars: Star[] = []
    for (let i = 0; i < count; i++) {
        stars.push({
            x: rng() * cssW,
            y: rng() * Math.max(24, cssH * 0.7),
            s: PIXEL,
            tw: rng() * Math.PI * 2,
            twAmp: rng() < 0.58 ? 0.03 + rng() * 0.035 : 0,
        })
    }
    return stars
}

function makeBoats(seed: number): Boat[] {
    const rng = mulberry32(seed)
    const kinds: Boat["kind"][] = ["skiff", "ferry", "tug", "yacht", "cargo", "steamer", "lantern", "fireboat", "police", "sailboat"]
    const boats: Boat[] = []
    for (let i = 0; i < 10; i++) {
        const kind = kinds[i % kinds.length]
        const reverse = rng() > 0.4
        const spd = kind === "yacht" ? 2.6 + rng() * 2.4
            : kind === "cargo" || kind === "steamer" ? 1.0 + rng() * 1.1
            : kind === "tug" ? 1.4 + rng() * 1.4
            : kind === "lantern" ? 1.2 + rng() * 1.3
            : kind === "ferry" ? 1.5 + rng() * 1.2
            : kind === "fireboat" ? 2.0 + rng() * 1.6
            : kind === "police" ? 2.4 + rng() * 2.0
            : kind === "sailboat" ? 1.1 + rng() * 0.9
            : 1.6 + rng() * 2.4
        boats.push({
            x: rng() * WORLD,
            y: 5 + Math.floor(rng() * 22),
            w: kind === "cargo" || kind === "steamer" ? 16 + Math.floor(rng() * 8)
                : kind === "yacht" ? 11 + Math.floor(rng() * 5)
                : kind === "ferry" ? 18 + Math.floor(rng() * 6)
                : kind === "fireboat" ? 12 + Math.floor(rng() * 4)
                : kind === "police" ? 9 + Math.floor(rng() * 3)
                : kind === "tug" ? 7 + Math.floor(rng() * 3)
                : kind === "lantern" ? 8 + Math.floor(rng() * 4)
                : kind === "sailboat" ? 9 + Math.floor(rng() * 4)
                : 7 + Math.floor(rng() * 6),
            speed: reverse ? -spd : spd,
            kind,
        })
    }
    if (rng() > 0.28) {
        const reverse = rng() > 0.5
        const spd = 0.55 + rng() * 0.4
        boats.push({
            x: rng() * WORLD,
            y: 12 + Math.floor(rng() * 16),
            w: 38 + Math.floor(rng() * 10),
            speed: reverse ? -spd : spd,
            kind: "cruise",
        })
    }
    return boats
}

function makeHouseboats(seed: number): Boat[] {
    const rng = mulberry32(seed)
    const boats: Boat[] = []
    const n = 2 + Math.floor(rng() * 2)
    for (let i = 0; i < n; i++) {
        boats.push({
            x: 40 + Math.floor(rng() * (WORLD - 80)),
            y: 1 + Math.floor(rng() * 3),
            w: 9 + Math.floor(rng() * 5),
            speed: 0,
            kind: "houseboat",
        })
    }
    return boats
}

function makeCrafts(seed: number): Craft[] {
    const rng = mulberry32(seed)
    return [
        { x: rng() * WORLD, y: 70 + Math.floor(rng() * 18), speed: 4 + rng() * 3, blink: rng() * Math.PI * 2, kind: "blimp" },
        { x: rng() * WORLD, y: 48 + Math.floor(rng() * 22), speed: -(7 + rng() * 5), blink: rng() * Math.PI * 2, kind: "heli" },
        { x: rng() * WORLD, y: 40 + Math.floor(rng() * 16), speed: 8 + rng() * 6, blink: rng() * Math.PI * 2, kind: "heli" },
        { x: rng() * WORLD, y: 78 + Math.floor(rng() * 16), speed: (rng() > 0.5 ? 1 : -1) * (1.1 + rng() * 0.8), blink: rng() * Math.PI * 2, kind: "balloon" },
    ]
}

function bakeBridge(): HTMLCanvasElement {
    const w = 108
    const h = 44
    const { canvas, ctx } = artCanvas(w, h)
    if (!ctx) {
        return canvas
    }
    const steel = "#2a3244"
    const dark = "#161c28"
    const deckY = 30
    px(ctx, steel, 8, 4, 3, deckY)
    px(ctx, steel, w - 11, 4, 3, deckY)
    px(ctx, dark, 10, 4, 1, deckY)
    px(ctx, dark, w - 9, 4, 1, deckY)
    px(ctx, "#c9a43a", 9, 3, 1, 2)
    px(ctx, "#c9a43a", w - 10, 3, 1, 2)
    px(ctx, steel, 0, deckY, w, 3)
    px(ctx, "#4a5468", 0, deckY + 1, w, 1)
    for (let x = 0; x < w; x++) {
        const t = x / (w - 1)
        const sag = Math.round(Math.sin(t * Math.PI) * 18)
        px(ctx, steel, x, 6 + (18 - sag), 1, 1)
        if (x % 6 === 0) {
            px(ctx, dark, x, 6 + (18 - sag), 1, deckY - (6 + (18 - sag)))
        }
    }
    px(ctx, dark, 8, h - 8, 3, 8)
    px(ctx, dark, w - 11, h - 8, 3, 8)
    return canvas
}

function bakeOilRig(): HTMLCanvasElement {
    const w = 28
    const h = 22
    const { canvas, ctx } = artCanvas(w, h)
    if (!ctx) {
        return canvas
    }
    const steel = "#3a4254"
    const dark = "#1a2030"
    px(ctx, steel, 4, 8, 2, h - 8)
    px(ctx, steel, 22, 8, 2, h - 8)
    px(ctx, steel, 4, 10, 20, 3)
    px(ctx, dark, 6, 6, 16, 5)
    px(ctx, WIN, 8, 8, 1, 1)
    px(ctx, WIN, 12, 8, 1, 1)
    px(ctx, WIN, 16, 8, 1, 1)
    px(ctx, steel, 18, 0, 1, 10)
    px(ctx, "#ff6a4a", 18, 0, 1, 1)
    px(ctx, dark, 2, h - 2, 24, 2)
    return canvas
}

function makePlanes(seed: number): Plane[] {
    const rng = mulberry32(seed)
    const planes: Plane[] = []
    for (let i = 0; i < 3; i++) {
        const reverse = rng() > 0.45
        const spd = i === 0 ? 6 + rng() * 5 : 12 + rng() * 18
        planes.push({
            x: rng() * WORLD,
            y: 52 + Math.floor(rng() * 34),
            speed: reverse ? -spd : spd,
            blink: rng() * Math.PI * 2,
            banner: i === 0,
            contrail: false,
        })
    }
    {
        const reverse = rng() > 0.45
        const spd = 5 + rng() * 4
        planes.push({
            x: rng() * WORLD,
            y: 90 + Math.floor(rng() * 18),
            speed: reverse ? -spd : spd,
            blink: rng() * Math.PI * 2,
            banner: false,
            contrail: true,
        })
    }
    return planes
}

function makeSpotlights(buildings: Building[]): Spotlight[] {
    const rng = mulberry32(227)
    const n = 2 + Math.floor(rng() * 2)
    const candidates = buildings.filter(b => b.h >= 32 && b.w >= 4)
    const pool = candidates.length > 0 ? candidates : buildings
    const lights: Spotlight[] = []
    let tries = 0
    while (lights.length < n && tries < 80 && pool.length > 0) {
        tries += 1
        const b = pool[Math.floor(rng() * pool.length)]
        const x = b.x + Math.floor(b.w / 2)
        if (lights.some(s => Math.abs(s.x - x) < 48)) {
            continue
        }
        lights.push({
            x,
            yOff: Math.max(8, Math.floor(b.h * 0.52)),
            phase: rng() * Math.PI * 2,
            rate: 0.16 + rng() * 0.14,
            sweep: 0.22 + rng() * 0.18,
            lean: (rng() - 0.5) * 0.2,
            len: 150 + Math.floor(rng() * 70),
            clock: 0,
        })
    }
    return lights
}

function wrapCopies(viewArtW: number) {
    return Math.ceil(viewArtW / WORLD) + 2
}

function makeHills(seed: number, minPeak: number, maxPeak: number, minSpread: number, maxSpread: number, span = WORLD) {
    const rng = mulberry32(seed)
    const hills: { cx: number; spread: number; peak: number; sharp: number; lin: number; skew: number }[] = []
    let x = -20
    while (x < span + 40) {
        const spread = minSpread + Math.floor(rng() * (maxSpread - minSpread))
        let peak = minPeak + Math.floor(rng() * (maxPeak - minPeak))
        if (rng() > 0.86) {
            peak = Math.min(maxPeak + 10, peak + 8 + Math.floor(rng() * 8))
        }
        hills.push({
            cx: x + spread,
            spread: rng() > 0.8 ? spread + 16 + Math.floor(rng() * 14) : spread,
            peak,
            sharp: 0.48 + rng() * 0.38,
            lin: 0.18 + rng() * 0.28,
            skew: (rng() - 0.5) * 0.55,
        })
        x += Math.floor(spread * 0.62)
    }
    return hills
}

function hillHeightAt(hills: { cx: number; spread: number; peak: number; sharp: number; lin: number; skew: number }[], x: number) {
    let h = 0
    for (const hill of hills) {
        const dx = x - hill.cx
        const spread = dx < 0 ? hill.spread * (1 - hill.skew) : hill.spread * (1 + hill.skew)
        const d = Math.abs(dx) / Math.max(6, spread)
        if (d >= 1) {
            continue
        }
        const v = Math.round(hill.peak * Math.max(0, 1 - d * d * hill.sharp - d * hill.lin))
        if (v > h) {
            h = v
        }
    }
    return h
}

function bakeMountains(seed: number, base: string, mid: string, shade: string, snow: string, minPeak: number, maxPeak: number): HTMLCanvasElement {
    const span = WORLD * 3
    const hills = makeHills(seed, minPeak, maxPeak, 28, 60, span)
    let tallest = maxPeak
    const heights = new Array(span)
    for (let x = 0; x < span; x++) {
        heights[x] = hillHeightAt(hills, x)
        if (heights[x] > tallest) {
            tallest = heights[x]
        }
    }
    const canvas = document.createElement("canvas")
    canvas.width = span
    canvas.height = tallest + 6
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    for (let x = 0; x < span; x++) {
        const h = heights[x]
        if (h <= 0) {
            continue
        }
        const top = canvas.height - h
        const rising = x > 0 && h > heights[x - 1]
        ctx.fillStyle = rising ? mid : base
        ctx.fillRect(x, top, 1, h)
        ctx.fillStyle = shade
        ctx.fillRect(x, top + Math.floor(h * 0.55), 1, h - Math.floor(h * 0.55))
        if (h > tallest * 0.78) {
            ctx.fillStyle = snow
            ctx.fillRect(x, top, 1, 2)
        } else if (rising && (x % 5 === 0)) {
            ctx.fillStyle = mid
            ctx.fillRect(x, top + 2, 1, 1)
        }
    }
    const rngObs = mulberry32(seed + 91)
    let placed = 0
    let guard = 0
    while (placed < 2 && guard < 40) {
        guard += 1
        const ox = 40 + Math.floor(rngObs() * (span - 80))
        const hh = heights[ox]
        if (hh < tallest * 0.62) {
            continue
        }
        if (ox > 8 && Math.abs(heights[ox] - heights[ox - 6]) > 8) {
            continue
        }
        const top = canvas.height - hh
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(ox - 3, top - 5, 7, 5)
        ctx.fillStyle = "#5a6878"
        ctx.fillRect(ox - 2, top - 8, 5, 4)
        ctx.fillRect(ox - 1, top - 9, 3, 1)
        ctx.fillStyle = WIN
        ctx.fillRect(ox, top - 7, 1, 1)
        ctx.fillStyle = "#2a3448"
        ctx.fillRect(ox, top - 9, 1, 2)
        placed += 1
    }
    return canvas
}

function makeSkiSlope(seed: number, minPeak: number, maxPeak: number): { x: number; y: number }[] {
    const span = WORLD * 3
    const hills = makeHills(seed, minPeak, maxPeak, 28, 60, span)
    let tallest = maxPeak
    const heights = new Array(span)
    let peakX = Math.floor(span * 0.35)
    for (let x = 0; x < span; x++) {
        heights[x] = hillHeightAt(hills, x)
        if (heights[x] > tallest) {
            tallest = heights[x]
            peakX = x
        }
    }
    const canvasH = tallest + 6
    const lights: { x: number; y: number }[] = []
    let x = peakX + 3
    let steps = 0
    while (x < span - 8 && steps < 18) {
        const h = heights[x]
        if (h < tallest * 0.42) {
            break
        }
        lights.push({ x, y: canvasH - h })
        x += 5
        steps += 1
    }
    return lights
}

function bakeBuildingLayer(buildings: Building[]): HTMLCanvasElement {
    let maxH = 8
    for (const b of buildings) {
        if (b.h > maxH) {
            maxH = b.h
        }
    }
    const extra = BUILD_EXTRA
    const canvas = document.createElement("canvas")
    canvas.width = WORLD
    canvas.height = extra + maxH
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    for (const b of buildings) {
        ctx.drawImage(b.sprite, b.x, maxH - b.h)
    }
    return canvas
}

function bakeShoreline(seed: number): HTMLCanvasElement {
    const rng = mulberry32(seed)
    const h = 18
    const canvas = document.createElement("canvas")
    canvas.width = WORLD
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    const ink = "#0c101c"
    const ink2 = "#141a28"
    const dim = "#2a2410"

    const tree = (tx: number, base: number) => {
        ctx.fillStyle = ink2
        ctx.fillRect(tx, base - 5, 1, 5)
        ctx.fillRect(tx - 1, base - 8, 3, 4)
        ctx.fillRect(tx, base - 9, 1, 1)
    }
    const house = (hx: number, base: number, hw: number, hh: number) => {
        ctx.fillStyle = ink
        ctx.fillRect(hx, base - hh, hw, hh)
        if (hw > 4 && rng() > 0.45) {
            ctx.fillStyle = dim
            ctx.fillRect(hx + 1, base - hh + 2, 1, 1)
        }
        if (rng() > 0.55) {
            ctx.fillStyle = ink
            ctx.fillRect(hx + Math.floor(hw / 2), base - hh - 2, 1, 2)
        }
    }

    const lamp = (lx: number, base: number) => {
        ctx.fillStyle = ink
        ctx.fillRect(lx, base - 7, 1, 7)
        ctx.fillRect(lx - 1, base - 8, 3, 1)
        ctx.fillStyle = "#6a6030"
        ctx.fillRect(lx, base - 9, 1, 1)
    }

    let x = 4
    while (x < WORLD - 10) {
        if (rng() > 0.42) {
            if (rng() > 0.55) {
                lamp(x, h)
            }
            x += 8 + Math.floor(rng() * 18)
            continue
        }
        const kind = pick(rng, ["land", "houses", "dock", "dock", "trees", "pier", "bridge"])
        if (kind === "dock" || kind === "pier") {
            const w = 12 + Math.floor(rng() * 18)
            ctx.fillStyle = ink
            ctx.fillRect(x, h - 3, w, 2)
            for (let p = 0; p < w; p += 3) {
                ctx.fillRect(x + p, h - 6, 1, 3)
            }
            lamp(x + 2, h - 3)
            if (w > 14) {
                lamp(x + w - 3, h - 3)
            }
            x += w + 6
        } else if (kind === "bridge") {
            const w = 28 + Math.floor(rng() * 24)
            ctx.fillStyle = ink
            ctx.fillRect(x, h - 6, w, 2)
            ctx.fillRect(x, h - 5, 2, 5)
            ctx.fillRect(x + w - 2, h - 5, 2, 5)
            for (let i = 8; i < w - 6; i += 7) {
                ctx.fillRect(x + i, h - 5, 1, 3)
            }
            x += w + 12
        } else if (kind === "trees") {
            const n = 2 + Math.floor(rng() * 4)
            for (let i = 0; i < n; i++) {
                tree(x + i * 4, h)
            }
            x += n * 4 + 10
        } else {
            const w = 16 + Math.floor(rng() * 28)
            const gh = 3 + Math.floor(rng() * 4)
            ctx.fillStyle = ink
            ctx.fillRect(x, h - gh, w, gh)
            ctx.fillRect(x + 1, h - gh - 1, w - 2, 1)
            if (kind === "houses" || rng() > 0.4) {
                let hx = x + 2
                while (hx < x + w - 6) {
                    const hw = 4 + Math.floor(rng() * 5)
                    const hh = 5 + Math.floor(rng() * 6)
                    house(hx, h - gh, hw, hh)
                    if (rng() > 0.55) {
                        tree(hx + hw + 1, h - gh)
                    }
                    hx += hw + 2 + Math.floor(rng() * 3)
                }
            } else if (rng() > 0.35) {
                tree(x + 3, h - gh)
                tree(x + 8, h - gh)
            }
            x += w + 10
        }
    }
    return canvas
}

function bakeClouds(clouds: Cloud[]): HTMLCanvasElement {
    const canvas = document.createElement("canvas")
    canvas.width = WORLD
    canvas.height = 90
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    ctx.fillStyle = "#8ea0cc"
    for (const c of clouds) {
        for (const p of c.parts) {
            ctx.fillRect(c.x + p.dx, canvas.height - c.y + p.dy, p.w, p.h)
        }
    }
    return canvas
}

function bakeTracks(postH: number, railColor: string, postColor: string): HTMLCanvasElement {
    const canvas = document.createElement("canvas")
    canvas.width = WORLD
    canvas.height = postH + 6
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    const deck = canvas.height - postH
    ctx.fillStyle = "#1a1c28"
    ctx.fillRect(0, deck, WORLD, 4)
    ctx.fillStyle = railColor
    ctx.fillRect(0, deck + 1, WORLD, 1)
    ctx.fillRect(0, deck + 3, WORLD, 1)
    ctx.fillStyle = "#2a2430"
    for (let x = 0; x < WORLD; x += 4) {
        ctx.fillRect(x, deck, 2, 4)
    }
    ctx.fillStyle = postColor
    for (let x = 6; x < WORLD; x += 18) {
        ctx.fillRect(x, deck + 4, 2, postH - 1)
        ctx.fillRect(x - 1, canvas.height - 2, 4, 2)
    }
    return canvas
}

function bakeTrain(style: "metro" | "freight" | "shinkansen", cars: number, body: string, cabin: string): HTMLCanvasElement {
    if (style === "shinkansen") {
        const nose = 12
        const carW = 8
        const w = nose + cars * carW + 3
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = 9
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return canvas
        }
        ctx.imageSmoothingEnabled = false
        ctx.fillStyle = body
        ctx.fillRect(2, 3, w - nose - 1, 4)
        for (let i = 0; i < nose; i++) {
            const u = i / (nose - 1)
            const drop = Math.floor(u * u * 3)
            const top = 3 + drop
            const h = Math.max(1, 4 - drop)
            ctx.fillRect(w - nose + i, top, 1, h)
        }
        ctx.fillStyle = shadeHex(body, -18)
        ctx.fillRect(3, 6, w - nose - 2, 1)
        ctx.fillStyle = cabin
        ctx.fillRect(6, 4, w - nose - 8, 1)
        ctx.fillStyle = "#1a2438"
        for (let x = 8; x < w - nose - 2; x += 3) {
            ctx.fillRect(x, 3, 2, 1)
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(w - 3, 5, 1, 1)
        ctx.fillStyle = "#2a3040"
        ctx.fillRect(Math.floor((w - nose) * 0.45), 1, 1, 2)
        ctx.fillRect(Math.floor((w - nose) * 0.45) - 1, 1, 3, 1)
        ctx.fillStyle = "#2a2834"
        ctx.fillRect(10, 7, 2, 2)
        ctx.fillRect(Math.floor(w * 0.45), 7, 2, 2)
        ctx.fillRect(w - nose - 4, 7, 2, 2)
        return canvas
    }
    if (style === "freight") {
        const carW = 11
        const w = 14 + cars * carW
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = 9
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return canvas
        }
        ctx.imageSmoothingEnabled = false
        ctx.fillStyle = cabin
        ctx.fillRect(1, 3, 10, 5)
        ctx.fillStyle = body
        ctx.fillRect(2, 2, 6, 3)
        ctx.fillStyle = "#1a1420"
        ctx.fillRect(3, 1, 2, 2)
        ctx.fillRect(4, 0, 1, 1)
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(8, 4, 1, 1)
        ctx.fillStyle = "#3a3040"
        ctx.fillRect(2, 7, 2, 2)
        ctx.fillRect(7, 7, 2, 2)
        for (let i = 0; i < cars; i++) {
            const x = 13 + i * carW
            ctx.fillStyle = i % 2 === 0 ? body : shadeHex(body, 10)
            ctx.fillRect(x, 2, carW - 2, 6)
            ctx.fillStyle = "#1a1420"
            ctx.fillRect(x - 1, 5, 2, 1)
            ctx.fillRect(x + 2, 3, 2, 3)
            ctx.fillStyle = "#3a3040"
            ctx.fillRect(x + 1, 7, 2, 2)
            ctx.fillRect(x + 6, 7, 2, 2)
        }
        return canvas
    }
    const carW = 12
    const w = 16 + cars * carW
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = 8
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return canvas
    }
    ctx.imageSmoothingEnabled = false
    ctx.fillStyle = body
    ctx.fillRect(1, 3, 12, 4)
    ctx.fillStyle = cabin
    ctx.fillRect(6, 1, 6, 3)
    ctx.fillStyle = "#f5d84a"
    ctx.fillRect(7, 2, 1, 1)
    ctx.fillRect(9, 2, 1, 1)
    ctx.fillStyle = "#1a1420"
    ctx.fillRect(3, 1, 2, 2)
    ctx.fillRect(4, 0, 1, 1)
    ctx.fillStyle = "#3a3040"
    ctx.fillRect(2, 6, 2, 2)
    ctx.fillRect(8, 6, 2, 2)
    for (let i = 0; i < cars; i++) {
        const x = 15 + i * carW
        ctx.fillStyle = body
        ctx.fillRect(x, 3, carW - 2, 4)
        ctx.fillStyle = "#1a1420"
        ctx.fillRect(x - 1, 4, 2, 1)
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(x + 2, 4, 1, 1)
        ctx.fillRect(x + 5, 4, 1, 1)
        ctx.fillStyle = "#3a3040"
        ctx.fillRect(x + 1, 6, 2, 2)
        ctx.fillRect(x + 6, 6, 2, 2)
    }
    return canvas
}

function drawCars(
    ctx: CanvasRenderingContext2D,
    cars: { x: number; y: number; speed: number; color: string; police?: boolean; ambulance?: boolean }[],
    scroll: number,
    waterline: number,
    viewW: number,
    layerSpeed: number,
    t: number,
) {
    const copies = wrapCopies(viewW / PIXEL)
    for (const car of cars) {
        const ax = mod(car.x + t * car.speed - scroll * layerSpeed, WORLD)
        for (let copy = -1; copy <= copies; copy++) {
            const x = (ax + copy * WORLD) * PIXEL
            if (x < -PIXEL || x > viewW) {
                continue
            }
            if (car.police) {
                ctx.fillStyle = Math.floor(t * 10) % 2 === 0 ? "#ff3a4a" : "#3a7aff"
            } else if (car.ambulance) {
                ctx.fillStyle = Math.floor(t * 3.2) % 2 === 0 ? "#ff3a4a" : "#5a2030"
            } else {
                ctx.fillStyle = car.color
            }
            ctx.fillRect(x, (waterline - car.y) * PIXEL, PIXEL, PIXEL)
        }
    }
}

function makeCars(seed: number, count: number, yMin: number, yMax: number, speedMin: number, speedMax: number, policeCount = 0, ambulanceCount = 0) {
    const rng = mulberry32(seed)
    const colors = ["#f5d84a", "#ffe8a0", "#f4f0e0", "#e8a040", "#ff7a4a", "#d0d8e8"]
    const cars: { x: number; y: number; speed: number; color: string; police?: boolean; ambulance?: boolean }[] = []
    for (let i = 0; i < count; i++) {
        const reverse = rng() > 0.55
        const police = i < policeCount
        const ambulance = !police && i < policeCount + ambulanceCount
        const spd = police
            ? 34 + rng() * 16
            : ambulance
                ? 26 + rng() * 10
            : speedMin + rng() * (speedMax - speedMin)
        cars.push({
            x: rng() * WORLD,
            y: yMin + Math.floor(rng() * (yMax - yMin + 1)),
            speed: (reverse ? -1 : 1) * spd,
            color: ambulance ? "#e8e8f0" : pick(rng, colors),
            police,
            ambulance,
        })
    }
    return cars
}

function bakeTram(): HTMLCanvasElement {
        const w = 18
        const h = 5
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return canvas
        }
        ctx.imageSmoothingEnabled = false
        ctx.fillStyle = "#2a3040"
        ctx.fillRect(1, 2, 16, 3)
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(2, 1, 14, 2)
        ctx.fillStyle = WIN
        for (let x = 3; x < 15; x += 2) {
            ctx.fillRect(x, 2, 1, 1)
        }
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(16, 2, 1, 1)
        ctx.fillStyle = "#1a2030"
        ctx.fillRect(8, 0, 1, 1)
        ctx.fillRect(7, 0, 3, 1)
        return canvas
}

function drawTrain(ctx: CanvasRenderingContext2D, train: HTMLCanvasElement, xArt: number, destY: number, viewW: number, wrap = true, doorsFlash = false, t = 0) {
    const w = train.width * PIXEL
    const h = train.height * PIXEL
    const drawOne = (x: number) => {
        ctx.drawImage(train, 0, 0, train.width, train.height, x, destY, w, h)
        if (doorsFlash && Math.floor(t * 6) % 2 === 0) {
            ctx.fillStyle = "#ffe8a0"
            for (let i = 4; i < train.width - 3; i += 4) {
                ctx.fillRect(x + i * PIXEL, destY + 2 * PIXEL, PIXEL, 2 * PIXEL)
            }
        }
    }
    if (!wrap) {
        const x = xArt * PIXEL
        if (x > viewW || x + w < 0) {
            return
        }
        drawOne(x)
        return
    }
    const copies = wrapCopies(viewW / PIXEL)
    for (let copy = -1; copy <= copies; copy++) {
        const x = (xArt + copy * WORLD) * PIXEL
        if (x > viewW || x + w < 0) {
            continue
        }
        drawOne(x)
    }
}

function drawStrip(ctx: CanvasRenderingContext2D, strip: HTMLCanvasElement, scroll: number, destY: number, viewW: number) {
    const artW = strip.width
    const shift = mod(scroll, artW) * PIXEL
    const w = artW * PIXEL
    const h = strip.height * PIXEL
    const copies = Math.ceil(viewW / PIXEL / artW) + 2
    for (let copy = -1; copy <= copies; copy++) {
        const x = copy * w - shift
        if (x > viewW || x + w < 0) {
            continue
        }
        ctx.drawImage(strip, 0, 0, strip.width, strip.height, x, destY, w, h)
    }
}

function snapP(v: number) {
    return Math.round(v / PIXEL) * PIXEL
}

function craneTrolleyAndCable(u: number) {
    const c = mod(u, 1)
    if (c < 0.28) {
        return { trolley: c / 0.28, cable: 0.18 }
    }
    if (c < 0.48) {
        return { trolley: 1, cable: 0.18 + ((c - 0.28) / 0.2) * 0.72 }
    }
    if (c < 0.6) {
        return { trolley: 1, cable: 0.9 }
    }
    if (c < 0.8) {
        return { trolley: 1, cable: 0.9 - ((c - 0.6) / 0.2) * 0.72 }
    }
    return { trolley: Math.max(0, 1 - (c - 0.8) / 0.2), cable: 0.18 }
}

type LandmarkId =
    | "eiffel"
    | "wtc"
    | "burj"
    | "petronas"
    | "empire"
    | "cn"
    | "tokyoTower"
    | "skytree"
    | "twilight"
    | "bigben"
    | "pyramid"

const LANDMARK_IDS: LandmarkId[] = [
    "eiffel", "wtc", "burj", "petronas", "empire", "cn",
    "tokyoTower", "skytree", "twilight", "bigben", "pyramid",
]

function artCanvas(w: number, h: number) {
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (ctx) {
        ctx.imageSmoothingEnabled = false
    }
    return { canvas, ctx }
}

function px(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, w = 1, h = 1) {
    ctx.fillStyle = color
    ctx.fillRect(x, y, w, h)
}

function gridWins(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    color: string,
    winW: number,
    winH: number,
    gapX: number,
    gapY: number,
) {
    const cellW = winW + gapX
    const cellH = winH + gapY
    const cols = Math.floor((w - gapX) / cellW)
    const rows = Math.floor((h - gapY) / cellH)
    if (cols < 1 || rows < 1) {
        return
    }
    const usedW = cols * winW + (cols - 1) * gapX
    const usedH = rows * winH + (rows - 1) * gapY
    const ox = x + Math.floor((w - usedW) / 2)
    const oy = y + Math.floor((h - usedH) / 2)
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            px(ctx, color, ox + c * cellW, oy + r * cellH, winW, winH)
        }
    }
}

function fillDisk(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string) {
    for (let y = -r; y <= r; y++) {
        for (let x = -r; x <= r; x++) {
            if (x * x + y * y <= r * r + r * 0.2) {
                px(ctx, color, cx + x, cy + y)
            }
        }
    }
}

function bakePixelSprite(src: HTMLCanvasElement) {
    const canvas = document.createElement("canvas")
    canvas.width = src.width * PIXEL
    canvas.height = src.height * PIXEL
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return src
    }
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, canvas.width, canvas.height)
    return canvas
}

function paintClockFace(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, face: string, rim: string, hands: string) {
    fillDisk(ctx, cx, cy, r, rim)
    fillDisk(ctx, cx, cy, r - 2, face)
    px(ctx, hands, cx, cy - (r - 4), 1, r - 3)
    px(ctx, hands, cx, cy, r - 4, 1)
    px(ctx, rim, cx, cy)
}

function paintLandmark(id: LandmarkId): HTMLCanvasElement {
    if (id === "eiffel") {
        const w = 57
        const h = 148
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const cx = 28
        const iron = "#3a3448"
        const dark = "#241e30"
        const mid = "#2e283c"
        px(ctx, "#6a5c40", cx, 0, 1, 8)
        px(ctx, iron, cx - 2, 8, 5, 5)
        px(ctx, WIN, cx, 9)
        const taper = (y0: number, y1: number, half0: number, half1: number) => {
            for (let y = y0; y < y1; y++) {
                const t = (y - y0) / Math.max(1, y1 - y0)
                const half = half0 + Math.floor((half1 - half0) * t)
                const c = y % 2 === 0 ? iron : dark
                px(ctx, c, cx - half, y, 2, 1)
                px(ctx, c, cx + half - 1, y, 2, 1)
                if ((y - y0) % 5 === 0) {
                    px(ctx, mid, cx - half, y, half * 2 + 1, 1)
                } else if ((y - y0) % 5 === 2) {
                    px(ctx, dark, cx - Math.floor(half * 0.45), y, 1, 1)
                    px(ctx, dark, cx + Math.floor(half * 0.45), y, 1, 1)
                }
            }
        }
        taper(11, 52, 2, 7)
        px(ctx, iron, cx - 9, 52, 19, 4)
        px(ctx, dark, cx + 8, 52, 2, 4)
        px(ctx, WIN, cx - 6, 53, 2, 2)
        px(ctx, WIN, cx + 5, 53, 2, 2)
        taper(56, 90, 7, 13)
        px(ctx, iron, 8, 90, 41, 5)
        px(ctx, dark, 47, 90, 2, 5)
        px(ctx, mid, 8, 90, 41, 1)
        px(ctx, WIN, 12, 91, 3, 2)
        px(ctx, WIN, 20, 91, 3, 2)
        px(ctx, WIN, 34, 91, 3, 2)
        px(ctx, WIN, 42, 91, 3, 2)
        const y0 = 95
        for (let y = y0; y < h; y++) {
            const t = (y - y0) / (h - 1 - y0)
            const leftOuter = 10 - Math.floor(t * 9)
            const leftInner = 17 - Math.floor(t * 8)
            const c = y % 2 === 0 ? iron : dark
            px(ctx, c, leftOuter, y, Math.max(3, leftInner - leftOuter), 1)
            px(ctx, c, w - 1 - leftInner, y, Math.max(3, leftInner - leftOuter), 1)
            if ((y - y0) % 6 === 0) {
                px(ctx, mid, leftOuter, y, 3, 1)
                px(ctx, mid, w - 4 - leftOuter, y, 3, 1)
            }
            const archT = t
            const hole = 5 + Math.floor(archT * archT * 16)
            if (y < y0 + 28) {
                px(ctx, iron, cx - hole, y, 2, 1)
                px(ctx, iron, cx + hole - 1, y, 2, 1)
            }
        }
        return canvas
    }

    if (id === "tokyoTower") {
        const w = 31
        const h = 138
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const cx = 15
        const orange = "#8a3428"
        const white = "#5a6278"
        const dark = "#4a2018"
        px(ctx, "#8a3030", cx, 0, 1, 5)
        px(ctx, white, cx, 5, 1, 6)
        px(ctx, orange, cx, 11, 1, 6)
        px(ctx, white, cx, 17, 1, 7)
        px(ctx, orange, cx - 1, 24, 3, 4)
        px(ctx, white, cx - 1, 28, 3, 3)
        const band = (y: number) => (Math.floor(y / 8) % 2 === 0 ? orange : white)
        for (let y = 31; y < 58; y++) {
            const t = (y - 31) / 27
            const half = 2 + Math.floor(t * 3)
            const c = band(y)
            px(ctx, c, cx - half, y, 2, 1)
            px(ctx, c, cx + half - 1, y, 2, 1)
            if ((y - 31) % 4 === 0) {
                px(ctx, c, cx - half, y, half * 2 + 1, 1)
            }
        }
        px(ctx, white, cx - 6, 58, 13, 5)
        px(ctx, "#3a4458", cx - 5, 59, 11, 3)
        for (let x = cx - 4; x < cx + 5; x += 2) {
            px(ctx, WIN, x, 60, 1, 2)
        }
        for (let y = 63; y < 92; y++) {
            const t = (y - 63) / 29
            const half = 5 + Math.floor(t * 4)
            px(ctx, orange, cx - half, y, 2, 1)
            px(ctx, orange, cx + half - 1, y, 2, 1)
            if ((y - 63) % 5 === 0) {
                px(ctx, orange, cx - half, y, half * 2 + 1, 1)
            }
        }
        const archTop = 98
        for (let y = 92; y < h; y++) {
            const t = (y - 92) / (h - 93)
            const spread = 8 + Math.floor(t * 6)
            px(ctx, orange, cx - spread - 1, y, 2, 1)
            px(ctx, orange, cx + spread, y, 2, 1)
            if ((y - 92) % 6 === 0 && y < archTop + 4) {
                px(ctx, orange, cx - spread, y, spread * 2 + 1, 1)
            }
            const u = Math.max(0, (y - archTop) / (h - archTop))
            const inner = Math.floor(2 + u * u * (spread - 2))
            if (y >= archTop && y < h - 2) {
                px(ctx, dark, cx - inner, y, 1, 1)
                px(ctx, dark, cx + inner, y, 1, 1)
            }
        }
        return canvas
    }

    if (id === "wtc") {
        const w = 25
        const h = 198
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const glass = "#3a5a78"
        const hi = "#4a6a88"
        const side = "#2a3a50"
        const edge = "#1a2434"
        const podium = "#3a3c44"
        const cx = 12
        px(ctx, "#5a6878", cx, 0, 1, 38)
        px(ctx, "#8a3038", cx, 3, 1, 2)
        px(ctx, edge, cx - 1, 36, 3, 4)
        for (let y = 40; y < 186; y++) {
            const t = (y - 40) / (185 - 40)
            const totalW = 5 + Math.floor(t * 16)
            const chamfer = Math.max(1, Math.floor(totalW * 0.22))
            const mainW = Math.max(2, totalW - chamfer * 2)
            const x0 = cx - Math.floor(totalW / 2)
            px(ctx, side, x0, y, chamfer, 1)
            px(ctx, glass, x0 + chamfer, y, mainW, 1)
            px(ctx, edge, x0 + chamfer + mainW, y, Math.max(1, totalW - chamfer - mainW), 1)
            px(ctx, hi, x0 + chamfer, y, 1, 1)
            if (y % 3 !== 1) {
                for (let x = x0 + chamfer + 2; x < x0 + chamfer + mainW - 1; x += 2) {
                    px(ctx, hi, x, y)
                }
            }
        }
        px(ctx, podium, 1, 186, 23, 12)
        px(ctx, edge, 22, 186, 2, 12)
        return canvas
    }

    if (id === "burj") {
        const w = 31
        const h = 224
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const silver = "#3a4a5c"
        const lit = "#4a5a70"
        const edge = "#1e2838"
        const cx = 15
        px(ctx, "#6a7888", cx, 0, 1, 36)
        px(ctx, WIN, cx, 4)
        px(ctx, silver, cx - 1, 36, 3, 10)
        let left = 13
        let right = 18
        let y = 46
        const terraces = 18
        for (let i = 0; i < terraces; i++) {
            const th = i < 4 ? 6 : i < 10 ? 8 : 10
            const y1 = Math.min(h, y + th)
            px(ctx, silver, left, y, right - left, y1 - y)
            px(ctx, edge, right - 1, y, 1, y1 - y)
            px(ctx, lit, left, y, 1, y1 - y)
            px(ctx, "#2e3a4c", left, y, right - left, 1)
            for (let wy = y + 2; wy < y1 - 1; wy += 2) {
                for (let wx = left + 2; wx < right - 2; wx += 2) {
                    px(ctx, WIN, wx, wy)
                }
            }
            if (i % 2 === 0) {
                left = Math.max(0, left - 1)
            } else {
                right = Math.min(w, right + 1)
            }
            y = y1
            if (y >= h) {
                break
            }
        }
        if (y < h) {
            px(ctx, silver, 0, y, w, h - y)
            px(ctx, edge, w - 1, y, 1, h - y)
        }
        return canvas
    }

    if (id === "petronas") {
        const w = 43
        const h = 168
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const glass = "#2a4058"
        const steel = "#4a6078"
        const edge = "#1a2838"
        const tower = (x0: number) => {
            const mid = x0 + 6
            px(ctx, steel, mid, 0, 1, 16)
            px(ctx, WIN, mid, 4)
            let y = 14
            const rings = [1, 3, 5, 7, 9, 11]
            for (const rw of rings) {
                const rx = mid - Math.floor(rw / 2)
                px(ctx, steel, rx, y, rw, 3)
                px(ctx, edge, rx + rw - 1, y, 1, 3)
                if (rw > 2) {
                    px(ctx, glass, rx + 1, y + 1, rw - 2, 1)
                }
                y += 3
            }
            px(ctx, glass, x0, y, 13, h - y)
            px(ctx, edge, x0 + 12, y, 1, h - y)
            px(ctx, steel, x0, y, 1, h - y)
            for (let wy = y + 1; wy < h; wy += 2) {
                px(ctx, WIN, x0 + 1, wy, 11, 1)
            }
        }
        tower(4)
        tower(26)
        const by = 88
        px(ctx, steel, 16, by, 11, 3)
        px(ctx, WIN, 17, by + 1, 9, 1)
        for (let i = 0; i < 12; i++) {
            px(ctx, steel, 19 - Math.floor(i * 0.35), by + 4 + i, 1, 1)
            px(ctx, steel, 23 + Math.floor(i * 0.35), by + 4 + i, 1, 1)
        }
        return canvas
    }

    if (id === "empire") {
        const w = 27
        const h = 150
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const stone = "#4a4654"
        const lit = "#5a5664"
        const dark = "#2a2834"
        const cx = 13
        px(ctx, "#5a6070", cx, 0, 1, 22)
        px(ctx, WIN, cx, 3)
        px(ctx, dark, cx - 1, 20, 3, 3)
        const crown = [
            { y0: 23, y1: 30, hw: 2 },
            { y0: 30, y1: 38, hw: 3 },
            { y0: 38, y1: 46, hw: 4 },
            { y0: 46, y1: 56, hw: 5 },
        ]
        for (const c of crown) {
            px(ctx, stone, cx - c.hw, c.y0, c.hw * 2 + 1, c.y1 - c.y0)
            px(ctx, dark, cx + c.hw, c.y0, 1, c.y1 - c.y0)
            px(ctx, dark, cx - c.hw, c.y0, c.hw * 2 + 1, 1)
            gridWins(ctx, cx - c.hw, c.y0 + 1, c.hw * 2 + 1, c.y1 - c.y0 - 2, WIN, 1, 1, 1, 2)
        }
        px(ctx, stone, cx - 6, 56, 13, 10)
        px(ctx, dark, cx + 5, 56, 2, 10)
        px(ctx, dark, cx - 6, 56, 13, 1)
        gridWins(ctx, cx - 6, 57, 13, 8, WIN, 1, 1, 1, 2)

        const shaftX = 6
        const shaftW = 15
        px(ctx, stone, shaftX, 64, shaftW, 64)
        px(ctx, dark, shaftX + shaftW - 2, 64, 2, 64)
        for (let x = shaftX + 1; x < shaftX + shaftW - 2; x += 2) {
            px(ctx, lit, x, 64, 1, 64)
            for (let y = 66; y < 126; y += 3) {
                px(ctx, WIN, x, y, 1, 1)
            }
        }

        px(ctx, stone, 4, 128, 19, 10)
        px(ctx, dark, 21, 128, 2, 10)
        px(ctx, dark, 4, 128, 19, 1)
        for (let x = 6; x < 21; x += 2) {
            px(ctx, lit, x, 129, 1, 8)
            for (let y = 130; y < 137; y += 3) {
                px(ctx, WIN, x, y)
            }
        }
        px(ctx, stone, 1, 138, 25, 12)
        px(ctx, dark, 24, 138, 2, 12)
        for (let x = 3; x < 24; x += 2) {
            px(ctx, lit, x, 139, 1, 10)
            for (let y = 140; y < 149; y += 3) {
                px(ctx, WIN, x, y)
            }
        }
        return canvas
    }

    if (id === "cn") {
        const w = 31
        const h = 192
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const cx = 15
        const conc = "#4a4658"
        const lit = "#5a5668"
        const shade = "#2e2c3a"
        const rib = "#3a3848"
        const pod = "#525064"
        const podDark = "#2a2838"
        const rust = "#7a3840"
        const mast = "#6a7084"

        px(ctx, "#8a3038", cx, 0, 1, 5)
        px(ctx, mast, cx, 5, 1, 4)
        px(ctx, "#8a3038", cx, 9, 1, 3)
        px(ctx, mast, cx, 12, 1, 28)
        px(ctx, mast, cx - 1, 36, 3, 2)

        px(ctx, podDark, cx - 4, 40, 9, 1)
        px(ctx, pod, cx - 5, 41, 11, 3)
        px(ctx, rust, cx - 5, 43, 11, 1)
        px(ctx, podDark, cx - 4, 44, 9, 1)
        px(ctx, WIN, cx - 3, 42, 1, 1)
        px(ctx, WIN, cx + 2, 42, 1, 1)

        px(ctx, conc, cx - 1, 45, 3, 16)
        px(ctx, shade, cx + 1, 45, 1, 16)

        const saucer = [
            { y: 61, x: 8, ww: 15, c: pod },
            { y: 62, x: 5, ww: 21, c: pod },
            { y: 63, x: 4, ww: 23, c: pod },
            { y: 64, x: 3, ww: 25, c: podDark },
            { y: 65, x: 3, ww: 25, c: podDark },
            { y: 66, x: 3, ww: 25, c: rust },
            { y: 67, x: 3, ww: 25, c: rust },
            { y: 68, x: 4, ww: 23, c: pod },
            { y: 69, x: 6, ww: 19, c: pod },
            { y: 70, x: 8, ww: 15, c: shade },
        ]
        for (const row of saucer) {
            px(ctx, row.c, row.x, row.y, row.ww, 1)
        }
        px(ctx, WIN, 7, 65, 2, 1)
        px(ctx, WIN, 12, 65, 2, 1)
        px(ctx, WIN, 17, 65, 2, 1)
        px(ctx, WIN, 22, 65, 2, 1)
        px(ctx, shade, 27, 64, 1, 4)

        for (let y = 71; y < h; y++) {
            const t = (y - 71) / (h - 72)
            const hw = 2 + Math.floor(t * 6)
            const x0 = cx - hw
            const bw = hw * 2 + 1
            px(ctx, conc, x0, y, bw, 1)
            px(ctx, lit, x0, y, Math.max(1, Math.floor(hw * 0.45)), 1)
            px(ctx, shade, cx + hw, y, 1, 1)
            px(ctx, rib, cx, y, 1, 1)
            if (t > 0.82) {
                const fl = 1 + Math.floor((t - 0.82) / 0.06)
                px(ctx, conc, x0 - fl, y, bw + fl * 2, 1)
                px(ctx, shade, cx + hw + fl, y, 1, 1)
            }
        }
        return canvas
    }

    if (id === "skytree") {
        const w = 27
        const h = 198
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const cx = 13
        const pale = "#5a6a80"
        const dark = "#2a3448"
        px(ctx, pale, cx, 0, 1, 32)
        for (let y = 2; y < 30; y += 4) {
            px(ctx, dark, cx, y, 1, 2)
        }
        const bulb = (cy: number, hw: number, hh: number) => {
            for (let y = -hh; y <= hh; y++) {
                const t = 1 - (y * y) / (hh * hh)
                const rowW = Math.max(2, Math.floor(hw * Math.sqrt(Math.max(0, t))))
                px(ctx, pale, cx - rowW, cy + y, 1, 1)
                px(ctx, pale, cx + rowW, cy + y, 1, 1)
                if (Math.abs(y) < hh - 1) {
                    px(ctx, dark, cx - rowW + 1, cy + y, Math.max(1, rowW * 2 - 1), 1)
                }
            }
        }
        bulb(38, 4, 5)
        px(ctx, pale, cx, 44, 1, 5)
        bulb(58, 6, 7)
        for (let x = cx - 3; x <= cx + 3; x += 2) {
            px(ctx, WIN, x, 57, 1, 2)
        }
        for (let y = 66; y < h; y++) {
            const t = (y - 66) / (h - 67)
            const half = 3 + Math.floor(t * 9)
            px(ctx, pale, cx - half, y, 1, 1)
            px(ctx, pale, cx + half, y, 1, 1)
            const cell = 12
            const phase = (y - 66) % cell
            if (phase === 0) {
                const gap = Math.max(1, Math.floor(half * 0.5))
                px(ctx, pale, cx - half + 1, y, half - gap, 1)
                px(ctx, pale, cx + gap, y, half - gap, 1)
            } else if (phase === 4 || phase === 8) {
                const u = phase === 4 ? 0.32 : 0.68
                px(ctx, pale, cx - half + Math.round(u * half * 2), y)
                px(ctx, pale, cx + half - Math.round(u * half * 2), y)
            }
        }
        return canvas
    }

    if (id === "twilight") {
        const w = 55
        const h = 150
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const cx = 27
        const roof = "#5a2418"
        const roofDk = "#3a140c"
        const cream = "#6a5c48"
        const creamLt = "#7a6c54"
        const creamDk = "#3e382c"
        const gold = "#8a7438"
        const bronze = "#6a4a28"
        const bronzeDk = "#3a2818"
        const face = "#2a2014"
        const roofTop = 10
        const roofBot = 36
        const topW = 15
        const botW = 31

        const arm = (dir: number) => {
            const sx = cx + dir * 8
            for (let i = 0; i < 10; i++) {
                px(ctx, bronze, sx + dir * i, 12 - Math.min(4, Math.floor(i * 0.6)), 1, 2)
            }
            const hx = cx + dir * 17
            for (let i = 0; i < 12; i++) {
                px(ctx, bronze, hx, 10 + i, 1, 1)
            }
            px(ctx, bronze, hx - 1, 21, 3, 2)
        }
        arm(-1)
        arm(1)
        const bell = (bx: number, by: number) => {
            px(ctx, bronze, bx, by, 1, 2)
            px(ctx, bronze, bx - 4, by + 2, 9, 3)
            px(ctx, bronzeDk, bx - 3, by + 5, 7, 3)
            px(ctx, bronze, bx - 2, by + 8, 5, 1)
            px(ctx, bronzeDk, bx, by + 9, 1, 2)
        }
        bell(cx - 17, 22)
        bell(cx + 17, 22)

        px(ctx, gold, cx - 1, 4, 3, 6)
        px(ctx, gold, cx - 6, 8, 1, 4)
        px(ctx, gold, cx + 6, 8, 1, 4)
        for (let y = roofTop; y <= roofBot; y++) {
            const t = (y - roofTop) / (roofBot - roofTop)
            const ww = topW + Math.floor((botW - topW) * t)
            const x = cx - Math.floor(ww / 2)
            px(ctx, y % 2 === 0 ? roof : roofDk, x, y, ww, 1)
            px(ctx, roofDk, x + ww - 1, y, 1, 1)
        }
        px(ctx, roofDk, cx - Math.floor(botW / 2), roofBot, botW, 2)
        paintClockFace(ctx, cx, 22, 4, face, gold, creamDk)

        px(ctx, cream, cx - 11, 38, 23, 6)
        px(ctx, creamDk, cx + 10, 38, 2, 6)
        px(ctx, gold, cx - 10, 38, 21, 1)
        for (let x = cx - 9; x < cx + 10; x += 3) {
            px(ctx, creamLt, x, 39, 1, 5)
        }

        for (let y = 44; y < 70; y++) {
            const t = y < 56 ? (56 - y) / 12 : (y - 56) / 14
            const extra = Math.max(0, 8 - Math.floor(t * 9))
            px(ctx, cream, cx - 10 - extra, y, 21 + extra * 2, 1)
            px(ctx, creamDk, cx + 10 + extra - 1, y, 2, 1)
            px(ctx, creamLt, cx - 10 - extra, y, 1, 1)
        }
        paintClockFace(ctx, cx, 56, 9, face, gold, creamDk)
        px(ctx, gold, cx - 2, 58, 2, 2)
        px(ctx, gold, cx + 3, 60, 3, 1)

        px(ctx, cream, cx - 10, 70, 21, h - 70)
        px(ctx, creamDk, cx + 9, 70, 2, h - 70)
        for (let x = cx - 8; x <= cx + 6; x += 3) {
            px(ctx, creamLt, x, 70, 1, h - 94)
            px(ctx, creamDk, x + 1, 70, 1, h - 94)
        }

        const turret = (x0: number) => {
            for (let i = 0; i < 7; i++) {
                px(ctx, roof, x0 + i, 94 + 7 - i, 15 - i * 2, 1)
            }
            px(ctx, cream, x0 + 1, 102, 13, h - 102)
            px(ctx, creamDk, x0 + 12, 102, 2, h - 102)
            px(ctx, gold, x0 + 2, 102, 11, 1)
            gridWins(ctx, x0 + 2, 106, 11, h - 112, WIN, 2, 4, 3, 4)
        }
        turret(1)
        turret(39)
        for (let i = 0; i < 22; i++) {
            const rise = Math.floor(Math.sin((i / 21) * Math.PI) * 7)
            px(ctx, gold, 16 + i, h - 28 - rise, 1, 2)
        }
        px(ctx, creamDk, 16, h - 26, 23, 10)
        px(ctx, roofDk, 16, h - 16, 23, 16)
        px(ctx, WIN, 25, h - 22, 5, 6)
        px(ctx, gold, 16, h - 16, 23, 1)
        return canvas
    }

    if (id === "bigben") {
        const w = 17
        const h = 70
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const stone = "#3e3a44"
        const dark = "#242028"
        const gold = "#8a7438"
        const cx = 8
        px(ctx, gold, cx, 0, 1, 7)
        px(ctx, gold, cx - 1, 6, 3, 2)
        px(ctx, stone, cx - 3, 8, 7, 7)
        px(ctx, dark, cx + 2, 8, 2, 7)
        px(ctx, gold, cx - 2, 8, 5, 1)
        px(ctx, stone, 3, 15, 11, h - 15)
        px(ctx, dark, 12, 15, 2, h - 15)
        px(ctx, dark, 3, 15, 1, h - 15)
        px(ctx, gold, 4, 15, 9, 1)
        paintClockFace(ctx, cx, 24, 4, "#5a5040", gold, "#1c1814")
        px(ctx, dark, 4, 30, 9, 1)
        gridWins(ctx, 3, 33, 11, h - 38, "#1c1814", 2, 3, 3, 3)
        px(ctx, stone, 2, 15, 1, 9)
        px(ctx, stone, 14, 15, 1, 9)
        return canvas
    }

    if (id === "pyramid") {
        const w = 78
        const h = 86
        const { canvas, ctx } = artCanvas(w, h)
        if (!ctx) {
            return canvas
        }
        const glass = "#3a5464"
        const glassLt = "#4a6878"
        const stone = "#4a403c"
        const brick = "#3a3230"
        const dark = "#241e1c"
        const cx = 39
        const peakH = 70
        for (let y = 0; y < peakH; y++) {
            const u = y / Math.max(1, peakH - 1)
            const half = Math.max(1, Math.floor((w / 2 - 1) * u))
            const c = y < 16 ? (y % 2 === 0 ? glass : glassLt) : (y % 3 === 0 ? brick : stone)
            px(ctx, c, cx - half, y, half * 2 + 1, 1)
            px(ctx, dark, cx + half, y, 1, 1)
            if (y > 18 && y % 5 === 0) {
                px(ctx, dark, cx - half + 2, y, Math.max(1, half * 2 - 3), 1)
            }
            if (y > 22 && y % 5 === 2) {
                for (let x = cx - half + 3; x < cx + half - 2; x += 4) {
                    px(ctx, WIN, x, y - 1, 1, 1)
                }
            }
        }
        px(ctx, "#2a4858", cx - 1, 0, 3, 3)
        px(ctx, WIN, cx, 1)
        px(ctx, brick, 8, peakH, w - 16, h - peakH)
        px(ctx, dark, w - 10, peakH, 4, h - peakH)
        px(ctx, "#2a2420", 18, peakH + 4, 10, h - peakH - 4)
        px(ctx, WIN, 21, peakH + 6, 2, 2)
        px(ctx, WIN, 24, peakH + 6, 2, 2)
        return canvas
    }

    return artCanvas(1, 1).canvas
}


function drawBoat(ctx: CanvasRenderingContext2D, boat: Boat, x: number, waterline: number, t: number) {
    const y = (waterline + boat.y) * PIXEL
    const w = boat.w
    const dir = boat.speed >= 0 ? 1 : -1
    if (boat.kind !== "houseboat" && boat.kind !== "dockship" && Math.abs(boat.speed) > 0.2) {
        const len = boat.kind === "cruise" || boat.kind === "cargo" || boat.kind === "ferry" ? 8 : 5
        for (let i = 1; i <= len; i++) {
            if (Math.sin(t * 5 + boat.x + i * 0.8) < -0.35) {
                continue
            }
            ctx.fillStyle = `rgba(180, 200, 230, ${0.28 - i * 0.028})`
            ctx.fillRect(x - dir * i * 2 * PIXEL, y + PIXEL, Math.max(PIXEL, (3 - Math.floor(i / 3)) * PIXEL), PIXEL)
        }
    }
    if (boat.kind === "fireboat") {
        ctx.fillStyle = "#1a1420"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#8a3038"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, (w - 2) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#c85050"
        ctx.fillRect(x + 3 * PIXEL, y - 5 * PIXEL, 3 * PIXEL, 2 * PIXEL)
        const nose = dir > 0 ? w - 2 : 2
        for (let i = 0; i < 5; i++) {
            const on = Math.sin(t * 10 + boat.x + i) > -0.2
            if (!on) {
                continue
            }
            ctx.fillStyle = `rgba(160, 200, 230, ${0.35 + i * 0.08})`
            ctx.fillRect(x + (nose + dir * i) * PIXEL, y - (4 + i) * PIXEL, PIXEL, PIXEL)
        }
        return
    }
    if (boat.kind === "police") {
        ctx.fillStyle = "#141428"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#2a3048"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, (w - 2) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = Math.floor(t * 10) % 2 === 0 ? "#ff3a4a" : "#3a7aff"
        ctx.fillRect(x + 3 * PIXEL, y - 4 * PIXEL, PIXEL, PIXEL)
        ctx.fillRect(x + 5 * PIXEL, y - 4 * PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "ferry") {
        ctx.fillStyle = "#141428"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(x + PIXEL, y - 4 * PIXEL, (w - 2) * PIXEL, 4 * PIXEL)
        ctx.fillStyle = "#4a5468"
        ctx.fillRect(x + 3 * PIXEL, y - 6 * PIXEL, Math.max(6, w - 8) * PIXEL, 2 * PIXEL)
        for (let i = 3; i < w - 3; i += 2) {
            ctx.fillStyle = Math.sin(t * 2 + boat.x + i) > -0.4 ? "#f5d84a" : "#8a7430"
            ctx.fillRect(x + i * PIXEL, y - 3 * PIXEL, PIXEL, PIXEL)
        }
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(x + (dir > 0 ? w - 2 : 1) * PIXEL, y - PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "cruise") {
        ctx.fillStyle = "#101018"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#2a3448"
        ctx.fillRect(x + PIXEL, y - 4 * PIXEL, (w - 2) * PIXEL, 4 * PIXEL)
        ctx.fillStyle = "#3a4458"
        ctx.fillRect(x + 4 * PIXEL, y - 7 * PIXEL, Math.max(10, w - 12) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#4a5468"
        ctx.fillRect(x + 8 * PIXEL, y - 9 * PIXEL, Math.max(6, w - 18) * PIXEL, 2 * PIXEL)
        for (let row = 0; row < 3; row++) {
            for (let i = 3; i < w - 4; i += 2) {
                const on = Math.sin(t * 1.4 + boat.x + i * 0.4 + row) > -0.15
                ctx.fillStyle = on ? "#f5d84a" : "#6a5a30"
                ctx.fillRect(x + i * PIXEL, y - (2 + row * 2) * PIXEL, PIXEL, PIXEL)
            }
        }
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(x + (dir > 0 ? w - 2 : 1) * PIXEL, y - PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "sailboat") {
        ctx.fillStyle = "#141428"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        const mast = dir > 0 ? Math.floor(w * 0.4) : Math.floor(w * 0.55)
        ctx.fillStyle = "#c8c4b0"
        ctx.fillRect(x + mast * PIXEL, y - 9 * PIXEL, PIXEL, 9 * PIXEL)
        ctx.fillStyle = "#d8d0b8"
        const sailW = Math.max(3, w - 5)
        if (dir > 0) {
            ctx.fillRect(x + (mast + 1) * PIXEL, y - 8 * PIXEL, sailW * PIXEL, 5 * PIXEL)
        } else {
            ctx.fillRect(x + (mast - sailW) * PIXEL, y - 8 * PIXEL, sailW * PIXEL, 5 * PIXEL)
        }
        ctx.fillStyle = Math.sin(t * 4 + boat.x) > 0 ? "#ffe8a0" : "#8a7430"
        ctx.fillRect(x + mast * PIXEL, y - 10 * PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "houseboat") {
        ctx.fillStyle = "#181828"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#3a3048"
        ctx.fillRect(x + PIXEL, y - 4 * PIXEL, (w - 2) * PIXEL, 4 * PIXEL)
        ctx.fillStyle = "#4a4058"
        ctx.fillRect(x + 2 * PIXEL, y - 6 * PIXEL, Math.max(4, w - 5) * PIXEL, 2 * PIXEL)
        for (let i = 2; i < w - 2; i += 2) {
            ctx.fillStyle = Math.sin(t * 1.1 + boat.x + i) > -0.2 ? "#f5d84a" : "#6a5828"
            ctx.fillRect(x + i * PIXEL, y - 3 * PIXEL, PIXEL, PIXEL)
        }
        return
    }
    if (boat.kind === "tug") {
        ctx.fillStyle = "#1a1828"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#5a4050"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, Math.max(3, w - 3) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#2a2430"
        ctx.fillRect(x + 2 * PIXEL, y - 6 * PIXEL, 2 * PIXEL, 3 * PIXEL)
        ctx.fillStyle = Math.sin(t * 8 + boat.x) > 0.2 ? "#f5d84a" : "#8a7030"
        ctx.fillRect(x + (w - 2) * PIXEL, y - PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "yacht") {
        ctx.fillStyle = "#1c2238"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#a8b0c8"
        ctx.fillRect(x + 2 * PIXEL, y - 2 * PIXEL, Math.max(4, w - 4) * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#d8dce8"
        ctx.fillRect(x + 4 * PIXEL, y - 4 * PIXEL, Math.max(2, w - 8) * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(x + (dir > 0 ? w - 1 : 0) * PIXEL, y - PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "cargo") {
        ctx.fillStyle = "#141428"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#3a3048"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, (w - 2) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#4a3850"
        ctx.fillRect(x + 3 * PIXEL, y - 6 * PIXEL, Math.max(4, w - 8) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#2a2838"
        ctx.fillRect(x + 5 * PIXEL, y - 8 * PIXEL, 2 * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#c87848"
        ctx.fillRect(x + PIXEL, y - PIXEL, 2 * PIXEL, PIXEL)
        return
    }
    if (boat.kind === "dockship") {
        ctx.fillStyle = "#101018"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#2a2438"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, (w - 2) * PIXEL, 3 * PIXEL)
        const crate = Math.floor((Math.sin(t * 0.7 + boat.x) + 1) * 0.5 * Math.max(4, w - 10))
        const lift = Math.floor((1 - Math.abs(Math.sin(t * 0.7 + boat.x))) * 5)
        ctx.fillStyle = "#c87848"
        ctx.fillRect(x + 2 * PIXEL, y - 5 * PIXEL, 3 * PIXEL, 2 * PIXEL)
        ctx.fillRect(x + 6 * PIXEL, y - 5 * PIXEL, 3 * PIXEL, 2 * PIXEL)
        ctx.fillRect(x + 10 * PIXEL, y - 7 * PIXEL, 3 * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#e0a060"
        ctx.fillRect(x + Math.floor(w * 0.55) * PIXEL, y - 12 * PIXEL, PIXEL, 12 * PIXEL)
        ctx.fillRect(x + Math.floor(w * 0.55) * PIXEL, y - 12 * PIXEL, Math.max(6, crate) * PIXEL, PIXEL)
        ctx.fillStyle = "#d89858"
        ctx.fillRect(x + (Math.floor(w * 0.55) + crate) * PIXEL, y - (6 + lift) * PIXEL, 2 * PIXEL, 2 * PIXEL)
        return
    }
    if (boat.kind === "steamer") {
        ctx.fillStyle = "#181422"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#3a2c38"
        ctx.fillRect(x + PIXEL, y - 3 * PIXEL, (w - 2) * PIXEL, 3 * PIXEL)
        ctx.fillStyle = "#2a2430"
        ctx.fillRect(x + 3 * PIXEL, y - 6 * PIXEL, 3 * PIXEL, 3 * PIXEL)
        ctx.fillRect(x + 8 * PIXEL, y - 5 * PIXEL, 2 * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#c9a43a"
        ctx.fillRect(x + 4 * PIXEL, y - 8 * PIXEL, PIXEL, 2 * PIXEL)
        ctx.fillStyle = "rgba(200, 204, 220, 0.5)"
        const puff = Math.floor((t * 2 + boat.x) % 3)
        ctx.fillRect(x + 4 * PIXEL + puff * PIXEL, y - (9 + puff) * PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "lantern") {
        ctx.fillStyle = "#1a1828"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#4a3040"
        ctx.fillRect(x + PIXEL, y - 2 * PIXEL, (w - 2) * PIXEL, 2 * PIXEL)
        const glow = 0.45 + 0.35 * Math.sin(t * 3 + boat.x)
        ctx.fillStyle = `rgba(255, 180, 80, ${glow})`
        ctx.fillRect(x + 2 * PIXEL, y - 4 * PIXEL, PIXEL, PIXEL)
        ctx.fillRect(x + 5 * PIXEL, y - 4 * PIXEL, PIXEL, PIXEL)
        ctx.fillRect(x + (w - 3) * PIXEL, y - 4 * PIXEL, PIXEL, PIXEL)
        return
    }
    if (boat.kind === "skiff") {
        ctx.fillStyle = "#181828"
        ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
        ctx.fillStyle = "#c8c4b0"
        ctx.fillRect(x + Math.floor(w / 2) * PIXEL, y - 7 * PIXEL, PIXEL, 7 * PIXEL)
        ctx.fillStyle = "#e8e0cc"
        const sailW = Math.max(3, w - Math.floor(w / 2) - 1)
        ctx.fillRect(x + (Math.floor(w / 2) + 1) * PIXEL, y - 6 * PIXEL, sailW * PIXEL, 4 * PIXEL)
        ctx.fillStyle = "#f5d84a"
        ctx.fillRect(x + Math.floor(w / 2) * PIXEL, y - 8 * PIXEL, PIXEL, PIXEL)
        return
    }
    ctx.fillStyle = "#141428"
    ctx.fillRect(x, y, w * PIXEL, 2 * PIXEL)
    ctx.fillStyle = "#c87848"
    ctx.fillRect(x + 2 * PIXEL, y - 3 * PIXEL, Math.max(3, w - 4) * PIXEL, 3 * PIXEL)
    ctx.fillStyle = "#e8a060"
    ctx.fillRect(x + 3 * PIXEL, y - 4 * PIXEL, Math.max(2, w - 6) * PIXEL, PIXEL)
    ctx.fillStyle = "#f5d84a"
    ctx.fillRect(x + (w - 2) * PIXEL, y - PIXEL, PIXEL, PIXEL)
}

const GZ_LAYER = 4
const GZ_INK = "#07080d"
const GZ_GLOW = "#2a1010"

function rareWait(meanSec: number) {
    return meanSec * (0.6 + Math.random() * 0.8)
}

function grayBuildingSprite(src: HTMLCanvasElement) {
    const canvas = document.createElement("canvas")
    canvas.width = src.width
    canvas.height = src.height
    const g = canvas.getContext("2d")
    if (!g) {
        return src
    }
    g.imageSmoothingEnabled = false
    g.drawImage(src, 0, 0)
    g.globalCompositeOperation = "source-atop"
    g.fillStyle = "rgba(62, 66, 74, 0.58)"
    g.fillRect(0, 0, canvas.width, canvas.height)
    g.globalCompositeOperation = "source-over"
    return canvas
}

function plotKaijuPix(
    ctx: CanvasRenderingContext2D,
    feetX: number,
    feetY: number,
    facing: number,
    lx: number,
    ly: number,
    riseH: number,
    lean: number,
    color: string,
) {
    if (ly < 0.2 || ly > riseH) {
        return
    }
    const s = PIXEL
    const x = snapP(feetX + (facing * lx + lean * ly * 0.04) * s)
    const y = snapP(feetY - ly * s)
    ctx.fillStyle = color
    ctx.fillRect(x, y, s, s)
}

function blobKaiju(
    ctx: CanvasRenderingContext2D,
    feetX: number,
    feetY: number,
    facing: number,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    riseH: number,
    lean: number,
    color: string,
) {
    const rxn = Math.ceil(rx)
    const ryn = Math.ceil(ry)
    for (let y = -ryn; y <= ryn; y++) {
        for (let x = -rxn; x <= rxn; x++) {
            const u = (x / Math.max(0.4, rx))
            const v = (y / Math.max(0.4, ry))
            if (u * u + v * v <= 1.05) {
                plotKaijuPix(ctx, feetX, feetY, facing, cx + x, cy + y, riseH, lean, color)
            }
        }
    }
}

function drawGodzillaSilhouette(
    ctx: CanvasRenderingContext2D,
    feetX: number,
    feetY: number,
    facing: number,
    gait: number,
    riseH: number,
    lean: number,
    jaw: number,
    dying: number,
    headAng: number,
    melee: number,
) {
    ctx.imageSmoothingEnabled = false
    const walking = melee <= 0 && dying <= 0
    const walk = walking ? Math.sin(gait) : 0
    const walkB = walking ? Math.sin(gait + Math.PI) : 0
    const breath = Math.sin(gait * 0.35) * 0.7
    const bob = dying > 0 || melee > 0 ? 0 : Math.abs(Math.sin(gait)) * 0.9
    const hipY = 15 + bob
    const windup = melee > 0 && melee < 0.38 ? melee / 0.38 : 0
    const slam = melee >= 0.38 && melee < 0.62 ? (melee - 0.38) / 0.24 : melee >= 0.62 ? 1 : 0
    const recover = melee >= 0.62 ? (melee - 0.62) / 0.38 : 0
    const meleeLean = windup * -3 + slam * 5 - recover * 1.5
    const ink = GZ_INK

    for (let i = 0; i < 14; i++) {
        const u = i / 13
        const tx = -6 - i * 1.55
        const ty = 8 + u * 10 + Math.sin(gait * 0.55 + i * 0.45) * (1.6 + u * 3.2) * (0.55 + 0.45 * (1 - dying))
        const r = 4.2 - u * 3.2
        blobKaiju(ctx, feetX, feetY, facing, tx, ty, r, r * 0.85, riseH, lean, ink)
    }

    const plant = (phase: number, ox: number) => {
        const swing = walking ? Math.sin(gait + phase) * 6.2 : 0
        const lift = walking ? Math.max(0, Math.sin(gait + phase)) * 3.6 : 0
        const footX = ox + swing
        const footY = 1 + lift
        const kneeX = ox + swing * 0.45 + 2.2
        const kneeY = 8 + lift * 0.35
        blobKaiju(ctx, feetX, feetY, facing, kneeX, kneeY, 3.6, 4.2, riseH, lean, ink)
        blobKaiju(ctx, feetX, feetY, facing, (footX + kneeX) * 0.5, (footY + kneeY) * 0.5, 2.8, 3.4, riseH, lean, ink)
        blobKaiju(ctx, feetX, feetY, facing, footX, footY + 1, 4.2, 1.6, riseH, lean, ink)
        blobKaiju(ctx, feetX, feetY, facing, footX + 2.4, footY + 0.4, 2.2, 1.1, riseH, lean, ink)
    }
    plant(0, 1)
    plant(Math.PI, 6)

    blobKaiju(ctx, feetX, feetY, facing, 4, hipY, 8.5, 7.5, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 7, hipY + 10 + breath, 9.5, 9.2, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 8, hipY + 18 + breath, 8.2, 7.4, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 10, hipY + 15 + breath, 6.2, 5.6, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 11, hipY + 16 + breath, 5.8, 6.2, riseH, lean, "#000000")

    for (let i = 0; i < 7; i++) {
        const sx = 2 + i * 1.6
        const sy = hipY + 12 + i * 2.1 + breath
        const sh = 4 + (i % 3) + Math.sin(gait * 1.2 + i) * 0.4
        blobKaiju(ctx, feetX, feetY, facing, sx - 1, sy + sh * 0.35, 1.3, sh * 0.55, riseH, lean, ink)
        plotKaijuPix(ctx, feetX, feetY, facing, sx - 1, sy + sh, riseH, lean, ink)
    }

    let armSwing = walk * 3.2
    if (melee > 0) {
        if (windup > 0) {
            armSwing = -8 - windup * 6
        } else if (slam < 1 || recover > 0) {
            const u = slam < 1 ? slam : 1 - recover * 0.7
            armSwing = -14 + u * 28
        }
    }
    const armDrop = slam * 10 - recover * 4
    blobKaiju(ctx, feetX, feetY, facing, 12 + armSwing * 0.15, hipY + 14 + meleeLean, 2.8, 2.6, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 15 + armSwing, hipY + 10 - armDrop * 0.4, 2.6, 3.2, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 18 + armSwing * 1.15, hipY + 7 - armDrop, 3.2, 2.2, riseH, lean, ink)
    blobKaiju(ctx, feetX, feetY, facing, 5 - walkB * 0.8, hipY + 13, 2.4, 2.4, riseH, lean, ink)

    const pose = godzillaHeadPose(gait, jaw, dying, headAng)
    const ca = pose.ca
    const sa = pose.sa
    blobKaiju(ctx, feetX, feetY, facing, pose.nx, pose.ny, 4.6, 5.0, riseH, lean, ink)
    for (let i = 1; i <= 5; i++) {
        const u = i / 5
        const bx = pose.nx + (3 + u * 11) * ca
        const by = pose.ny + (3 + u * 11) * sa
        blobKaiju(ctx, feetX, feetY, facing, bx, by, 4.4 - u * 2.2, 3.4 - u * 1.4, riseH, lean, ink)
    }
    const jawDrop = 1.2 + jaw * 2.6
    blobKaiju(
        ctx, feetX, feetY, facing,
        pose.lx + sa * jawDrop,
        pose.ly - ca * jawDrop,
        3.6, 1.4 + jaw * 1.2,
        riseH, lean, ink,
    )
    if (jaw > 0.35) {
        plotKaijuPix(ctx, feetX, feetY, facing, pose.lx, pose.ly, riseH, lean, GZ_GLOW)
        plotKaijuPix(ctx, feetX, feetY, facing, pose.lx + ca, pose.ly + sa, riseH, lean, "#5a2020")
    }
}

function godzillaHeadPose(gait: number, jaw: number, dying: number, headAng: number) {
    const bob = dying > 0 ? 0 : Math.abs(Math.sin(gait)) * 0.9
    const hipY = 15 + bob
    const nx = 14
    const ny = hipY + 22
    const ca = Math.cos(headAng)
    const sa = Math.sin(headAng)
    const len = 14
    return {
        nx,
        ny,
        ca,
        sa,
        lx: nx + len * ca + sa * jaw * 0.2,
        ly: ny + len * sa - ca * jaw * 0.2,
    }
}

function godzillaMouthLocal(gait: number, jaw: number, dying: number, headAng: number) {
    const pose = godzillaHeadPose(gait, jaw, dying, headAng)
    return { lx: pose.lx, ly: pose.ly }
}

function kaijuScreenPos(feetX: number, feetY: number, facing: number, lean: number, lx: number, ly: number) {
    return {
        x: feetX + (facing * lx + lean * ly * 0.04) * PIXEL,
        y: feetY - ly * PIXEL,
    }
}

export default function CityBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) {
            return
        }
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return
        }

        const loadSeed = (Math.random() * 0x7fffffff) | 0
        const plazaSpans = makeOccasionalSpans(409 + loadSeed, 3, 4, 42, 82)
        const factorySpans = makeOccasionalSpans(401 + loadSeed, 1, 1, 64, 100)
        const trainViewGaps = makeOccasionalSpans(419 + loadSeed, 4, 6, 16, 32)
        const layerBuildings = LAYER_DEFS.map((def, i) => (
            makeLayer(
                def.seed + loadSeed + i * 9973,
                def.minH,
                def.maxH,
                def.colors,
                def.density,
                def.landmarks,
                def.gapChance,
                def.maxGap,
                def.detail,
                def.plazas,
                i >= 5 ? plazaSpans : undefined,
                i >= 4 && i <= 6 ? factorySpans : undefined,
                i >= 2 && i <= 4 ? trainViewGaps : undefined,
                i === LAYER_DEFS.length - 1 ? ["industrial", "pagoda", "market", "arena", "arts", "cathedral", "amusement", "neon", "lighthouse", "civic", "campus", "utilities", "station", "harborvillage", "castle", "cinema", "floatmarket", "aquarium", "greenhouse", "wharf"] : [],
            )
        ))
        {
            const skip = new Set<BuildingKind>(["ferris", "coaster", "lighthouse", "pagoda", "cathedral", "arena", "artscenter", "castle", "marquee", "harborcrane", "station", "powerplant"])
            const hasCrane = layerBuildings.some(bs => bs.some(b => b.kind === "construction" || b.roofProp === "crane"))
            if (!hasCrane) {
                for (const i of [6, 5, 7, 4]) {
                    const cand = layerBuildings[i]?.find(b => b.w >= 10 && b.h >= 10 && !skip.has(b.kind))
                    if (!cand) {
                        continue
                    }
                    cand.kind = "construction"
                    cand.roofProp = "none"
                    cand.windows = []
                    cand.sprite = paintBuilding(cand)
                    break
                }
            }
        }
        const layers: CityLayer[] = layerBuildings.map((buildings, i) => ({
            speed: LAYER_DEFS[i].speed,
            strip: bakeBuildingLayer(buildings),
        }))
        const paradeLayer = layers.length - 1
        const paradeSpeed = LAYER_DEFS[paradeLayer].speed
        const paradeStreet = (() => {
            const sorted = layerBuildings[paradeLayer].slice().sort((a, b) => a.x - b.x)
            let best = { start: 88, end: 158 }
            let bestW = 0
            if (sorted.length >= 2) {
                for (let i = 0; i < sorted.length - 1; i++) {
                    const start = sorted[i].x + sorted[i].w
                    const end = sorted[i + 1].x
                    const gap = end - start
                    if (gap > bestW) {
                        bestW = gap
                        best = { start, end }
                    }
                }
                const last = sorted[sorted.length - 1]
                const wrapEnd = sorted[0].x + WORLD
                const wrapStart = last.x + last.w
                const wrap = wrapEnd - wrapStart
                if (wrap > bestW) {
                    bestW = wrap
                    best = { start: wrapStart, end: wrapStart + wrap }
                }
            }
            if (bestW >= 28) {
                return { start: best.start, end: best.start + bestW }
            }
            const plaza = [...plazaSpans].sort((a, b) => (b.end - b.start) - (a.end - a.start))[0]
            return plaza || { start: 88, end: 158 }
        })()
        const paradeLanterns: { x: number; yOff: number; color: string; phase: number }[] = []
        const paradeStalls: { x: number; w: number; color: string }[] = []
        const paradeWalkers: { x: number; speed: number; color: string }[] = []
        {
            const rng = mulberry32(811 + loadSeed)
            const lanternCols = ["#ffb060", "#ff6b8a", "#f5d84a", "#ff9a4a", "#c8a0ff"]
            for (let px = paradeStreet.start + 3; px < paradeStreet.end - 2; px += 4 + Math.floor(rng() * 2)) {
                paradeLanterns.push({
                    x: px,
                    yOff: 10 + Math.floor(rng() * 2),
                    color: pick(rng, lanternCols),
                    phase: rng() * Math.PI * 2,
                })
            }
            for (let px = paradeStreet.start + 5; px < paradeStreet.end - 8; px += 12 + Math.floor(rng() * 6)) {
                paradeStalls.push({
                    x: px,
                    w: 4 + Math.floor(rng() * 3),
                    color: pick(rng, ["#6a4050", "#5a5040", "#4a4868", "#684848", "#3a5458"]),
                })
            }
            const walkerCols = ["#e8c84a", "#d8e8ff", "#ff9a4a", "#c8a0ff", "#88e0a0"]
            for (let i = 0; i < 6; i++) {
                paradeWalkers.push({
                    x: rng() * Math.max(8, paradeStreet.end - paradeStreet.start),
                    speed: (rng() > 0.5 ? 1 : -1) * (1.6 + rng() * 2.2),
                    color: pick(rng, walkerCols),
                })
            }
        }
        const stations = layerBuildings.flatMap((bs, i) => bs.filter(b => b.kind === "station").map(b => ({ x: b.x, w: b.w, layer: i })))
        const farMountains = bakeMountains(101, "#24344c", "#2e425c", "#1a283c", "#c8d2e4", 34, 62)
        const nearMountains = bakeMountains(113, "#1c2a40", "#26364c", "#141e30", "#b8c4d8", 28, 50)
        const skiLights = makeSkiSlope(113, 28, 50)
        const horizonGlowX = 640
        const cloudStrip = bakeClouds(makeClouds(73))
        const shoreStrip = bakeShoreline(211)
        const farTracks = bakeTracks(8, "#6a7080", "#2a3040")
        const nearTracks = bakeTracks(11, "#7a8090", "#1e2430")
        const farTrains = [
            { sprite: bakeTrain("metro", 3, "#2a2230", "#3a3048"), x: 40, speed: 28, cooldown: 0, rare: false },
            { sprite: bakeTrain("freight", 5, "#241c22", "#3a2a28"), x: 390, speed: 18, cooldown: 0, rare: false },
        ]
        const nearTrains = [
            { sprite: bakeTrain("metro", 4, "#221c28", "#342838"), x: 510, speed: 40, cooldown: 0, rare: false },
            { sprite: bakeTrain("shinkansen", 7, "#7a8498", "#243a68"), x: 90, speed: 76, cooldown: 0, rare: true },
        ]
        const smokes: StackSmoke[] = []
        {
            const rng = mulberry32(331)
            for (let i = 4; i < layerBuildings.length; i++) {
                for (const b of layerBuildings[i]) {
                    if (smokes.length >= 22) {
                        break
                    }
                    if (b.kind === "factory") {
                        const cx = 1 + Math.floor(b.w * 0.18)
                        const cx2 = Math.max(cx + 5, b.w - 5)
                        smokes.push({
                            layer: i,
                            x: b.x + cx,
                            yOff: b.h + 14,
                            phase: rng() * Math.PI * 2,
                            rate: 0.28 + rng() * 0.16,
                        })
                        if (rng() > 0.35 && smokes.length < 22) {
                            smokes.push({
                                layer: i,
                                x: b.x + cx2,
                                yOff: b.h + 11,
                                phase: rng() * Math.PI * 2,
                                rate: 0.24 + rng() * 0.14,
                            })
                        }
                    } else if (b.kind === "stack" && rng() > 0.35) {
                        smokes.push({
                            layer: i,
                            x: b.x + Math.floor(b.w / 2),
                            yOff: b.h + 5,
                            phase: rng() * Math.PI * 2,
                            rate: 0.26 + rng() * 0.12,
                        })
                    } else if (b.kind === "powerplant") {
                        smokes.push({
                            layer: i,
                            x: b.x + b.w - 4,
                            yOff: b.h + 6,
                            phase: rng() * Math.PI * 2,
                            rate: 0.22 + rng() * 0.12,
                        })
                    }
                }
            }
        }
        const farCars = makeCars(131, 22, 18, 21, 8, 16)
        const nearCars = makeCars(149, 28, 2, 3, 12, 22, 1, 1)
        const tramSprite = bakeTram()
        const trams = [
            { x: 70, speed: 9 },
            { x: 380, speed: -7.5 },
        ]
        const boats = makeBoats(91)
        {
            const front = layerBuildings[layerBuildings.length - 1] || []
            const dock = front.find(b => b.kind === "harborcrane") || front.find(b => b.kind === "warehouse") || front.find(b => b.kind === "factory")
            boats.push({
                x: dock ? dock.x : 240,
                y: 3,
                w: 26,
                speed: 0,
                kind: "dockship",
            })
        }
        const houseboats = makeHouseboats(97)
        const planes = makePlanes(163)
        const crafts = makeCrafts(177)
        const raining = Math.random() < 0.25
        const moonPhase = Math.floor(Math.random() * 8)
        const tideShift = Math.random() < 0.5 ? -1 : 1
        const fishJumps: { x: number; y: number; life: number; max: number; span: number }[] = []
        let nextFish = 2.5 + Math.random() * 6
        const floatStalls: { x: number; y: number; phase: number; color: string }[] = []
        {
            const rng = mulberry32(419)
            const base = 80 + Math.floor(rng() * (WORLD - 160))
            const colors = ["#6a4050", "#5a5040", "#4a4868", "#684848", "#3a5458"]
            for (let i = 0; i < 6; i++) {
                floatStalls.push({
                    x: base + i * (7 + Math.floor(rng() * 4)),
                    y: 6 + Math.floor(rng() * 5),
                    phase: rng() * Math.PI * 2,
                    color: pick(rng, colors),
                })
            }
        }
        const rainDrops: { x: number; y: number; spd: number; len: number }[] = []
        if (raining) {
            for (let i = 0; i < 120; i++) {
                rainDrops.push({
                    x: Math.random() * 4000,
                    y: Math.random() * 2000,
                    spd: 420 + Math.random() * 280,
                    len: 2 + Math.floor(Math.random() * 3),
                })
            }
        }
        const bridgeSprite = bakePixelSprite(bakeBridge())
        const bridgeX = 210
        const oilRigSprite = bakePixelSprite(bakeOilRig())
        const oilRigs: { x: number; y: number }[] = [
            { x: 90 + Math.floor(Math.random() * 160), y: 16 + Math.floor(Math.random() * 12) },
        ]
        const windFarms: { x: number; y: number; n: number }[] = []
        if (Math.random() < 0.18) {
            windFarms.push({
                x: 80 + Math.floor(Math.random() * (WORLD - 160)),
                y: 14 + Math.floor(Math.random() * 12),
                n: 1 + Math.floor(Math.random() * 2),
            })
        }
        const trafficLights: { x: number; yOff: number; phase: number }[] = []
        {
            const rng = mulberry32(307)
            for (let i = 0; i < 5; i++) {
                trafficLights.push({
                    x: 30 + Math.floor(rng() * (WORLD - 60)),
                    yOff: 7 + Math.floor(rng() * 4),
                    phase: Math.floor(rng() * 3),
                })
            }
        }
        const buoys: { x: number; y: number; phase: number }[] = []
        {
            const rng = mulberry32(203)
            for (let i = 0; i < 5; i++) {
                buoys.push({
                    x: rng() * WORLD,
                    y: 10 + Math.floor(rng() * 24),
                    phase: rng() * Math.PI * 2,
                })
            }
        }
        const liveFx: {
            layer: number
            x: number
            yOff: number
            kind: "flicker" | "elevator" | "aviation" | "radiomast" | "neon" | "lantern" | "arena" | "ferris" | "lighthouse" | "coaster" | "helipad" | "billboard" | "parkcar" | "amber" | "flag" | "aqfish" | "marquee" | "crane"
            w: number
            h: number
            phase: number
            color: string
            r: number
            hold?: number
        }[] = []
        {
            const rng = mulberry32(501)
            layerBuildings.forEach((buildings, i) => {
                for (const b of buildings) {
                    if (b.kind === "arena") {
                        liveFx.push({ layer: i, x: b.x + Math.floor(b.w / 2), yOff: b.h, kind: "arena", w: b.w, h: b.h, phase: rng() * 6, color: "#d8e8ff", r: 0 })
                    }
                    if (b.kind === "ferris") {
                        liveFx.push({
                            layer: i,
                            x: b.x + Math.floor(b.w / 2),
                            yOff: b.h + 2,
                            kind: "ferris",
                            w: b.w,
                            h: b.h,
                            phase: rng() * Math.PI * 2,
                            color: "#f5d84a",
                            r: Math.min(16, Math.floor(b.w / 2) - 2),
                            hold: 0,
                        })
                    }
                    if (b.kind === "coaster") {
                        liveFx.push({
                            layer: i,
                            x: b.x,
                            yOff: b.h,
                            kind: "coaster",
                            w: b.w,
                            h: b.h,
                            phase: 0,
                            color: "#c8d0e0",
                            r: 0.08,
                        })
                    }
                    if (b.kind === "lighthouse") {
                        liveFx.push({ layer: i, x: b.x + Math.floor(b.w / 2), yOff: b.h + 5, kind: "lighthouse", w: b.w, h: b.h, phase: rng() * 4, color: "#ffe8a0", r: 0 })
                    }
                    if (b.kind === "pagoda") {
                        for (let n = 0; n < 4; n++) {
                            liveFx.push({
                                layer: i,
                                x: b.x + 3 + n * Math.max(3, Math.floor((b.w - 6) / 3)),
                                yOff: b.h + 6 + (n % 2) * 5,
                                kind: "lantern",
                                w: 1,
                                h: 1,
                                phase: rng() * Math.PI * 2,
                                color: "#ffb060",
                                r: 0,
                            })
                        }
                    }
                    if (b.kind === "cinema") {
                        liveFx.push({
                            layer: i,
                            x: b.x,
                            yOff: b.h + 5,
                            kind: "neon",
                            w: b.w + 2,
                            h: 5,
                            phase: rng() * 5,
                            color: "#c45a78",
                            r: 0,
                        })
                    }
                    if (b.neon && b.sign) {
                        liveFx.push({
                            layer: i,
                            x: b.x + b.sign.x,
                            yOff: b.h - b.sign.y,
                            kind: "neon",
                            w: b.sign.w,
                            h: b.sign.h,
                            phase: rng() * 5,
                            color: b.sign.color,
                            r: 0,
                        })
                    }
                    if (b.roofProp === "crane") {
                        const cx = Math.floor(b.w * 0.3)
                        liveFx.push({
                            layer: i,
                            x: b.x + cx,
                            yOff: b.h + 12,
                            kind: "crane",
                            w: Math.max(5, b.w - cx - 2),
                            h: 7,
                            phase: rng() * 8,
                            color: "#8a94a8",
                            r: 0,
                        })
                    }
                    if (b.roofProp === "mast") {
                        liveFx.push({ layer: i, x: b.x + Math.floor(b.w / 2), yOff: b.h + 17, kind: "radiomast", w: 1, h: 1, phase: rng() * 8, color: "#ff3030", r: 0 })
                    }
                    if (i >= 5 && b.w >= 8 && b.h >= 10 && rng() > 0.84
                        && b.kind !== "ferris" && b.kind !== "coaster" && b.kind !== "lighthouse" && b.kind !== "pagoda"
                        && b.kind !== "hospital" && b.kind !== "school" && b.kind !== "powerplant" && b.kind !== "station"
                        && b.kind !== "controltower" && b.kind !== "stilthouse" && b.kind !== "castle" && b.kind !== "cinema"
                        && b.kind !== "marquee") {
                        liveFx.push({
                            layer: i,
                            x: b.x + 1,
                            yOff: b.h - 2,
                            kind: "billboard",
                            w: Math.min(6, b.w - 2),
                            h: 3,
                            phase: rng() * 6,
                            color: pick(rng, ["#c45a78", "#5a88c8", "#c87848", "#7a5aa8"]),
                            r: 0,
                        })
                    }
                    if (b.roofProp === "helipad" && !liveFx.some(f => f.kind === "helipad")) {
                        liveFx.push({ layer: i, x: b.x + Math.floor(b.w / 2), yOff: b.h + 2, kind: "helipad", w: b.w, h: b.h, phase: rng() * 4, color: "#c9a43a", r: 0 })
                    }
                    if (b.kind === "harborcrane") {
                        liveFx.push({ layer: i, x: b.x + 3, yOff: b.h + 15, kind: "aviation", w: 1, h: 1, phase: rng() * 8, color: "#ff4040", r: 0 })
                    }
                    if (b.windows.length > 3 && rng() > 0.4) {
                        const win = b.windows[Math.floor(rng() * b.windows.length)]
                        liveFx.push({ layer: i, x: b.x + win.x, yOff: b.h - win.y, kind: "flicker", w: win.s, h: 1, phase: rng() * 12, color: WIN, r: 0 })
                    }
                    if ((b.kind === "office" || b.kind === "tall" || b.kind === "hotel" || b.kind === "apartments" || b.kind === "tower") && b.h >= 14 && rng() > 0.5) {
                        liveFx.push({
                            layer: i,
                            x: b.x + 2 + Math.floor(rng() * Math.max(1, b.w - 4)),
                            yOff: b.h,
                            kind: "elevator",
                            w: 1,
                            h: Math.max(8, b.h - 5),
                            phase: rng(),
                            color: "#ffe8a0",
                            r: rng() > 0.5 ? 1 : -1,
                            hold: rng() * 1.4,
                        })
                    }
                    if (b.kind === "garage") {
                        liveFx.push({
                            layer: i,
                            x: b.x + 1,
                            yOff: b.h,
                            kind: "parkcar",
                            w: b.w - 2,
                            h: Math.max(2, Math.floor(b.h / 3)),
                            phase: rng() * 8,
                            color: "#f5d84a",
                            r: 4 + rng() * 6,
                        })
                    }
                    if (b.kind === "construction") {
                        liveFx.push({
                            layer: i,
                            x: b.x + Math.floor(b.w / 2),
                            yOff: b.h + 6,
                            kind: "crane",
                            w: Math.max(4, Math.floor(b.w * 0.4)),
                            h: Math.max(5, Math.floor(b.h * 0.42)),
                            phase: rng() * 8,
                            color: "#c9a43a",
                            r: 0,
                        })
                    }
                    if (b.kind === "aquarium") {
                        liveFx.push({
                            layer: i,
                            x: b.x + 3,
                            yOff: Math.floor(b.h * 0.55),
                            kind: "aqfish",
                            w: Math.max(6, b.w - 8),
                            h: Math.max(3, b.h - 8),
                            phase: rng() * 6,
                            color: "#3ae0c8",
                            r: 0,
                        })
                    }
                    if (b.kind === "marquee") {
                        liveFx.push({
                            layer: i,
                            x: b.x + 2,
                            yOff: b.h - 3,
                            kind: "marquee",
                            w: MARQUEE_COLS,
                            h: MARQUEE_ROWS,
                            phase: rng() * 40,
                            color: WIN,
                            r: MARQUEE_PITCH,
                        })
                    }
                    if ((b.kind === "hotel" || b.kind === "school" || b.kind === "castle" || b.kind === "hospital" || b.kind === "station") && rng() > 0.35) {
                        liveFx.push({
                            layer: i,
                            x: b.x + Math.max(1, b.w - 3),
                            yOff: b.h + 4,
                            kind: "flag",
                            w: 3,
                            h: 2,
                            phase: rng() * 6,
                            color: pick(rng, ["#c45a78", "#5a88c8", "#c9a43a", "#e8e8f0"]),
                            r: 0,
                        })
                    }
                }
            })
        }
        const spotLayer = 1
        const spotlights = makeSpotlights(layerBuildings[spotLayer])
        const spotSpeed = LAYER_DEFS[spotLayer].speed
        let spotRateMul = 1

        const scene = document.createElement("canvas")
        const above = document.createElement("canvas")
        const sceneCtx = scene.getContext("2d")
        const aboveCtx = above.getContext("2d")
        if (!sceneCtx || !aboveCtx) {
            return
        }

        let width = 0
        let height = 0
        let cssW = 0
        let cssH = 0
        let stars: Star[] = []
        let raf = 0
        let running = true
        let last = performance.now()
        let scroll = 0
        let cloudDrift = 0
        let t = 0
        const padHeli = { mode: 0, timer: 2 + Math.random() * 3, sx: 0, sy: 0 }
        let marqueeCols = marqueeTextColumns("HELLO · CITY")
        let marqueeNextShuffle = 4
        const ufo = {
            mode: 0,
            timer: rareWait(600),
            x: 0,
            y: 0,
            tx: 0,
            hoverY: 0,
            beam: 0,
            personY: 0,
            dir: 1,
            hidePerson: false,
        }
        const gz = {
            mode: 0,
            placed: false,
            wx: 0,
            rise: 0,
            h: 74,
            facing: -1,
            jaw: 0,
            lean: 0,
            die: 0,
            fightT: 0,
            fightMax: 30 + Math.random() * 30,
            laserT: 0,
            nextLaser: 2.2,
            nextSmash: 0.35,
            nextJet: 0,
            timerReset: rareWait(1200),
            gait: 0,
            headAng: 0,
            melee: 0,
            meleeHit: false,
        }
        const gzJets: { x: number; y: number; vx: number; vy: number; cd: number; phase: number; flee: boolean }[] = []
        const gzShots: { x: number; y: number; vx: number; vy: number; life: number }[] = []
        const gzChutes: { x: number; y: number; vx: number; vy: number; open: boolean; grounded: boolean }[] = []
        const gzLaser = { on: false, t: 0, x0: 0, y0: 0, x1: 0, y1: 0 }
        let gzLaserPick: { kind: "jet" | "chute"; i: number } | null = null
        const ruins: {
            layer: number
            x: number
            w: number
            h: number
            sprite: HTMLCanvasElement
            fall: number
            smokeT: number
        }[] = []
        const rockets: {
            x: number
            y: number
            vx: number
            vy: number
            boomY: number
            delay: number
            color: string
            scale: number
            trail: { x: number; y: number }[]
        }[] = []
        const sparks: {
            x: number
            y: number
            vx: number
            vy: number
            life: number
            max: number
            color: string
            size: number
        }[] = []
        const boatScreens: { x: number; y: number }[] = []
        const shootingStars: {
            x: number
            y: number
            vx: number
            vy: number
            life: number
            max: number
        }[] = []
        let nextShootingStar = 6 + Math.random() * 10
        const satellites: { x: number; y: number; vx: number; vy: number }[] = []
        let nextSatellite = 8 + Math.random() * 12
        let lightningLife = 0
        let lightningBolts: { x: number; y: number }[][] = []
        let nextLightning = 7 + Math.random() * 14
        let moonX = 0
        let moonY = 0
        const eggs: { id: LandmarkId; layer: number; x: number; sprite: HTMLCanvasElement }[] = []
        let eggPeriod = 1

        function placeLandmarks() {
            const layer = 5
            const ids = LANDMARK_IDS.slice().sort(() => Math.random() - 0.5)
            const minGap = Math.floor((WORLD + 240) / 2)
            let cursor = Math.floor(Math.random() * minGap)
            for (const id of ids) {
                const sprite = bakePixelSprite(paintLandmark(id))
                eggs.push({ id, layer, x: cursor, sprite })
                cursor += Math.ceil(sprite.width / PIXEL) + minGap + Math.floor(Math.random() * (WORLD / 2))
            }
            eggPeriod = cursor + minGap
        }

        function drawEggOnLayer(target: CanvasRenderingContext2D, layerIndex: number, waterline: number) {
            target.imageSmoothingEnabled = false
            for (const egg of eggs) {
                if (egg.layer !== layerIndex) {
                    continue
                }
                const y = waterline * PIXEL - egg.sprite.height
                const base = egg.x - scroll * layers[egg.layer].speed
                const first = Math.floor((-egg.sprite.width / PIXEL - base) / eggPeriod) - 1
                const last = Math.ceil((width / PIXEL - base) / eggPeriod) + 1
                for (let k = first; k <= last; k++) {
                    const x = (base + k * eggPeriod) * PIXEL
                    if (x > width || x + egg.sprite.width < 0) {
                        continue
                    }
                    target.drawImage(egg.sprite, x, y)
                }
            }
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            cssW = window.innerWidth
            cssH = window.innerHeight
            width = Math.max(1, Math.ceil(cssW / CITY_ZOOM))
            height = Math.max(1, Math.ceil(cssH / CITY_ZOOM))
            canvas!.width = Math.floor(cssW * dpr)
            canvas!.height = Math.floor(cssH * dpr)
            canvas!.style.width = `${cssW}px`
            canvas!.style.height = `${cssH}px`
            ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx!.imageSmoothingEnabled = false
            scene.width = width
            scene.height = height
            above.width = width
            above.height = height
            sceneCtx!.imageSmoothingEnabled = false
            aboveCtx!.imageSmoothingEnabled = false
            stars = makeStars(3, width, Math.max(40, height - WATER_H * PIXEL))
            moonX = Math.floor(width * 0.42)
            moonY = Math.floor(Math.max(28, height * 0.14) / PIXEL) * PIXEL
            for (const d of rainDrops) {
                d.x = Math.random() * width
                d.y = Math.random() * height
            }
        }

        function drawSky(target: CanvasRenderingContext2D, waterY: number) {
            const g = target.createLinearGradient(0, 0, 0, waterY)
            g.addColorStop(0, "#0c1450")
            g.addColorStop(0.55, "#1a2a78")
            g.addColorStop(1, "#3a4c96")
            target.fillStyle = g
            target.fillRect(0, 0, width, waterY)
            const mx = snapP(moonX)
            const my = snapP(moonY)
            const moonLit = "#e8eefc"
            const moonDark = "#4a556c"
            for (let dy = -4; dy <= 4; dy++) {
                for (let dx = -4; dx <= 4; dx++) {
                    if (dx * dx + dy * dy > 18) {
                        continue
                    }
                    let lit = true
                    if (moonPhase === 0) {
                        lit = false
                    } else if (moonPhase === 1) {
                        lit = dx >= 2
                    } else if (moonPhase === 2) {
                        lit = dx >= 1
                    } else if (moonPhase === 3) {
                        lit = dx >= 0
                    } else if (moonPhase === 4) {
                        lit = true
                    } else if (moonPhase === 5) {
                        lit = dx <= 0
                    } else if (moonPhase === 6) {
                        lit = dx <= -1
                    } else {
                        lit = dx <= -2
                    }
                    target.fillStyle = lit ? moonLit : moonDark
                    target.fillRect(mx + dx * PIXEL, my + dy * PIXEL, PIXEL, PIXEL)
                }
            }
            if (raining) {
                target.fillStyle = "rgba(10, 14, 32, 0.22)"
                target.fillRect(0, 0, width, waterY)
            }
        }

        function drawStars(target: CanvasRenderingContext2D, waterY: number) {
            for (const s of stars) {
                if (s.y >= waterY - 8) {
                    continue
                }
                const a = 0.56 + s.twAmp * Math.sin(t * 0.26 + s.tw)
                target.fillStyle = `rgba(236, 240, 255, ${a})`
                target.fillRect(s.x, s.y, s.s, s.s)
            }
        }

        function drawShootingStars(target: CanvasRenderingContext2D, waterY: number) {
            for (const m of shootingStars) {
                if (m.y >= waterY - 12) {
                    continue
                }
                const n = 6
                for (let i = 0; i < n; i++) {
                    const u = i / n
                    const a = (1 - u) * Math.max(0, m.life / m.max)
                    target.globalAlpha = a * (i === 0 ? 0.95 : 0.22)
                    target.fillStyle = i === 0 ? "#f4f6ff" : "#c8d4f0"
                    target.fillRect(snapP(m.x - m.vx * u * 0.045), snapP(m.y - m.vy * u * 0.045), PIXEL, PIXEL)
                }
            }
            target.globalAlpha = 1
        }

        function drawPlanes(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const layerSpeed = 0.07
            for (const p of planes) {
                const ax = mod(p.x + t * p.speed - scroll * layerSpeed, WORLD)
                const blink = Math.sin(t * 5.5 + p.blink) > 0.78
                target.fillStyle = blink ? "#ffe2b0" : "#c4cce0"
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (ax + copy * WORLD) * PIXEL
                    if (x < -PIXEL || x > viewW) {
                        continue
                    }
                    target.fillRect(x, (waterline - p.y) * PIXEL, PIXEL, PIXEL)
                    if (p.contrail) {
                        const dir = p.speed >= 0 ? 1 : -1
                        const py = (waterline - p.y) * PIXEL
                        for (let k = 1; k <= 16; k++) {
                            const a = (1 - k / 16) * 0.38
                            target.fillStyle = `rgba(210, 220, 240, ${a})`
                            target.fillRect(snapP(x - dir * k * 2 * PIXEL), py + (k > 8 ? PIXEL : 0), PIXEL, PIXEL)
                        }
                    }
                    if (p.banner) {
                        const dir = p.speed >= 0 ? 1 : -1
                        const bx = x - dir * 2 * PIXEL
                        const by = (waterline - p.y) * PIXEL
                        target.fillStyle = "#3a3048"
                        const bw = 11
                        target.fillRect(bx - (dir > 0 ? bw * PIXEL : 0), by + PIXEL, bw * PIXEL, 2 * PIXEL)
                        target.fillStyle = "#f5d84a"
                        for (let k = 1; k < bw - 1; k += 2) {
                            target.fillRect(bx - (dir > 0 ? (bw - k) * PIXEL : -k * PIXEL), by + PIXEL, PIXEL, PIXEL)
                        }
                    }
                }
            }
        }

        function drawCrafts(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            for (const c of crafts) {
                const layerSpeed = c.kind === "balloon" ? 0.035 : 0.08
                const ax = mod(c.x + t * c.speed - scroll * layerSpeed, WORLD)
                const blink = Math.sin(t * 4.2 + c.blink) > 0.55
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (ax + copy * WORLD) * PIXEL
                    const y = (waterline - c.y) * PIXEL
                    if (x < -20 || x > viewW) {
                        continue
                    }
                    if (c.kind === "blimp") {
                        target.fillStyle = "#6a7488"
                        target.fillRect(x, y, 7 * PIXEL, 3 * PIXEL)
                        target.fillStyle = "#4a5468"
                        target.fillRect(x + PIXEL, y + 3 * PIXEL, 4 * PIXEL, PIXEL)
                        target.fillStyle = blink ? "#ffe2b0" : "#8a90a4"
                        target.fillRect(x + 6 * PIXEL, y + PIXEL, PIXEL, PIXEL)
                    } else if (c.kind === "balloon") {
                        target.fillStyle = "#8a4058"
                        target.fillRect(x, y, 4 * PIXEL, 4 * PIXEL)
                        target.fillStyle = "#6a3048"
                        target.fillRect(x + PIXEL, y + PIXEL, 2 * PIXEL, 2 * PIXEL)
                        target.fillStyle = "#3a3040"
                        target.fillRect(x + PIXEL, y + 4 * PIXEL, 2 * PIXEL, 2 * PIXEL)
                        target.fillStyle = Math.sin(t * 7 + c.blink) > 0.15 ? "#ff9a4a" : "#6a4030"
                        target.fillRect(x + PIXEL, y + 4 * PIXEL, PIXEL, PIXEL)
                    } else {
                        target.fillStyle = "#3a4458"
                        target.fillRect(x, y + PIXEL, 4 * PIXEL, 2 * PIXEL)
                        target.fillStyle = "#2a3448"
                        const rotor = Math.sin(t * 28 + c.blink) > 0 ? 3 : 2
                        target.fillRect(x - PIXEL, y, rotor * PIXEL, PIXEL)
                        target.fillStyle = blink ? "#ff9a4a" : "#7ec8e0"
                        target.fillRect(x + 3 * PIXEL, y + PIXEL, PIXEL, PIXEL)
                    }
                }
            }
        }

        function drawSatellites(target: CanvasRenderingContext2D, waterY: number) {
            for (const s of satellites) {
                if (s.y >= waterY - 8) {
                    continue
                }
                const edge = 70
                let a = 1
                if (s.x < edge) {
                    a = Math.max(0, s.x / edge)
                } else if (s.x > width - edge) {
                    a = Math.max(0, (width - s.x) / edge)
                }
                if (a < 0.04) {
                    continue
                }
                target.globalAlpha = a
                target.fillStyle = "#e8eefc"
                target.fillRect(snapP(s.x), snapP(s.y), PIXEL, PIXEL)
                target.fillStyle = "rgba(200, 210, 230, 0.45)"
                target.fillRect(snapP(s.x - Math.sign(s.vx) * PIXEL), snapP(s.y), PIXEL, PIXEL)
                target.globalAlpha = 1
            }
        }

        function drawLightning(target: CanvasRenderingContext2D, waterY: number) {
            if (lightningLife <= 0) {
                return
            }
            const a = Math.min(1, lightningLife * 6)
            target.fillStyle = `rgba(210, 220, 255, ${0.08 * a})`
            target.fillRect(0, 0, width, waterY)
            target.fillStyle = `rgba(236, 242, 255, ${0.85 * a})`
            for (const bolt of lightningBolts) {
                for (const p of bolt) {
                    target.fillRect(snapP(p.x), snapP(p.y), PIXEL, PIXEL)
                }
            }
        }

        function drawLiveFx(target: CanvasRenderingContext2D, waterline: number, viewW: number, layerIndex: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const speed = LAYER_DEFS[layerIndex].speed
            target.imageSmoothingEnabled = false
            for (const fx of liveFx) {
                if (fx.layer !== layerIndex) {
                    continue
                }
                const ax = mod(fx.x - scroll * speed, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (ax + copy * WORLD) * PIXEL
                    if (x < -40 || x > viewW + 40) {
                        continue
                    }
                    if (fx.kind === "flicker") {
                        if (Math.sin(t * 1.4 + fx.phase) > 0.72) {
                            target.fillStyle = "#12182c"
                            target.fillRect(x, (waterline - fx.yOff) * PIXEL, fx.w * PIXEL, PIXEL)
                        }
                    } else if (fx.kind === "elevator") {
                        const yOff = 3 + fx.phase * fx.h
                        target.fillStyle = "#ffe8a0"
                        target.fillRect(x, (waterline - yOff) * PIXEL, PIXEL, PIXEL)
                    } else if (fx.kind === "aviation") {
                        if (Math.sin(t * 6 + fx.phase) > 0.15) {
                            target.fillStyle = "#ff4040"
                            target.fillRect(x, (waterline - fx.yOff) * PIXEL, PIXEL, PIXEL)
                        }
                    } else if (fx.kind === "radiomast") {
                        if (Math.sin(t * 2.05 + fx.phase) > 0.2) {
                            target.fillStyle = "#ff2a2a"
                            target.fillRect(x, (waterline - fx.yOff) * PIXEL, PIXEL, PIXEL)
                            target.fillStyle = "rgba(255, 60, 60, 0.35)"
                            target.fillRect(x - PIXEL, (waterline - fx.yOff) * PIXEL - PIXEL, 3 * PIXEL, 3 * PIXEL)
                        }
                    } else if (fx.kind === "billboard") {
                        const pal = ["#c45a78", "#5a88c8", "#c87848", "#7a5aa8", "#3ae07a"]
                        const idx = Math.floor(t * 1.7 + fx.phase) % pal.length
                        const y = (waterline - fx.yOff) * PIXEL
                        target.fillStyle = "#141820"
                        target.fillRect(x, y, fx.w * PIXEL, fx.h * PIXEL)
                        target.fillStyle = pal[idx]
                        target.fillRect(x + PIXEL, y + PIXEL, Math.max(PIXEL, (fx.w - 2) * PIXEL), PIXEL)
                        const slide = Math.floor(mod(t * 4 + fx.phase, Math.max(1, fx.w - 2)))
                        target.fillStyle = pal[(idx + 1) % pal.length]
                        target.fillRect(x + PIXEL + slide * PIXEL, y + PIXEL, PIXEL, PIXEL)
                    } else if (fx.kind === "neon") {
                        const pulse = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.2 + fx.phase))
                        const c = fx.color
                        const n = parseInt(c.slice(1), 16)
                        const r = Math.min(255, Math.floor(((n >> 16) & 255) * pulse + 40))
                        const g = Math.min(255, Math.floor(((n >> 8) & 255) * pulse + 20))
                        const b = Math.min(255, Math.floor((n & 255) * pulse + 40))
                        target.fillStyle = `rgb(${r},${g},${b})`
                        target.fillRect(x, (waterline - fx.yOff) * PIXEL, fx.w * PIXEL, Math.max(PIXEL, fx.h * PIXEL * 0.35))
                    } else if (fx.kind === "lantern") {
                        const bob = Math.sin(t * 1.3 + fx.phase) * 2
                        const glow = 0.4 + 0.4 * Math.sin(t * 2 + fx.phase)
                        target.fillStyle = `rgba(255, 176, 80, ${glow})`
                        target.fillRect(x, (waterline - fx.yOff) * PIXEL + bob * PIXEL, PIXEL, PIXEL)
                    } else if (fx.kind === "arena") {
                        const pulse = 0.12 + 0.1 * (0.5 + 0.5 * Math.sin(t * 1.1 + fx.phase))
                        target.globalCompositeOperation = "lighter"
                        const originY = (waterline - fx.yOff) * PIXEL
                        for (let i = 0; i < 18; i++) {
                            const u = i / 18
                            target.fillStyle = `rgba(210, 222, 255, ${(1 - u) * pulse})`
                            target.fillRect(snapP(x - i * PIXEL * 0.4), snapP(originY - i * 3 * PIXEL), PIXEL * 2, PIXEL)
                            target.fillRect(snapP(x + i * PIXEL * 0.4), snapP(originY - i * 3 * PIXEL), PIXEL * 2, PIXEL)
                        }
                        target.globalCompositeOperation = "source-over"
                    } else if (fx.kind === "ferris") {
                        const originY = (waterline - fx.yOff) * PIXEL
                        const rad = fx.r + 2
                        for (let i = 0; i < 8; i++) {
                            const a = fx.phase + (Math.PI * 2 * i) / 8
                            const px = x + Math.round(Math.cos(a) * rad) * PIXEL
                            const py = originY + Math.round(Math.sin(a) * rad) * PIXEL
                            target.fillStyle = i % 2 === 0 ? "#f5d84a" : "#ff9a4a"
                            target.fillRect(px - PIXEL, py, 3 * PIXEL, 2 * PIXEL)
                            target.fillStyle = "#2a2438"
                            target.fillRect(px, py + PIXEL, PIXEL, PIXEL)
                        }
                    } else if (fx.kind === "crane") {
                        const pose = craneTrolleyAndCable(t * 0.16 + fx.phase)
                        const jib = Math.max(3, fx.w)
                        const mastTop = (waterline - fx.yOff) * PIXEL
                        target.fillStyle = "#e0a060"
                        target.fillRect(x, mastTop, jib * PIXEL, PIXEL)
                        const tx = x + Math.round(1 + pose.trolley * Math.max(1, jib - 2)) * PIXEL
                        const drop = Math.max(2, Math.round(pose.cable * fx.h))
                        target.fillStyle = "#2a2430"
                        for (let k = 1; k <= drop; k++) {
                            target.fillRect(tx, mastTop + k * PIXEL, PIXEL, PIXEL)
                        }
                        target.fillStyle = fx.color
                        target.fillRect(tx - PIXEL, mastTop + drop * PIXEL, 3 * PIXEL, PIXEL)
                        target.fillStyle = "#6a7488"
                        target.fillRect(tx - PIXEL, mastTop + (drop + 1) * PIXEL, 3 * PIXEL, PIXEL)
                        if (Math.floor(t * 4 + fx.phase) % 2 === 0) {
                            target.fillStyle = "#ff9a4a"
                            target.fillRect(x + (jib - 1) * PIXEL, mastTop - PIXEL, PIXEL, PIXEL)
                        }
                    } else if (fx.kind === "parkcar") {
                        const decks = Math.max(1, fx.h)
                        const deck = Math.floor(mod(t * 0.12 + fx.phase, decks))
                        const u = mod(t * fx.r * 0.08 + fx.phase, 1)
                        const cx = x + Math.floor(u * Math.max(1, fx.w - 1)) * PIXEL
                        const cy = (waterline - fx.yOff + 2 + deck * 3) * PIXEL
                        target.fillStyle = fx.color
                        target.fillRect(cx, cy, PIXEL, PIXEL)
                    } else if (fx.kind === "amber") {
                        if (Math.floor(t * 3 + fx.phase) % 2 === 0) {
                            target.fillStyle = "#ff9a4a"
                            target.fillRect(x, (waterline - fx.yOff) * PIXEL, PIXEL, PIXEL)
                        }
                    } else if (fx.kind === "flag") {
                        const flap = Math.sin(t * 3.2 + fx.phase) >= 0 ? 0 : 1
                        const y = (waterline - fx.yOff) * PIXEL
                        target.fillStyle = "#8a94a8"
                        target.fillRect(x, y, PIXEL, 4 * PIXEL)
                        target.fillStyle = fx.color
                        target.fillRect(x + PIXEL, y + PIXEL, PIXEL, 2 * PIXEL)
                        target.fillRect(x + 2 * PIXEL, y + PIXEL + flap * PIXEL, PIXEL, 2 * PIXEL)
                    } else if (fx.kind === "aqfish") {
                        const u = (Math.sin(t * 0.9 + fx.phase) + 1) / 2
                        const v = (Math.sin(t * 1.4 + fx.phase * 1.7) + 1) / 2
                        target.fillStyle = fx.color
                        target.fillRect(x + Math.floor(u * fx.w) * PIXEL, (waterline - fx.yOff + Math.floor(v * fx.h)) * PIXEL, PIXEL, PIXEL)
                    } else if (fx.kind === "marquee") {
                        const pitch = fx.r || MARQUEE_PITCH
                        const originY = (waterline - fx.yOff) * PIXEL
                        const shift = Math.floor(t * 4.4 + fx.phase)
                        const n = marqueeCols.length
                        for (let c = 0; c < fx.w; c++) {
                            const col = marqueeCols[(shift + c) % n]
                            for (let r = 0; r < fx.h; r++) {
                                if (!col[r]) {
                                    continue
                                }
                                target.fillStyle = WIN
                                target.fillRect(x + c * pitch * PIXEL, originY + r * pitch * PIXEL, PIXEL, PIXEL)
                            }
                        }
                    } else if (fx.kind === "coaster") {
                        if (fx.phase >= 0) {
                            const head = fx.phase
                            for (let c = 0; c < 4; c++) {
                                const u = head - c * 0.034
                                if (u < 0 || u > 1) {
                                    continue
                                }
                                const p = coasterTrackAt(u, fx.w, fx.h)
                                const cx = x + p.x * PIXEL
                                const cy = (waterline - fx.yOff + p.y) * PIXEL
                                target.fillStyle = c === 0 ? "#e8eef8" : "#8a98ac"
                                target.fillRect(cx, cy, 3 * PIXEL, PIXEL)
                                if (c === 0) {
                                    target.fillStyle = "#f5d84a"
                                    target.fillRect(cx + 2 * PIXEL, cy, PIXEL, PIXEL)
                                }
                            }
                        }
                    } else if (fx.kind === "lighthouse") {
                        const originY = (waterline - fx.yOff) * PIXEL
                        const yaw = t * 0.65 + fx.phase
                        const side = Math.sin(yaw)
                        const toward = Math.cos(yaw)
                        const fade = Math.max(0, Math.min(1, (toward + 0.82) / 1.15))
                        if (fade > 0.02) {
                            target.globalCompositeOperation = "lighter"
                            const glow = (0.18 + 0.2 * Math.max(0, toward)) * fade
                            target.fillStyle = `rgba(255, 220, 140, ${glow})`
                            target.fillRect(x - PIXEL, originY - PIXEL, 3 * PIXEL, 3 * PIXEL)
                            const len = 28 + Math.floor(Math.abs(side) * 86)
                            const dx = side >= 0 ? 1 : -1
                            const dy = 0.16
                            for (let i = 3; i < len; i++) {
                                const u = i / len
                                const a = (1 - u) * (1 - u) * 0.32 * (0.35 + Math.abs(side)) * fade
                                target.fillStyle = `rgba(255, 220, 140, ${a})`
                                const cx = x + dx * i * PIXEL
                                const cy = originY + dy * i * PIXEL
                                if (cy > waterline * PIXEL - PIXEL) {
                                    continue
                                }
                                const half = u < 0.2 ? 0 : 1
                                target.fillRect(snapP(cx), snapP(cy), PIXEL, PIXEL)
                                if (half) {
                                    target.fillRect(snapP(cx), snapP(cy + PIXEL), PIXEL, PIXEL)
                                }
                            }
                            target.globalCompositeOperation = "source-over"
                        }
                    }
                }
            }
        }

        function drawBridge(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const ax = mod(bridgeX - scroll, WORLD)
            const y = waterline * PIXEL - bridgeSprite.height + 8 * PIXEL
            for (let copy = -1; copy <= copies; copy++) {
                const x = (ax + copy * WORLD) * PIXEL
                if (x > viewW || x + bridgeSprite.width < 0) {
                    continue
                }
                target.drawImage(bridgeSprite, x, y)
                for (let i = 0; i < 6; i++) {
                    const u = mod(t * (10 + i * 3) / (bridgeSprite.width / PIXEL) + i * 0.16, 1)
                    target.fillStyle = i % 2 === 0 ? "#f5d84a" : "#ffe8a0"
                    target.fillRect(x + Math.floor(u * (bridgeSprite.width - PIXEL)), y + 30 * PIXEL, PIXEL, PIXEL)
                }
            }
        }

        function drawSpotlights(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const waterY = waterline * PIXEL
            target.imageSmoothingEnabled = false
            target.globalCompositeOperation = "lighter"
            for (const s of spotlights) {
                const ax = mod(s.x - scroll * spotSpeed, WORLD)
                const ang = s.lean + Math.sin(s.clock + s.phase) * s.sweep
                const dx = Math.sin(ang)
                const dy = -Math.cos(ang)
                const originY = (waterline - s.yOff) * PIXEL
                for (let copy = -1; copy <= copies; copy++) {
                    const originX = (ax + copy * WORLD) * PIXEL
                    if (originX < -180 || originX > viewW + 180) {
                        continue
                    }
                    const start = 0
                    for (let i = start; i < s.len; i++) {
                        const u = i / s.len
                        const cx = originX + dx * i * PIXEL
                        const cy = originY + dy * i * PIXEL
                        if (cy < 4 || cy > waterY - PIXEL) {
                            continue
                        }
                        const half = 2 + Math.floor(u * 11)
                        const a = (1 - u) * (1 - u) * 0.22
                        target.fillStyle = `rgba(210, 222, 255, ${a})`
                        for (let k = -half; k <= half; k++) {
                            const px = snapP(cx + (-dy) * k * PIXEL)
                            const py = snapP(cy + dx * k * PIXEL)
                            if (px < -PIXEL || px > viewW) {
                                continue
                            }
                            target.fillRect(px, py, PIXEL, PIXEL)
                        }
                    }
                }
            }
            target.globalCompositeOperation = "source-over"
            target.globalAlpha = 1
        }

        function updateShootingStars(dt: number, waterY: number) {
            nextShootingStar -= dt
            if (nextShootingStar <= 0 && shootingStars.length === 0) {
                const shower = Math.random() < 0.42
                const n = shower ? 5 + Math.floor(Math.random() * 4) : 1
                const fromLeft = Math.random() > 0.4
                for (let i = 0; i < n; i++) {
                    const y = 8 + Math.random() * Math.max(18, waterY * 0.32) + i * 6
                    const speed = 240 + Math.random() * 180
                    const dip = 0.2 + Math.random() * 0.3
                    const ang = fromLeft ? dip : Math.PI - dip
                    const life = 1.5 + Math.random() * 1.2
                    shootingStars.push({
                        x: fromLeft ? -12 - i * 18 : width + 12 + i * 18,
                        y,
                        vx: Math.cos(ang) * speed,
                        vy: Math.sin(ang) * speed,
                        life,
                        max: life,
                    })
                }
                nextShootingStar = shower ? 22 + Math.random() * 20 : 14 + Math.random() * 24
            }
            for (let i = shootingStars.length - 1; i >= 0; i--) {
                const m = shootingStars[i]
                m.x += m.vx * dt
                m.y += m.vy * dt
                m.life -= dt
                if (m.life <= 0 || m.x < -80 || m.x > width + 80 || m.y > waterY) {
                    shootingStars.splice(i, 1)
                }
            }
        }

        function updateSkyFx(dt: number, waterY: number) {
            nextSatellite -= dt
            if (nextSatellite <= 0 && satellites.length === 0) {
                const fromLeft = Math.random() > 0.35
                const vx = (fromLeft ? 1 : -1) * (38 + Math.random() * 18)
                satellites.push({
                    x: fromLeft ? -12 : width + 12,
                    y: 10 + Math.random() * Math.max(16, waterY * 0.22),
                    vx,
                    vy: 1.5 + Math.random() * 3,
                })
                nextSatellite = 16 + Math.random() * 22
            }
            for (let i = satellites.length - 1; i >= 0; i--) {
                const s = satellites[i]
                s.x += s.vx * dt
                s.y += s.vy * dt
                if (s.x < -80 || s.x > width + 80 || s.y > waterY) {
                    satellites.splice(i, 1)
                }
            }
            nextLightning -= dt
            lightningLife = Math.max(0, lightningLife - dt)
            if (raining && nextLightning <= 0 && lightningLife <= 0) {
                const bolts: { x: number; y: number }[][] = []
                const n = 1 + (Math.random() > 0.55 ? 1 : 0)
                for (let b = 0; b < n; b++) {
                    const pts: { x: number; y: number }[] = []
                    let x = width * (0.12 + Math.random() * 0.76)
                    let y = 4
                    while (y < waterY * 0.4) {
                        pts.push({ x, y })
                        x += (Math.random() - 0.5) * 22
                        y += 7 + Math.random() * 12
                    }
                    bolts.push(pts)
                }
                lightningBolts = bolts
                lightningLife = 0.1 + Math.random() * 0.14
                nextLightning = 9 + Math.random() * 16
            }
        }

        function drawSmoke(target: CanvasRenderingContext2D, waterline: number, viewW: number, layerIndex: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const speed = LAYER_DEFS[layerIndex].speed
            target.imageSmoothingEnabled = false
            for (const s of smokes) {
                if (s.layer !== layerIndex) {
                    continue
                }
                const ax = mod(s.x - scroll * speed, WORLD)
                const originY = (waterline - s.yOff) * PIXEL
                for (let copy = -1; copy <= copies; copy++) {
                    const originX = (ax + copy * WORLD) * PIXEL
                    if (originX < -20 || originX > viewW + 20) {
                        continue
                    }
                    for (let p = 0; p < 8; p++) {
                        const u = (t * s.rate + s.phase + p * 0.12) % 1
                        const drift = Math.sin(t * 0.7 + s.phase + p) * u * 4 * PIXEL
                        const px = snapP(originX + drift)
                        const py = snapP(originY - u * 22 * PIXEL)
                        const a = (1 - u) * 0.55
                        if (a < 0.06) {
                            continue
                        }
                        const sz = u < 0.35 ? PIXEL : 2 * PIXEL
                        target.fillStyle = `rgba(10, 10, 12, ${Math.min(0.85, a + 0.2)})`
                        target.fillRect(px, py, sz, sz)
                        target.fillStyle = `rgba(28, 28, 32, ${a * 0.65})`
                        target.fillRect(px + PIXEL, py - PIXEL, PIXEL, PIXEL)
                    }
                }
            }
        }

        function drawTrafficLights(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const step = Math.floor(t / 1.7) % 3
            for (const light of trafficLights) {
                const ax = mod(light.x - scroll * 0.88, WORLD)
                const lit = (step + light.phase) % 3
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (ax + copy * WORLD) * PIXEL
                    if (x < -PIXEL || x > viewW) {
                        continue
                    }
                    const y = (waterline - light.yOff) * PIXEL
                    target.fillStyle = "#1a2030"
                    target.fillRect(x, y - 6 * PIXEL, PIXEL, 6 * PIXEL)
                    const colors = ["#ff3a4a", "#f5d84a", "#3ae07a"]
                    for (let i = 0; i < 3; i++) {
                        target.fillStyle = i === lit ? colors[i] : "#2a3040"
                        target.fillRect(x, y - (6 - i * 2) * PIXEL, PIXEL, PIXEL)
                    }
                }
            }
        }

        function drawPadHeli(target: CanvasRenderingContext2D) {
            if (padHeli.mode === 0) {
                return
            }
            const x = padHeli.sx
            const y = padHeli.sy
            if (x < -24 || x > width + 24 || y < -24) {
                return
            }
            const rotor = Math.sin(t * 32) > 0 ? 3 : 2
            target.fillStyle = "#3a4458"
            target.fillRect(x, y + PIXEL, 4 * PIXEL, 2 * PIXEL)
            target.fillStyle = "#2a3448"
            target.fillRect(x - PIXEL, y, rotor * PIXEL, PIXEL)
            target.fillStyle = "#ff9a4a"
            target.fillRect(x + 3 * PIXEL, y + PIXEL, PIXEL, PIXEL)
        }

        function drawUfo(target: CanvasRenderingContext2D, waterline: number, sx = 1, sy = 1) {
            if (ufo.mode === 0) {
                return
            }
            const p = PIXEL * sx
            const groundY = (waterline - 2) * PIXEL * sy
            const x = ufo.x * sx
            const y = ufo.y * sy
            if (x < -80 || x > cssW + 80 || y < -80) {
                return
            }
            if (ufo.beam > 0.04) {
                const a = 0.22 + 0.42 * ufo.beam
                const beamH = Math.max(p, groundY - (y + 5 * p))
                target.fillStyle = `rgba(160, 255, 170, ${a})`
                target.fillRect(x + 4 * p, y + 5 * p, 3 * p, beamH)
                target.fillStyle = `rgba(220, 255, 210, ${a * 0.8})`
                target.fillRect(x + 5 * p, y + 5 * p, p, beamH)
            }
            if (!ufo.hidePerson && ufo.mode >= 2) {
                const py = groundY - ufo.personY * p
                target.fillStyle = "#d8c8a0"
                target.fillRect(x + 5 * p, py, p, p)
            }
            target.fillStyle = "#8aa0b8"
            target.fillRect(x, y + 2 * p, 11 * p, 3 * p)
            target.fillStyle = "#5a7088"
            target.fillRect(x + p, y + p, 9 * p, p)
            target.fillStyle = "#c8e8d8"
            target.fillRect(x + 3 * p, y, 5 * p, p)
            const blink = Math.sin(t * 10) > 0
            target.fillStyle = blink ? "#7cff9a" : "#f5d84a"
            target.fillRect(x + 2 * p, y + 3 * p, p, p)
            target.fillRect(x + 5 * p, y + 3 * p, p, p)
            target.fillRect(x + 8 * p, y + 3 * p, p, p)
        }

        function drawHorizonGlow(target: CanvasRenderingContext2D, waterline: number, mountainLift: number) {
            const period = farMountains.width
            const shift = scroll * 0.03
            const destY = (waterline - mountainLift - farMountains.height) * PIXEL
            const horizonY = destY + farMountains.height * PIXEL * 0.55
            const copies = Math.ceil(width / PIXEL / period) + 2
            target.save()
            target.globalCompositeOperation = "lighter"
            for (let copy = -1; copy <= copies; copy++) {
                const cx = (horizonGlowX - shift + copy * period) * PIXEL
                if (cx < -200 || cx > width + 200) {
                    continue
                }
                for (let i = 0; i < 22; i++) {
                    const u = i / 22
                    const a = (1 - u) * (1 - u) * 0.16
                    const hw = (40 + i * 7) * PIXEL
                    const y = snapP(horizonY - i * 2 * PIXEL)
                    target.fillStyle = `rgba(255, 140, 70, ${a})`
                    target.fillRect(snapP(cx - hw), y, hw * 2, PIXEL * 2)
                    target.fillStyle = `rgba(255, 90, 110, ${a * 0.55})`
                    target.fillRect(snapP(cx - hw * 0.45), y, hw * 0.9, PIXEL)
                }
            }
            target.restore()
        }

        function drawSkiLights(target: CanvasRenderingContext2D, waterline: number, mountainLift: number) {
            const period = nearMountains.width
            const shift = scroll * 0.055
            const destY = (waterline - mountainLift - nearMountains.height) * PIXEL
            const copies = Math.ceil(width / PIXEL / period) + 2
            for (const light of skiLights) {
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (light.x - shift + copy * period) * PIXEL
                    if (x < -PIXEL || x > width) {
                        continue
                    }
                    const y = destY + light.y * PIXEL
                    const on = Math.sin(t * 3.2 + light.x * 0.2) > -0.25
                    target.fillStyle = on ? "#ffe8a0" : "#6a6038"
                    target.fillRect(snapP(x), snapP(y), PIXEL, PIXEL)
                }
            }
        }

        function gzScreenX() {
            const speed = LAYER_DEFS[GZ_LAYER].speed
            return (gz.wx - scroll * speed) * PIXEL
        }

        function gzFeetY(waterline: number) {
            return waterline * PIXEL
        }

        function spawnGzJet() {
            if (gzJets.filter(j => !j.flee).length >= 3) {
                return
            }
            const fromRight = Math.random() > 0.45
            gzJets.push({
                x: fromRight ? width + 24 : -24,
                y: 70 + Math.random() * Math.max(40, height * 0.28),
                vx: fromRight ? -(80 + Math.random() * 50) : (80 + Math.random() * 50),
                vy: (Math.random() - 0.5) * 28,
                cd: 0.35,
                phase: Math.random() * 10,
                flee: false,
            })
        }

        function drawGzJet(target: CanvasRenderingContext2D, jet: { x: number; y: number; vx: number; vy: number; phase: number }) {
            const dir = jet.vx >= 0 ? 1 : -1
            const x = snapP(jet.x)
            const y = snapP(jet.y)
            const bodyX = dir > 0 ? x : x - 6 * PIXEL
            target.fillStyle = "#12141c"
            target.fillRect(bodyX, y, 6 * PIXEL, 2 * PIXEL)
            target.fillRect(dir > 0 ? x : x - 2 * PIXEL, y - PIXEL, 3 * PIXEL, PIXEL)
            target.fillStyle = Math.sin(t * 40 + jet.phase) > 0 ? "#ff9a4a" : "#f5d84a"
            target.fillRect(dir > 0 ? x - 2 * PIXEL : x + PIXEL, y, PIXEL, PIXEL)
            target.fillStyle = "#8a90a4"
            target.fillRect(dir > 0 ? x + PIXEL : x - PIXEL, y, PIXEL, PIXEL)
        }

        function drawGzChute(target: CanvasRenderingContext2D, p: { x: number; y: number; open: boolean }) {
            const x = snapP(p.x)
            const y = snapP(p.y)
            if (p.open) {
                const capY = y - 5 * PIXEL
                target.fillStyle = "#c44a4a"
                for (let dx = -2; dx <= 2; dx++) {
                    target.fillRect(x + dx * PIXEL, capY, PIXEL, PIXEL)
                }
                target.fillRect(x - PIXEL, capY + PIXEL, 3 * PIXEL, PIXEL)
                target.fillStyle = "#d8c8b0"
                target.fillRect(x - 2 * PIXEL, capY + PIXEL, PIXEL, PIXEL)
                target.fillRect(x + 2 * PIXEL, capY + PIXEL, PIXEL, PIXEL)
                target.fillStyle = "#8a8490"
                target.fillRect(x - PIXEL, capY + 2 * PIXEL, PIXEL, PIXEL)
                target.fillRect(x + PIXEL, capY + 2 * PIXEL, PIXEL, PIXEL)
                target.fillRect(x, capY + 3 * PIXEL, PIXEL, PIXEL)
            }
            target.fillStyle = "#f0dcc0"
            target.fillRect(x, y, PIXEL, PIXEL)
        }

        function ejectPilot(jet: { x: number; y: number; vx: number }) {
            gzChutes.push({
                x: jet.x,
                y: jet.y,
                vx: jet.vx * 0.12 + (Math.random() - 0.5) * 55,
                vy: -230 - Math.random() * 40,
                open: false,
                grounded: false,
            })
        }

        function chuteIsLow(p: { y: number }, waterline: number) {
            return p.y >= gzFeetY(waterline) - 22 * PIXEL
        }

        function punchBuildingFromStrip(layer: number, b: Building) {
            const strip = layers[layer].strip
            const g = strip.getContext("2d")
            if (!g) {
                return
            }
            const maxH = strip.height - BUILD_EXTRA
            g.save()
            g.globalCompositeOperation = "destination-out"
            g.drawImage(b.sprite, b.x, maxH - b.h)
            g.restore()
        }

        function drawLayerRuins(target: CanvasRenderingContext2D, waterline: number, layerIndex: number) {
            const speed = LAYER_DEFS[layerIndex].speed
            const copies = wrapCopies(width / PIXEL)
            target.imageSmoothingEnabled = false
            for (const ruin of ruins) {
                if (ruin.layer !== layerIndex) {
                    continue
                }
                const ax = mod(ruin.x - scroll * speed, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (ax + copy * WORLD) * PIXEL
                    if (x < -120 || x > width + 120) {
                        continue
                    }
                    const roofY = (waterline - ruin.h) * PIXEL
                    const fallPx = Math.min(1, ruin.fall) * ruin.h * 0.78 * PIXEL
                    const spriteTop = roofY - BUILD_EXTRA * PIXEL + fallPx
                    const sw = ruin.sprite.width * PIXEL
                    const sh = ruin.sprite.height * PIXEL
                    target.drawImage(ruin.sprite, 0, 0, ruin.sprite.width, ruin.sprite.height, x, spriteTop, sw, sh)
                    const fade = Math.max(0, 1 - ruin.smokeT / 18)
                    if (fade <= 0.02) {
                        continue
                    }
                    const baseY = waterline * PIXEL
                    const plumeW = Math.max(5, ruin.w + 2)
                    for (let p = 0; p < 32; p++) {
                        const u = (t * 0.2 + p * 0.037 + ruin.x * 0.011) % 1
                        const a = (1 - u * u) * 0.58 * fade
                        const sz = (u < 0.22 ? 2 : u < 0.5 ? 2 : u < 0.78 ? 3 : 4) * PIXEL
                        const drift = Math.sin(t * 0.7 + p * 0.6) * 2 * PIXEL
                        const g = u < 0.4 ? 78 : 96
                        target.fillStyle = `rgba(${g}, ${g + 2}, ${g + 6}, ${a})`
                        target.fillRect(
                            snapP(x + ((p * 1.9) % plumeW - 1) * PIXEL + drift),
                            snapP(baseY - PIXEL - u * 58 * PIXEL),
                            sz,
                            sz,
                        )
                    }
                }
            }
        }

        function stepRuins(dt: number) {
            for (const ruin of ruins) {
                if (ruin.fall < 1.15) {
                    ruin.fall = Math.min(1.15, ruin.fall + dt * 0.55)
                }
                ruin.smokeT += dt
            }
        }

        function pickSmashTargets() {
            const feetX = gzScreenX()
            if (feetX < -20 || feetX > width + 20) {
                return []
            }
            const minAhead = 1 * PIXEL
            const maxAhead = 20 * PIXEL
            const hits: { layer: number; b: Building; ahead: number }[] = []
            const copies = wrapCopies(width / PIXEL)
            for (let layer = GZ_LAYER; layer <= GZ_LAYER + 1 && layer < layerBuildings.length; layer++) {
                const speed = LAYER_DEFS[layer].speed
                for (const b of layerBuildings[layer]) {
                    if (b.w < 4 || b.h < 7) {
                        continue
                    }
                    if (ruins.some(r => r.layer === layer && Math.abs(r.x - b.x) < 2)) {
                        continue
                    }
                    const ax = mod(b.x + b.w * 0.5 - scroll * speed, WORLD)
                    for (let copy = -1; copy <= copies; copy++) {
                        const sx = (ax + copy * WORLD) * PIXEL
                        const ahead = gz.facing < 0 ? feetX - sx : sx - feetX
                        if (ahead < minAhead || ahead > maxAhead) {
                            continue
                        }
                        hits.push({ layer, b, ahead })
                        break
                    }
                }
            }
            hits.sort((a, b) => a.ahead - b.ahead)
            return hits.slice(0, 3)
        }

        function commitSmash(layer: number, b: Building, waterline: number) {
            punchBuildingFromStrip(layer, b)
            ruins.push({
                layer,
                x: b.x,
                w: b.w,
                h: b.h,
                sprite: grayBuildingSprite(b.sprite),
                fall: 0,
                smokeT: 0,
            })
            const speed = LAYER_DEFS[layer].speed
            const sx = mod(b.x - scroll * speed, WORLD) * PIXEL
            explode(sx + b.w * 0.5 * PIXEL, (waterline - b.h * 0.2) * PIXEL, "#6a6e78", 0.22)
        }

        function drawGodzillaBattle(target: CanvasRenderingContext2D, waterline: number) {
            if (gz.mode === 0 && gzJets.length === 0 && gzChutes.length === 0 && gzShots.length === 0) {
                return
            }
            if (gz.mode !== 0) {
                const feetX = gzScreenX()
                const feetY = gzFeetY(waterline)
                const riseH = gz.h * gz.rise
                if (feetX > -120 && feetX < width + 120) {
                    if (gz.mode === 1) {
                        target.fillStyle = "rgba(20, 18, 16, 0.35)"
                        for (let i = 0; i < 10; i++) {
                            const px = feetX + (Math.sin(t * 6 + i) * 10 - 8) * PIXEL
                            const py = feetY - (1 + (t * 8 + i * 1.7) % 7) * PIXEL
                            target.fillRect(snapP(px), snapP(py), PIXEL, PIXEL)
                        }
                    }
                    drawGodzillaSilhouette(target, feetX, feetY, gz.facing, gz.gait, riseH, gz.lean, gz.jaw, gz.die, gz.headAng, gz.melee)
                }
            }
            for (const shot of gzShots) {
                target.fillStyle = "#f5d84a"
                target.fillRect(snapP(shot.x), snapP(shot.y), PIXEL, PIXEL)
            }
            if (gzLaser.on) {
                const steps = 18
                for (let i = 0; i <= steps; i++) {
                    const u = i / steps
                    const x = gzLaser.x0 + (gzLaser.x1 - gzLaser.x0) * u
                    const y = gzLaser.y0 + (gzLaser.y1 - gzLaser.y0) * u
                    const a = 0.35 + 0.55 * (1 - Math.abs(u - 0.5) * 0.4)
                    target.fillStyle = `rgba(255, ${80 + i * 6}, 70, ${a})`
                    const sz = i < 2 || i > steps - 2 ? PIXEL : 2 * PIXEL
                    target.fillRect(snapP(x), snapP(y), sz, sz)
                }
                target.fillStyle = "#ffe8d0"
                target.fillRect(snapP(gzLaser.x0), snapP(gzLaser.y0), PIXEL, PIXEL)
            }
            for (const jet of gzJets) {
                drawGzJet(target, jet)
            }
            for (const chute of gzChutes) {
                drawGzChute(target, chute)
            }
        }

        function updateGodzilla(dt: number, waterline: number) {
            if (gz.mode === 0) {
                if (gz.timerReset > 0) {
                    gz.timerReset -= dt
                    if (gz.timerReset <= 0) {
                        gz.placed = false
                        gz.rise = 0
                        gz.die = 0
                        gz.lean = 0
                        gz.jaw = 0
                        gz.gait = 0
                        gz.headAng = 0
                        gz.melee = 0
                        gz.meleeHit = false
                        gz.fightT = 0
                        gz.fightMax = 30 + Math.random() * 30
                        gz.nextSmash = 0.4
                        gz.nextLaser = 2.2
                        gz.mode = 1
                    }
                }
                stepRuins(dt)
                updateGzAir(dt, waterline)
                return
            }
            if (!gz.placed && width > 40) {
                const viewRight = width / PIXEL + 22
                gz.wx = scroll * LAYER_DEFS[GZ_LAYER].speed + viewRight
                gz.placed = true
                gz.mode = 1
            }
            if (!gz.placed) {
                return
            }
            if (gz.mode === 1) {
                gz.rise = Math.min(1, gz.rise + dt * 0.42)
                gz.jaw = Math.max(0, gz.jaw - dt * 2)
                gz.gait += dt * 1.2
                if (gz.rise >= 1) {
                    gz.mode = 2
                    gz.fightT = 0
                    gz.fightMax = 30 + Math.random() * 30
                    gz.nextLaser = 1.6 + Math.random() * 1.4
                    while (gzJets.filter(j => !j.flee).length < 3) {
                        spawnGzJet()
                    }
                }
            } else if (gz.mode === 2) {
                gz.fightT += dt
                gz.rise = 1
                const swinging = gz.melee > 0
                if (!swinging && !gzLaser.on) {
                    gz.wx -= 8.4 * dt
                    gz.gait += dt * 5.4
                } else {
                    gz.gait += dt * 0.6
                }
                if (!swinging) {
                    gz.nextSmash -= dt
                    if (gz.nextSmash <= 0) {
                        const hits = pickSmashTargets()
                        if (hits.length > 0) {
                            gz.melee = 0.001
                            gz.meleeHit = false
                            gz.nextSmash = 0.65 + Math.random() * 0.55
                        } else {
                            gz.nextSmash = 0.28
                        }
                    }
                }
                if (gz.melee > 0) {
                    gz.melee += dt * 1.55
                    if (!gz.meleeHit && gz.melee >= 0.5) {
                        for (const hit of pickSmashTargets()) {
                            commitSmash(hit.layer, hit.b, waterline)
                        }
                        stompChutes(waterline)
                        gz.meleeHit = true
                    }
                    if (gz.melee >= 1) {
                        gz.melee = 0
                        gz.meleeHit = false
                    }
                }
                gz.nextLaser -= dt
                gz.jaw += ((gzLaser.on ? 1 : 0.12) - gz.jaw) * Math.min(1, dt * 8)
                const neckPose = godzillaHeadPose(gz.gait, gz.jaw, 0, 0)
                const neckPt = kaijuScreenPos(gzScreenX(), gzFeetY(waterline), gz.facing, gz.lean, neckPose.nx, neckPose.ny)
                const aimAt = (ax: number, ay: number) => {
                    const want = Math.atan2(neckPt.y - ay, gz.facing * (ax - neckPt.x))
                    return Math.max(-0.45, Math.min(1.35, want))
                }
                if (gz.nextLaser <= 0 && !gzLaser.on && gz.melee <= 0) {
                    type Pick = { kind: "jet" | "chute"; i: number; x: number; y: number }
                    const opts: Pick[] = []
                    for (let i = 0; i < gzJets.length; i++) {
                        if (!gzJets[i].flee) {
                            opts.push({ kind: "jet", i, x: gzJets[i].x, y: gzJets[i].y })
                        }
                    }
                    for (let i = 0; i < gzChutes.length; i++) {
                        if (chuteIsLow(gzChutes[i], waterline)) {
                            opts.push({ kind: "chute", i, x: gzChutes[i].x, y: gzChutes[i].y })
                        }
                    }
                    if (opts.length > 0) {
                        let pick = opts[0]
                        let best = Infinity
                        for (const o of opts) {
                            const d = Math.hypot(o.x - neckPt.x, o.y - neckPt.y)
                            if (d < best) {
                                best = d
                                pick = o
                            }
                        }
                        gz.headAng = aimAt(pick.x, pick.y)
                        gz.jaw = 1
                        const mouth = godzillaMouthLocal(gz.gait, 1, 0, gz.headAng)
                        const mp = kaijuScreenPos(gzScreenX(), gzFeetY(waterline), gz.facing, gz.lean, mouth.lx, mouth.ly)
                        gzLaser.on = true
                        gzLaser.t = 0.7
                        gzLaser.x0 = mp.x
                        gzLaser.y0 = mp.y
                        gzLaser.x1 = pick.x
                        gzLaser.y1 = pick.y
                        gz.nextLaser = 3.2 + Math.random() * 2.8
                        gzLaserPick = { kind: pick.kind, i: pick.i }
                    }
                }
                if (gzLaser.on) {
                    gz.headAng = aimAt(gzLaser.x1, gzLaser.y1)
                } else {
                    const live = gzJets.find(j => !j.flee)
                    if (live) {
                        const wantHead = aimAt(live.x, live.y)
                        gz.headAng += (wantHead - gz.headAng) * Math.min(1, dt * 3.2)
                    }
                }
                if (gz.fightT >= gz.fightMax || gzScreenX() < -90) {
                    gz.mode = 3
                    gz.die = 0
                    gzLaser.on = false
                    gz.melee = 0
                    for (const jet of gzJets) {
                        jet.flee = true
                    }
                }
            } else if (gz.mode === 3) {
                gz.die = Math.min(1, gz.die + dt * 0.55)
                gz.lean += dt * 22 * (gz.facing > 0 ? 1 : -1)
                gz.jaw = Math.max(0, gz.jaw - dt)
                gz.rise = Math.max(0, 1 - Math.max(0, gz.die - 0.28) * 1.5)
                gzLaser.on = false
                for (const jet of gzJets) {
                    jet.flee = true
                }
                if (gz.rise <= 0.02) {
                    gz.mode = 0
                    gz.timerReset = rareWait(1200)
                }
            }
            stepRuins(dt)
            if (gzLaser.on) {
                gzLaser.t -= dt
                const mouth = godzillaMouthLocal(gz.gait, gz.jaw, gz.die, gz.headAng)
                const mp = kaijuScreenPos(gzScreenX(), gzFeetY(waterline), gz.facing, gz.lean, mouth.lx, mouth.ly)
                gzLaser.x0 = mp.x
                gzLaser.y0 = mp.y
                if (gzLaserPick) {
                    if (gzLaserPick.kind === "jet" && gzLaserPick.i < gzJets.length) {
                        gzLaser.x1 = gzJets[gzLaserPick.i].x
                        gzLaser.y1 = gzJets[gzLaserPick.i].y
                    } else if (gzLaserPick.kind === "chute" && gzLaserPick.i < gzChutes.length) {
                        gzLaser.x1 = gzChutes[gzLaserPick.i].x
                        gzLaser.y1 = gzChutes[gzLaserPick.i].y
                    }
                }
                if (gzLaser.t <= 0) {
                    gzLaser.on = false
                    if (gzLaserPick) {
                        if (gzLaserPick.kind === "jet" && gzLaserPick.i < gzJets.length) {
                            const jet = gzJets[gzLaserPick.i]
                            ejectPilot(jet)
                            explode(jet.x, jet.y, "#ff6b8a", 0.7)
                            gzJets.splice(gzLaserPick.i, 1)
                            gz.nextJet = 0.8 + Math.random() * 1.4
                        } else if (gzLaserPick.kind === "chute" && gzLaserPick.i < gzChutes.length) {
                            const p = gzChutes[gzLaserPick.i]
                            explode(p.x, p.y, "#e8d4b0", 0.28)
                            gzChutes.splice(gzLaserPick.i, 1)
                        }
                    }
                    gzLaserPick = null
                }
            }
            updateGzAir(dt, waterline)
        }

        function stompChutes(waterline: number) {
            const gx = gzScreenX()
            for (let i = gzChutes.length - 1; i >= 0; i--) {
                const p = gzChutes[i]
                if (!chuteIsLow(p, waterline)) {
                    continue
                }
                if (Math.abs(p.x - gx) > 18 * PIXEL) {
                    continue
                }
                explode(p.x, p.y, "#e8d4b0", 0.28)
                gzChutes.splice(i, 1)
            }
        }

        function updateGzAir(dt: number, waterline: number) {
            gz.nextJet -= dt
            const fighting = gzJets.filter(j => !j.flee).length
            if (gz.mode === 2 && gz.nextJet <= 0 && fighting < 3) {
                spawnGzJet()
                gz.nextJet = 0.4
            }
            const gx = gzScreenX()
            const gy = gzFeetY(waterline) - gz.h * 0.62 * PIXEL
            const fireRange = 100 * PIXEL
            for (let i = gzJets.length - 1; i >= 0; i--) {
                const jet = gzJets[i]
                if (gz.mode !== 2) {
                    jet.flee = true
                }
                if (jet.flee) {
                    const away = jet.x < gx ? -1 : 1
                    jet.vx += away * 280 * dt
                    jet.vy -= 70 * dt
                    const sp = Math.hypot(jet.vx, jet.vy)
                    if (sp > 340) {
                        jet.vx *= 340 / sp
                        jet.vy *= 340 / sp
                    }
                    jet.x += jet.vx * dt
                    jet.y += jet.vy * dt
                    if (jet.x < -80 || jet.x > width + 80 || jet.y < -80 || jet.y > height + 80) {
                        gzJets.splice(i, 1)
                    }
                    continue
                }
                const tx = gx + Math.sin(t * 0.7 + jet.phase) * 90
                const ty = gy - 40 - Math.cos(t * 0.9 + jet.phase) * 36
                jet.vx += (tx - jet.x) * dt * 3.6
                jet.vy += (ty - jet.y) * dt * 3.6
                jet.vx *= 0.93
                jet.vy *= 0.93
                const sp = Math.hypot(jet.vx, jet.vy)
                if (sp > 300) {
                    jet.vx *= 300 / sp
                    jet.vy *= 300 / sp
                }
                jet.x += jet.vx * dt
                jet.y += jet.vy * dt
                jet.cd -= dt
                const near = Math.hypot(jet.x - gx, jet.y - gy) < fireRange
                if (gz.mode === 2 && near && jet.cd <= 0) {
                    const ang = Math.atan2(gy - jet.y, gx - jet.x)
                    gzShots.push({
                        x: jet.x,
                        y: jet.y,
                        vx: Math.cos(ang) * 220,
                        vy: Math.sin(ang) * 220,
                        life: 1.2,
                    })
                    jet.cd = 0.35 + Math.random() * 0.45
                }
            }
            const groundY = gzFeetY(waterline) - PIXEL
            for (let i = gzChutes.length - 1; i >= 0; i--) {
                const p = gzChutes[i]
                if (!p.grounded) {
                    p.vy += 380 * dt
                    if (!p.open && p.vy > 12) {
                        p.open = true
                    }
                    if (p.open) {
                        p.vy = Math.min(p.vy, 34)
                        p.vx += Math.sin(t * 1.6 + i) * 12 * dt
                        p.vx *= 0.97
                    }
                    p.x += p.vx * dt
                    p.y += p.vy * dt
                    if (p.y >= groundY) {
                        gzChutes.splice(i, 1)
                        continue
                    }
                }
                if (p.x < -40 || p.x > width + 40) {
                    gzChutes.splice(i, 1)
                }
            }
            for (let i = gzShots.length - 1; i >= 0; i--) {
                const s = gzShots[i]
                s.x += s.vx * dt
                s.y += s.vy * dt
                s.life -= dt
                if (s.life <= 0 || s.x < -40 || s.x > width + 40) {
                    gzShots.splice(i, 1)
                }
            }
        }

        function trainAtStation(xArt: number, layerSpeed: number) {
            const tx = mod(xArt, WORLD)
            for (const st of stations) {
                const sx = mod(st.x - scroll * LAYER_DEFS[st.layer].speed, WORLD)
                const d = Math.abs(mod(tx - sx + WORLD / 2, WORLD) - WORLD / 2)
                if (d < st.w * 0.5 + 10) {
                    return true
                }
            }
            return false
        }

        function drawParade(target: CanvasRenderingContext2D, waterline: number, viewW: number) {
            const copies = wrapCopies(viewW / PIXEL)
            const streetW = Math.max(12, paradeStreet.end - paradeStreet.start)
            const stringY = (waterline - 12) * PIXEL
            const groundY = (waterline - 3) * PIXEL
            const pulseT = t
            const worldX = (wx: number) => mod(wx - scroll * paradeSpeed, WORLD)
            if (paradeLanterns.length > 1) {
                target.fillStyle = "#1a1424"
                for (let i = 0; i < paradeLanterns.length - 1; i++) {
                    const a = paradeLanterns[i]
                    const b = paradeLanterns[i + 1]
                    const ax = worldX(a.x)
                    const bx = worldX(b.x)
                    for (let copy = -1; copy <= copies; copy++) {
                        const x0 = (ax + copy * WORLD) * PIXEL
                        const x1 = (bx + copy * WORLD) * PIXEL
                        if (Math.max(x0, x1) < -8 || Math.min(x0, x1) > viewW + 8) {
                            continue
                        }
                        const left = Math.min(x0, x1)
                        const right = Math.max(x0, x1)
                        if (right - left > 40 * PIXEL) {
                            continue
                        }
                        for (let x = left; x <= right; x += PIXEL) {
                            target.fillRect(snapP(x), stringY, PIXEL, PIXEL)
                        }
                    }
                }
            }
            for (let i = 0; i < paradeLanterns.length; i++) {
                const lamp = paradeLanterns[i]
                const ax = worldX(lamp.x)
                const bob = Math.round(Math.sin(pulseT * 1.4 + lamp.phase)) * PIXEL
                const glow = 0.35 + 0.45 * (0.5 + 0.5 * Math.sin(pulseT * 2.1 + lamp.phase))
                const y = (waterline - lamp.yOff) * PIXEL + bob
                for (let copy = -1; copy <= copies; copy++) {
                    const sx = (ax + copy * WORLD) * PIXEL
                    if (sx < -24 || sx > viewW + 24) {
                        continue
                    }
                    target.fillStyle = "#2a2030"
                    target.fillRect(sx, stringY, PIXEL, Math.max(PIXEL, y - stringY))
                    target.fillStyle = lamp.color
                    target.fillRect(sx, y, PIXEL, PIXEL)
                    target.fillStyle = `rgba(255, 200, 120, ${glow})`
                    target.fillRect(sx - PIXEL, y - PIXEL, 3 * PIXEL, 3 * PIXEL)
                }
            }
            for (let i = 0; i < paradeStalls.length; i++) {
                const stall = paradeStalls[i]
                const ax = worldX(stall.x)
                for (let copy = -1; copy <= copies; copy++) {
                    const sx = (ax + copy * WORLD) * PIXEL
                    if (sx < -24 || sx > viewW + 24) {
                        continue
                    }
                    target.fillStyle = "#0a1020"
                    target.fillRect(sx, groundY - PIXEL, stall.w * PIXEL, 2 * PIXEL)
                    target.fillStyle = stall.color
                    target.fillRect(sx, groundY - 4 * PIXEL, stall.w * PIXEL, 3 * PIXEL)
                    target.fillStyle = shadeHex(stall.color, 24)
                    target.fillRect(sx - PIXEL, groundY - 5 * PIXEL, (stall.w + 2) * PIXEL, PIXEL)
                    target.fillStyle = WIN
                    target.fillRect(sx + PIXEL, groundY - 3 * PIXEL, PIXEL, PIXEL)
                }
            }
            const floatLen = streetW - 12
            const floatWx = paradeStreet.start + 4 + mod(pulseT * 3.1, floatLen)
            const floatAx = worldX(floatWx)
            const floatGlow = 0.3 + 0.4 * (0.5 + 0.5 * Math.sin(pulseT * 3))
            for (let copy = -1; copy <= copies; copy++) {
                const sx = (floatAx + copy * WORLD) * PIXEL
                if (sx < -24 || sx > viewW + 24) {
                    continue
                }
                const y = groundY - PIXEL
                target.fillStyle = "#1a1420"
                target.fillRect(sx + PIXEL, y + 2 * PIXEL, PIXEL, PIXEL)
                target.fillRect(sx + 6 * PIXEL, y + 2 * PIXEL, PIXEL, PIXEL)
                target.fillStyle = "#c45a48"
                target.fillRect(sx, y, 8 * PIXEL, 2 * PIXEL)
                target.fillStyle = "#f5d84a"
                target.fillRect(sx + PIXEL, y, 6 * PIXEL, PIXEL)
                target.fillRect(sx + 8 * PIXEL, y - PIXEL, 2 * PIXEL, 2 * PIXEL)
                target.fillStyle = "#ffb060"
                target.fillRect(sx + 3 * PIXEL, y - 3 * PIXEL, PIXEL, 3 * PIXEL)
                target.fillRect(sx + 2 * PIXEL, y - 4 * PIXEL, 3 * PIXEL, PIXEL)
                target.fillStyle = `rgba(255, 176, 80, ${floatGlow})`
                target.fillRect(sx + PIXEL, y - 5 * PIXEL, 5 * PIXEL, PIXEL)
                target.fillStyle = "#e8d0a0"
                target.fillRect(sx - 3 * PIXEL, y + PIXEL, PIXEL, PIXEL)
                target.fillRect(sx - 6 * PIXEL, y + PIXEL, PIXEL, PIXEL)
            }
            for (let i = 0; i < paradeWalkers.length; i++) {
                const wlk = paradeWalkers[i]
                const u = mod(wlk.x + pulseT * wlk.speed, streetW)
                const ax = worldX(paradeStreet.start + 2 + u)
                for (let copy = -1; copy <= copies; copy++) {
                    const sx = (ax + copy * WORLD) * PIXEL
                    if (sx < -8 || sx > viewW + 8) {
                        continue
                    }
                    target.fillStyle = wlk.color
                    target.fillRect(sx, groundY, PIXEL, PIXEL)
                }
            }
        }

        function drawCity(target: CanvasRenderingContext2D, waterline: number) {
            const waterY = waterline * PIXEL
            drawSky(target, waterY)
            drawStars(target, waterY)
            drawShootingStars(target, waterY)
            drawSatellites(target, waterY)
            const mountainLift = 34
            drawStrip(target, farMountains, scroll * 0.03, (waterline - mountainLift - farMountains.height) * PIXEL, width)
            drawHorizonGlow(target, waterline, mountainLift)
            drawStrip(target, nearMountains, scroll * 0.055, (waterline - mountainLift - nearMountains.height) * PIXEL, width)
            drawSkiLights(target, waterline, mountainLift)
            drawLightning(target, waterY)
            drawPlanes(target, waterline, width)
            drawCrafts(target, waterline, width)
            drawStrip(target, cloudStrip, scroll * 0.06 + cloudDrift, (waterline - 20 - cloudStrip.height) * PIXEL, width)
            for (let i = 0; i < 3; i++) {
                const layer = layers[i]
                drawStrip(target, layer.strip, scroll * layer.speed, (waterline - layer.strip.height) * PIXEL, width)
                drawEggOnLayer(target, i, waterline)
                drawLiveFx(target, waterline, width, i)
            }
            drawSpotlights(target, waterline, width)
            const farTrackY = (waterline - 32 - farTracks.height) * PIXEL
            drawStrip(target, farTracks, scroll * 0.48, farTrackY, width)
            for (const train of farTrains) {
                if (train.cooldown > 0) {
                    continue
                }
                drawTrain(target, train.sprite, mod(train.x - scroll * 0.48, WORLD), farTrackY + PIXEL, width, true, trainAtStation(mod(train.x - scroll * 0.48, WORLD), 0.48), t)
            }
            drawCars(target, farCars, scroll, waterline, width, 0.52, t)
            for (let i = 3; i <= GZ_LAYER; i++) {
                const layer = layers[i]
                drawStrip(target, layer.strip, scroll * layer.speed, (waterline - layer.strip.height) * PIXEL, width)
                drawEggOnLayer(target, i, waterline)
                drawSmoke(target, waterline, width, i)
                drawLiveFx(target, waterline, width, i)
                drawLayerRuins(target, waterline, i)
            }
            drawGodzillaBattle(target, waterline)
            for (let i = GZ_LAYER + 1; i < layers.length - 1; i++) {
                const layer = layers[i]
                drawStrip(target, layer.strip, scroll * layer.speed, (waterline - layer.strip.height) * PIXEL, width)
                drawEggOnLayer(target, i, waterline)
                drawSmoke(target, waterline, width, i)
                drawLiveFx(target, waterline, width, i)
                drawLayerRuins(target, waterline, i)
            }
            drawCars(target, nearCars, scroll, waterline, width, 0.88, t)
            for (const tram of trams) {
                const tramX = mod(tram.x + t * tram.speed - scroll * 0.88, WORLD)
                drawTrain(target, tramSprite, tramX, (waterline - 6) * PIXEL, width)
                if (Math.sin(t * 14 + tram.x) > 0.92) {
                    const copies = wrapCopies(width / PIXEL)
                    for (let copy = -1; copy <= copies; copy++) {
                        const sx = (tramX + copy * WORLD + 8) * PIXEL
                        if (sx < -PIXEL || sx > width) {
                            continue
                        }
                        target.fillStyle = "#fff6c8"
                        target.fillRect(sx, (waterline - 8) * PIXEL, PIXEL, PIXEL)
                        target.fillStyle = "rgba(255, 240, 180, 0.45)"
                        target.fillRect(sx - PIXEL, (waterline - 9) * PIXEL, 3 * PIXEL, PIXEL)
                    }
                }
            }
            drawTrafficLights(target, waterline, width)
            const nearTrackY = (waterline - nearTracks.height) * PIXEL
            drawStrip(target, nearTracks, scroll * 0.9, nearTrackY, width)
            for (const train of nearTrains) {
                if (train.cooldown > 0) {
                    continue
                }
                if (train.rare) {
                    drawTrain(target, train.sprite, train.x - scroll * 0.9, nearTrackY + PIXEL, width, false, trainAtStation(train.x - scroll * 0.9, 0.9), t)
                } else {
                    drawTrain(target, train.sprite, mod(train.x - scroll * 0.9, WORLD), nearTrackY + PIXEL, width, true, trainAtStation(mod(train.x - scroll * 0.9, WORLD), 0.9), t)
                }
            }
            const front = layers[layers.length - 1]
            drawStrip(target, front.strip, scroll * front.speed, (waterline - front.strip.height) * PIXEL, width)
            drawSmoke(target, waterline, width, layers.length - 1)
            drawLiveFx(target, waterline, width, layers.length - 1)
            drawParade(target, waterline, width)
            drawLayerRuins(target, waterline, layers.length - 1)
            drawPadHeli(target)
            drawBridge(target, waterline, width)
            drawStrip(target, shoreStrip, scroll * 1.08, (waterline - shoreStrip.height) * PIXEL, width)
            target.fillStyle = "#3a4870"
            target.fillRect(0, waterY - PIXEL, width, PIXEL)
            drawRain(target, waterY)
        }

        function drawRain(target: CanvasRenderingContext2D, waterY: number) {
            if (!raining) {
                return
            }
            target.fillStyle = "rgba(186, 200, 224, 0.42)"
            for (const d of rainDrops) {
                if (d.y > waterY - PIXEL) {
                    continue
                }
                target.fillRect(snapP(d.x), snapP(d.y), PIXEL, d.len * PIXEL)
            }
        }

        function drawWater(waterline: number, waterH: number) {
            const waterY = waterline * PIXEL
            const waterPx = waterH * PIXEL
            aboveCtx!.clearRect(0, 0, width, height)
            aboveCtx!.drawImage(scene, 0, 0, width, waterY, 0, 0, width, waterY)

            const wg = sceneCtx!.createLinearGradient(0, waterY, 0, waterY + waterPx)
            wg.addColorStop(0, "#1a2a48")
            wg.addColorStop(1, "#0c162c")
            sceneCtx!.fillStyle = wg
            sceneCtx!.fillRect(0, waterY, width, waterPx)

            sceneCtx!.save()
            sceneCtx!.beginPath()
            sceneCtx!.rect(0, waterY, width, waterPx)
            sceneCtx!.clip()
            sceneCtx!.imageSmoothingEnabled = false
            sceneCtx!.translate(0, waterY)
            sceneCtx!.scale(1, -0.7)
            sceneCtx!.globalAlpha = 0.34
            sceneCtx!.drawImage(above, 0, 0, width, waterY, 0, -waterY, width, waterY)
            sceneCtx!.restore()

            sceneCtx!.fillStyle = "rgba(8,12,28,0.22)"
            for (let y = 2 * PIXEL; y < waterPx; y += 3 * PIXEL) {
                sceneCtx!.fillRect(0, waterY + y, width, PIXEL)
            }
            sceneCtx!.fillStyle = "#2a3858"
            sceneCtx!.fillRect(0, waterY, width, PIXEL)

            const boatScroll = scroll * 1.05
            const copies = wrapCopies(width / PIXEL)
            boatScreens.length = 0
            for (const boat of boats) {
                const bx = mod(boat.x + t * boat.speed - boatScroll, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (bx + copy * WORLD) * PIXEL
                    if (x + boat.w * PIXEL < 0 || x > width) {
                        continue
                    }
                    drawBoat(sceneCtx!, boat, x, waterline, t)
                    boatScreens.push({
                        x: x + (boat.w * PIXEL) / 2,
                        y: (waterline + boat.y - 2) * PIXEL,
                    })
                }
            }
            const shoreScroll = scroll * 1.08
            for (const boat of houseboats) {
                const bx = mod(boat.x - shoreScroll, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (bx + copy * WORLD) * PIXEL
                    if (x + boat.w * PIXEL < 0 || x > width) {
                        continue
                    }
                    drawBoat(sceneCtx!, boat, x, waterline, t)
                }
            }
            for (const rig of oilRigs) {
                const bx = mod(rig.x - boatScroll * 0.92, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (bx + copy * WORLD) * PIXEL
                    if (x < -80 || x > width) {
                        continue
                    }
                    const y = (waterline + rig.y) * PIXEL - oilRigSprite.height + PIXEL
                    sceneCtx!.drawImage(oilRigSprite, x, y)
                }
            }
            for (const farm of windFarms) {
                const bx = mod(farm.x - boatScroll * 0.92, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (bx + copy * WORLD) * PIXEL
                    if (x < -90 || x > width + 20) {
                        continue
                    }
                    const y = (waterline + farm.y) * PIXEL
                    for (let i = 0; i < farm.n; i++) {
                        const px = x + (4 + i * 12) * PIXEL
                        const hubY = y - 14 * PIXEL
                        sceneCtx!.fillStyle = "#4a5468"
                        sceneCtx!.fillRect(px, hubY, PIXEL, 14 * PIXEL)
                        const ang = t * 2.2 + i * 0.7
                        sceneCtx!.fillStyle = "#c4cce0"
                        for (let b = 0; b < 3; b++) {
                            const a = ang + (b * Math.PI * 2) / 3
                            sceneCtx!.fillRect(snapP(px + Math.cos(a) * 4 * PIXEL), snapP(hubY + Math.sin(a) * 4 * PIXEL), PIXEL, PIXEL)
                            sceneCtx!.fillRect(snapP(px + Math.cos(a) * 2 * PIXEL), snapP(hubY + Math.sin(a) * 2 * PIXEL), PIXEL, PIXEL)
                        }
                        sceneCtx!.fillStyle = "#e8eefc"
                        sceneCtx!.fillRect(px, hubY, PIXEL, PIXEL)
                    }
                }
            }
            for (const buoy of buoys) {
                const bx = mod(buoy.x - boatScroll, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (bx + copy * WORLD) * PIXEL
                    if (x < -PIXEL || x > width) {
                        continue
                    }
                    const y = (waterline + buoy.y) * PIXEL + Math.round(Math.sin(t * 2.1 + buoy.phase)) * PIXEL
                    sceneCtx!.fillStyle = "#2a2430"
                    sceneCtx!.fillRect(x, y, PIXEL, PIXEL)
                    sceneCtx!.fillStyle = Math.sin(t * 3.2 + buoy.phase) > 0.2 ? "#ff9a4a" : "#6a4030"
                    sceneCtx!.fillRect(x, y - PIXEL, PIXEL, PIXEL)
                }
            }
            for (const stall of floatStalls) {
                const sx = mod(stall.x - boatScroll, WORLD)
                for (let copy = -1; copy <= copies; copy++) {
                    const x = (sx + copy * WORLD) * PIXEL
                    if (x < -8 || x > width) {
                        continue
                    }
                    const bob = Math.round(Math.sin(t * 1.6 + stall.phase)) * PIXEL
                    const y = (waterline + stall.y) * PIXEL + bob
                    sceneCtx!.fillStyle = "#1a2030"
                    sceneCtx!.fillRect(x + PIXEL, y, PIXEL, 3 * PIXEL)
                    sceneCtx!.fillRect(x + 4 * PIXEL, y, PIXEL, 3 * PIXEL)
                    sceneCtx!.fillStyle = stall.color
                    sceneCtx!.fillRect(x, y - 3 * PIXEL, 6 * PIXEL, 3 * PIXEL)
                    sceneCtx!.fillStyle = shadeHex(stall.color, 20)
                    sceneCtx!.fillRect(x, y - 4 * PIXEL, 6 * PIXEL, PIXEL)
                    sceneCtx!.fillStyle = WIN
                    sceneCtx!.fillRect(x + PIXEL, y - 2 * PIXEL, PIXEL, PIXEL)
                    sceneCtx!.fillRect(x + 4 * PIXEL, y - 2 * PIXEL, PIXEL, PIXEL)
                }
            }
            for (const fish of fishJumps) {
                const u = fish.life / fish.max
                const fxx = snapP(mod(fish.x - boatScroll, WORLD) * PIXEL)
                const fy = (waterline + fish.y) * PIXEL - Math.sin(u * Math.PI) * fish.span * PIXEL
                if (fxx > -PIXEL && fxx < width) {
                    sceneCtx!.fillStyle = "#8ab0c8"
                    sceneCtx!.fillRect(fxx, fy, PIXEL, PIXEL)
                    if (fish.span > 1) {
                        sceneCtx!.fillRect(fxx + PIXEL, fy + PIXEL, PIXEL, PIXEL)
                    }
                }
            }
            const mx = snapP(moonX)
            for (let i = 0; i < 16; i++) {
                const a = 0.16 * (1 - i / 16)
                sceneCtx!.fillStyle = `rgba(210, 220, 240, ${a})`
                sceneCtx!.fillRect(mx, waterY + (2 + i * 2) * PIXEL, PIXEL, PIXEL)
            }
            if (raining) {
                sceneCtx!.fillStyle = "rgba(170, 190, 220, 0.28)"
                for (let i = 0; i < 18; i++) {
                    const rx = snapP(((t * 90 + i * 47) % 1) * width)
                    const ry = waterY + PIXEL + ((i * 13) % 7) * PIXEL
                    sceneCtx!.fillRect(rx, ry, PIXEL, PIXEL)
                }
            }
        }

        function explode(x: number, y: number, color: string, scale: number) {
            const add = (ang: number, sp: number, life: number, col: string, size: number) => {
                sparks.push({
                    x,
                    y,
                    vx: Math.cos(ang) * sp,
                    vy: Math.sin(ang) * sp,
                    life,
                    max: life,
                    color: col,
                    size,
                })
            }
            const patterns = ["burst", "ring", "willow", "palm", "double", "peony"]
            const pattern = patterns[Math.floor(Math.random() * patterns.length)]
            const accent = Math.random() > 0.45 ? "#f0f4ff" : FW_COLORS[Math.floor(Math.random() * FW_COLORS.length)]
            if (pattern === "ring") {
                const n = Math.floor((22 + Math.random() * 8) * scale)
                const sp = (88 + Math.random() * 28) * scale
                for (let i = 0; i < n; i++) {
                    add((Math.PI * 2 * i) / n, sp * (0.92 + Math.random() * 0.1), 0.85 + Math.random() * 0.25, i % 4 === 0 ? accent : color, 2)
                }
                return
            }
            if (pattern === "double") {
                const n = Math.floor(18 * scale)
                for (let ring = 0; ring < 2; ring++) {
                    const sp = (58 + ring * 42) * scale
                    const col = ring === 0 ? accent : color
                    for (let i = 0; i < n; i++) {
                        add((Math.PI * 2 * i) / n + ring * 0.12, sp, 0.8 + ring * 0.2, col, ring === 0 ? 2 : 3)
                    }
                }
                return
            }
            if (pattern === "willow") {
                const n = Math.floor((36 + Math.random() * 14) * scale)
                for (let i = 0; i < n; i++) {
                    const a = (Math.PI * 2 * i) / n + Math.random() * 0.15
                    const sp = (46 + Math.random() * 70) * scale
                    add(a, sp, 1.35 + Math.random() * 0.7, Math.random() > 0.7 ? accent : color, Math.random() > 0.6 ? 2 : 1)
                }
                return
            }
            if (pattern === "palm") {
                const arms = 7 + Math.floor(Math.random() * 4)
                for (let i = 0; i < arms; i++) {
                    const a = -Math.PI / 2 + (i - (arms - 1) / 2) * 0.28
                    const sp = (70 + Math.random() * 50) * scale
                    for (let k = 0; k < 5; k++) {
                        add(a + (Math.random() - 0.5) * 0.08, sp * (0.65 + k * 0.08), 1.0 + k * 0.12, k === 0 ? accent : color, k < 2 ? 3 : 2)
                    }
                }
                return
            }
            if (pattern === "peony") {
                const n = Math.floor((40 + Math.random() * 16) * scale)
                for (let i = 0; i < n; i++) {
                    const a = (Math.PI * 2 * i) / n + Math.random() * 0.12
                    const sp = (36 + Math.random() * 95) * scale
                    add(a, sp, 0.75 + Math.random() * 0.4, color, Math.random() > 0.5 ? 3 : 2)
                }
                const inner = Math.floor(12 * scale)
                for (let i = 0; i < inner; i++) {
                    add((Math.PI * 2 * i) / inner, (22 + Math.random() * 18) * scale, 0.9, accent, 2)
                }
                return
            }
            const n = Math.floor((28 + Math.random() * 12) * scale)
            for (let i = 0; i < n; i++) {
                const a = (Math.PI * 2 * i) / n + Math.random() * 0.2
                add(a, (40 + Math.random() * 110) * scale, (0.7 + Math.random() * 0.5) * (0.9 + scale * 0.15), color, Math.random() > 0.55 ? 3 : 2)
            }
            const glitter = Math.floor((10 + Math.random() * 8) * scale)
            for (let i = 0; i < glitter; i++) {
                add(Math.random() * Math.PI * 2, (18 + Math.random() * 40) * scale, (1.1 + Math.random() * 0.6) * (0.9 + scale * 0.12), Math.random() > 0.4 ? color : "#f0f4ff", Math.random() > 0.7 ? 2 : 1)
            }
        }

        function launchRocket(x: number, y: number, delay: number) {
            const color = FW_COLORS[Math.floor(Math.random() * FW_COLORS.length)]
            const boomY = 78 + Math.random() * Math.max(48, height * 0.3)
            const scale = Math.random() < 0.34 ? 1.06 + Math.random() * 0.2 : 0.9 + Math.random() * 0.14
            rockets.push({
                x,
                y,
                vx: (Math.random() - 0.5) * 28,
                vy: -(340 + Math.random() * 320),
                boomY,
                delay,
                color,
                scale,
                trail: [],
            })
        }

        function launchShow(waterline: number) {
            const originY = (waterline - 38) * PIXEL
            const count = 7 + Math.floor(Math.random() * 4)
            for (let i = 0; i < count; i++) {
                const x = width * (0.08 + 0.84 * ((i + Math.random() * 0.4) / count))
                const delay = Math.random() < 0.22
                    ? 1.8 + Math.random() * 1.8
                    : Math.random() * 1.7
                launchRocket(x, originY, delay)
            }
            const onScreen = boatScreens.filter(b => b.x > 20 && b.x < width - 20)
            const nBoats = Math.min(2, onScreen.length)
            for (let i = 0; i < nBoats; i++) {
                const b = onScreen[Math.floor(Math.random() * onScreen.length)]
                launchRocket(b.x, b.y, Math.random() * 2.6)
            }
        }

        function updateFireworks(dt: number) {
            for (let i = rockets.length - 1; i >= 0; i--) {
                const r = rockets[i]
                if (r.delay > 0) {
                    r.delay -= dt
                    continue
                }
                r.vy += 42 * dt
                r.x += r.vx * dt
                r.y += r.vy * dt
                r.trail.push({ x: r.x, y: r.y })
                if (r.trail.length > 8) {
                    r.trail.shift()
                }
                if (r.y <= r.boomY || r.vy >= -8) {
                    explode(r.x, r.y, r.color, r.scale * 2)
                    rockets.splice(i, 1)
                }
            }
            for (let i = sparks.length - 1; i >= 0; i--) {
                const s = sparks[i]
                s.vy += 48 * dt
                s.vx *= 0.985
                s.vy *= 0.985
                s.x += s.vx * dt
                s.y += s.vy * dt
                s.life -= dt
                if (s.life <= 0) {
                    sparks.splice(i, 1)
                }
            }
        }

        function drawFireworks(target: CanvasRenderingContext2D) {
            for (const r of rockets) {
                if (r.delay > 0) {
                    continue
                }
                for (let i = 0; i < r.trail.length; i++) {
                    const p = r.trail[i]
                    const a = (i + 1) / (r.trail.length + 1)
                    target.fillStyle = a > 0.6 ? r.color : "#fff6d0"
                    const sz = a > 0.75 ? PIXEL : PIXEL
                    target.globalAlpha = 0.25 + a * 0.7
                    target.fillRect(snapP(p.x), snapP(p.y), sz, sz)
                }
                target.globalAlpha = 1
                target.fillStyle = "#fff6d0"
                target.fillRect(snapP(r.x), snapP(r.y), PIXEL, PIXEL)
                target.fillStyle = r.color
                target.fillRect(snapP(r.x), snapP(r.y + PIXEL), PIXEL, PIXEL)
            }
            for (const s of sparks) {
                const a = Math.max(0, s.life / s.max)
                if (a < 0.2 && Math.floor(s.life * 18) % 2 === 0) {
                    continue
                }
                target.globalAlpha = a
                target.fillStyle = s.color
                const sz = s.size * PIXEL
                target.fillRect(snapP(s.x), snapP(s.y), sz, sz)
            }
            target.globalAlpha = 1
        }

        function tick(now: number) {
            if (!running) {
                return
            }
            const dt = Math.min(0.05, (now - last) / 1000)
            last = now
            t += dt
            scroll += dt * 11
            cloudDrift += dt * 1.6
            const stepTrain = (train: { sprite: HTMLCanvasElement; x: number; speed: number; cooldown: number; rare: boolean }) => {
                if (train.rare) {
                    if (train.cooldown > 0) {
                        train.cooldown -= dt
                        if (train.cooldown <= 0) {
                            train.x = scroll * 0.9 - train.sprite.width - 12
                        }
                        return
                    }
                    train.x += dt * train.speed
                    if (train.x - scroll * 0.9 > width / PIXEL + 20) {
                        train.cooldown = 6 + Math.random() * 8
                    }
                    return
                }
                train.x = mod(train.x + dt * train.speed, WORLD)
            }
            for (const train of farTrains) {
                stepTrain(train)
            }
            for (const train of nearTrains) {
                stepTrain(train)
            }
            const waterH = WATER_H
            const waterline = Math.floor(height / PIXEL) - waterH + tideShift
            updateGodzilla(dt, waterline)
            padHeli.timer -= dt
            const pad = liveFx.find(f => f.kind === "helipad")
            let padX: number | null = null
            let padY = 0
            if (pad) {
                const speed = LAYER_DEFS[pad.layer].speed
                const copies = wrapCopies(width / PIXEL)
                const ax = mod(pad.x - scroll * speed, WORLD)
                padY = (waterline - pad.yOff) * PIXEL
                let best = Infinity
                for (let copy = -1; copy <= copies; copy++) {
                    const px = (ax + copy * WORLD) * PIXEL
                    const d = padHeli.mode === 0 ? Math.abs(px - width * 0.45) : Math.abs(px - padHeli.sx)
                    if (px > -80 && px < width + 80 && d < best) {
                        best = d
                        padX = px
                    }
                }
            }
            const hold = 78
            const cruise = 40
            const descend = 7
            if (padHeli.mode === 0 && padHeli.timer <= 0 && padX !== null) {
                padHeli.mode = 1
                padHeli.sx = -36
                padHeli.sy = padY - hold
            } else if (padHeli.mode === 1) {
                const destX = padX ?? width * 0.4
                const dx = destX - padHeli.sx
                const step = cruise * dt
                if (Math.abs(dx) <= step) {
                    padHeli.sx = destX
                    padHeli.mode = 2
                } else {
                    padHeli.sx += Math.sign(dx) * step
                }
                if (padX !== null) {
                    padHeli.sy = padY - hold
                }
            } else if (padHeli.mode === 2) {
                if (padX !== null) {
                    padHeli.sx = padX
                }
                padHeli.sy += descend * dt
                if (padHeli.sy >= padY) {
                    padHeli.sy = padY
                    padHeli.mode = 3
                    padHeli.timer = 3.8
                }
            } else if (padHeli.mode === 3) {
                if (padX !== null) {
                    padHeli.sx = padX
                    padHeli.sy = padY
                }
                if (padHeli.timer <= 0 || padX === null) {
                    padHeli.mode = 4
                }
            } else if (padHeli.mode === 4) {
                if (padX !== null) {
                    padHeli.sx = padX
                }
                padHeli.sy -= descend * dt
                if (padHeli.sy <= padY - hold) {
                    padHeli.sy = padY - hold
                    padHeli.mode = 5
                }
            } else if (padHeli.mode === 5) {
                padHeli.sx += cruise * dt
                padHeli.sy -= dt * 4
                if (padHeli.sx > width + 28) {
                    padHeli.mode = 0
                    padHeli.timer = 6 + Math.random() * 7
                }
            }
            marqueeNextShuffle -= dt
            if (marqueeNextShuffle <= 0) {
                const names = getCityPlayerNames()
                if (names.length > 0) {
                    const order = names.slice()
                    for (let i = order.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1))
                        const tmp = order[i]
                        order[i] = order[j]
                        order[j] = tmp
                    }
                    marqueeCols = marqueeTextColumns(order.join(" · "))
                } else {
                    marqueeCols = marqueeTextColumns("HELLO · CITY")
                }
                marqueeNextShuffle = 10 + Math.random() * 8
            }
            if (ufo.mode === 0) {
                ufo.timer -= dt
                if (ufo.timer <= 0) {
                    ufo.dir = Math.random() > 0.5 ? 1 : -1
                    ufo.tx = width * (0.28 + Math.random() * 0.44)
                    ufo.x = ufo.dir > 0 ? -40 : width + 40
                    ufo.hoverY = Math.max(40, (waterline - 168) * PIXEL)
                    ufo.y = ufo.hoverY - 90
                    ufo.beam = 0
                    ufo.personY = 0
                    ufo.hidePerson = false
                    ufo.mode = 1
                }
            } else if (ufo.mode === 1) {
                const step = 280 * dt
                const dx = ufo.tx - ufo.x
                if (Math.abs(dx) <= step) {
                    ufo.x = ufo.tx
                    ufo.mode = 2
                    ufo.timer = 1.1
                } else {
                    ufo.x += Math.sign(dx) * step
                }
                ufo.y += (ufo.hoverY - ufo.y) * Math.min(1, dt * 6)
            } else if (ufo.mode === 2) {
                ufo.x = ufo.tx
                ufo.y += (ufo.hoverY - ufo.y) * Math.min(1, dt * 8)
                ufo.beam = Math.min(1, ufo.beam + dt * 1.8)
                ufo.timer -= dt
                if (ufo.timer <= 0) {
                    ufo.mode = 3
                }
            } else if (ufo.mode === 3) {
                ufo.x = ufo.tx
                ufo.beam = 1
                ufo.personY += dt * 28
                const lift = (waterline - 2) * PIXEL - ufo.y - 8 * PIXEL
                if (ufo.personY * PIXEL >= lift) {
                    ufo.personY = lift / PIXEL
                    ufo.hidePerson = true
                    ufo.mode = 4
                    ufo.timer = 0.4
                }
            } else if (ufo.mode === 4) {
                ufo.timer -= dt
                if (ufo.timer <= 0) {
                    ufo.beam = Math.max(0, ufo.beam - dt * 3.4)
                    ufo.x += ufo.dir * 320 * dt
                    ufo.y -= 160 * dt
                    if (ufo.x < -60 || ufo.x > width + 60 || ufo.y < -50) {
                        ufo.mode = 0
                        ufo.beam = 0
                        ufo.timer = rareWait(600)
                    }
                }
            }
            for (const s of spotlights) {
                s.clock += dt * s.rate * spotRateMul
            }
            for (const fx of liveFx) {
                if (fx.kind === "elevator") {
                    if ((fx.hold || 0) > 0) {
                        fx.hold = (fx.hold || 0) - dt
                    } else {
                        fx.phase += dt * 0.28 * (fx.r === 0 ? 1 : fx.r)
                        if (fx.phase >= 1) {
                            fx.phase = 1
                            fx.r = -1
                            fx.hold = 0.55 + Math.random() * 0.9
                        } else if (fx.phase <= 0) {
                            fx.phase = 0
                            fx.r = 1
                            fx.hold = 0.55 + Math.random() * 0.9
                        }
                    }
                    continue
                }
                if (fx.kind === "ferris") {
                    if ((fx.hold || 0) > 0) {
                        fx.hold = (fx.hold || 0) - dt
                    } else {
                        fx.phase += dt * 0.45
                        if (Math.random() < 0.0018) {
                            fx.hold = 1.8 + Math.random() * 3.2
                        }
                    }
                    continue
                }
                if (fx.kind !== "coaster") {
                    continue
                }
                if (fx.phase < 0) {
                    fx.phase += dt
                    if (fx.phase >= 0) {
                        fx.phase = 0
                        fx.r = 0.08
                    }
                    continue
                }
                const p0 = coasterTrackAt(fx.phase, fx.w, fx.h)
                const p1 = coasterTrackAt(Math.min(1, fx.phase + 0.012), fx.w, fx.h)
                const dy = p1.y - p0.y
                if (dy < -0.4) {
                    fx.r = 0.07
                } else {
                    fx.r += dy * 3.5 * dt
                    fx.r *= 0.99
                    fx.r = Math.max(0.2, Math.min(0.59, fx.r))
                }
                fx.phase += dt * fx.r
                if (fx.phase >= 1) {
                    fx.phase = -(2.5 + Math.random() * 2)
                }
            }

            updateShootingStars(dt, waterline * PIXEL)
            updateSkyFx(dt, waterline * PIXEL)
            if (raining) {
                const waterY = waterline * PIXEL
                for (const d of rainDrops) {
                    d.y += d.spd * dt
                    d.x += d.spd * 0.16 * dt
                    if (d.y > waterY) {
                        d.y = -8 - Math.random() * 40
                        d.x = Math.random() * width
                    }
                    if (d.x > width + 16) {
                        d.x -= width + 32
                    }
                }
            }

            nextFish -= dt
            if (nextFish <= 0) {
                fishJumps.push({
                    x: Math.random() * WORLD,
                    y: 4 + Math.floor(Math.random() * 18),
                    life: 0,
                    max: 0.45 + Math.random() * 0.35,
                    span: Math.random() > 0.45 ? 2 : 1,
                })
                nextFish = 4 + Math.random() * 10
            }
            for (let i = fishJumps.length - 1; i >= 0; i--) {
                fishJumps[i].life += dt
                if (fishJumps[i].life >= fishJumps[i].max) {
                    fishJumps.splice(i, 1)
                }
            }

            ctx!.imageSmoothingEnabled = false
            sceneCtx!.imageSmoothingEnabled = false
            sceneCtx!.clearRect(0, 0, width, height)
            updateFireworks(dt)
            drawCity(sceneCtx!, waterline)
            drawWater(waterline, waterH)
            drawFireworks(sceneCtx!)
            ctx!.clearRect(0, 0, cssW, cssH)
            ctx!.drawImage(scene, 0, 0, width, height, 0, 0, cssW, cssH)
            drawUfo(ctx!, Math.floor(height / PIXEL) - WATER_H + tideShift, cssW / width, cssH / height)

            raf = requestAnimationFrame(tick)
        }

        function onCardsFlipped(e: Event) {
            const showing = (e as CustomEvent).detail?.showingFront
            spotRateMul = showing === false ? 1 : 3.4
            if (showing === false) {
                return
            }
            const waterline = Math.floor(height / PIXEL) - WATER_H + tideShift
            launchShow(waterline)
        }

        placeLandmarks()
        resize()
        window.addEventListener("resize", resize)
        window.addEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        last = performance.now()
        raf = requestAnimationFrame(tick)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", resize)
            window.removeEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="falling-leaves-canvas"
            aria-hidden
        />
    )
}
